/**
 * voice-v8 — TTS Sentence-Queue (Pre.483)
 *
 * Nimmt Text-Chunks vom LLM-Stream entgegen, schneidet an Satzgrenzen,
 * holt fuer jeden fertigen Satz das TTS-Audio parallel UND spielt
 * sequenziell ab. So hoert der User den ersten Satz schon waehrend der
 * Bot noch weiter tippt.
 */

import { KLAUSEL_GRENZE, endetMitSatz, satzEnden, teileInSaetze } from "../../shared/satzgrenzen.js";
import { beginntAbsatz } from "../../shared/absatzgrenze.js";
import { meldeVorleseVerlust, neuerVorlesevorgang } from "./vorlese-verlust.js";
import { hookStimmeDiagnose, meldeErstenTon, meldePcmStimme, meldeTonZustand, prewarmHiFi } from "./playback-hifi.js";
import { endetMitFrage } from "./frage-am-ende.js";
import { sprichtSchmiedAn, zusatzAmEnde } from "../../shared/schmied.js";
import { zugEndeStand } from "./zug-ende.js";
import { beendeBeobachtung, beobachteWiedergabe, vorstartAbbruch, type VorstartLage } from "./mikro-vorstart.js";
import { istStromAdresse, pruefeNachspiel, pufferUrl } from "./vorlesen-nachspiel.js";
import { meldeSpur } from "./mikro-spur.js";
import { nativerVorleserDa, pausiereNativ, spieleNativ, vorleseHalt } from "./vorlesen-nativ.js";
import { isNativePlatform } from "./native-voice.js";
import { meldeWiedergabeName, meldeWiedergabeEnde } from "./wiedergabe-name.js";
import { langeSpurLaeuft, warteAufLangeSpur } from "./lange-spur.js";
import {
  STIMME_PARK_MS,
  beitragAnderswoLaeuft,
  stimmeBeginnt,
  stimmeDarfBeginnen,
  stimmeEndet,
  stimmeEntscheidSofort,
  seiteNichtVorn,
  stimmeParken,
  type ParkErgebnis,
  type StimmeEntscheid,
} from "./seiten-abgleich.js";
import { naechsteSeitenTonId, seitenTonMelder } from "./seiten-ton.js";
import {
  ERSTES_PAKET_FRIST_MS,
  PCM_ABZUG_DB,
  PcmStueck,
  START_VORRAT_S,
  istPcmAdresse,
  pcmDateiUrl,
  type PcmMessung,
} from "./tts-pcm.js";
import { pcmRueckfall } from "./tts-pcm-rueckfall.js";
import { PcmVorrat } from "./tts-pcm-vorrat.js";
import { STOERUNG_LUECKE_MS, istStromFehler, pcmGedaechtnis } from "./tts-pcm-gedaechtnis.js";
import { beruehrungSetztFort, nimmDateiSpieler } from "./datei-spieler.js";
import {
  ANLAUF_MAX,
  ENDE_NAH_S,
  adresseAb,
  FORTGANG_MIN_S,
  HINWEIS_WARTET_MAX_MS,
  WEITER_MAX,
  anlaufZaehltNeu,
  gesprocheneZeichen,
  ROUTE_FRIST_MS,
  kopfhoererWeg,
  sofortErlaubt,
  startAusAdresse,
  wiederanlaufAdresse,
  wiederanlaufAn,
  type StueckStand,
} from "./vorlese-wiederanlauf.js";
import { motorAudioText, motorSitzungText } from "./wachwort-zustand.js";
import { leseEingabe } from "./scroll-wache.js";
import { msSeitBewegung } from "./scroll-ruhe.js";
import { ausgangJetzt } from "./route-messung.js";
import { ZugMessung } from "./vorlese-zugmessung.js";
import { FortsetzSpur } from "./vorlese-fortsetzen.js";
import {
  MAX_DATEI_BYTES,
  NATIV_FRAGEN_MAX_MS,
  NATIV_NEU_FRAGEN_MS,
  NATIV_ZURUECK_FRIST_MS,
  NATIV_ZURUECK_MAX,
  WACHHUND_ZUSCHLAG_MS,
  dateiQuelle,
  erstesPaket,
  pcmQuelle,
  stimmeNativBereitKurz,
  stimmeNativBereitWarten,
  stimmeNativMoeglich,
  stimmeNativPause,
  stimmeNativPauseMelden,
  stimmeNativSpielen,
  type NativErgebnis,
  type NativQuelle,
} from "./stimme-nativ.js";

/*
 * ⚠️ Hier stand `SENTENCE_TERMINATORS = /([.!?]["')\]]?)\s+/g` — die
 *    Satzregel dieses Moduls. Sie ist ersatzlos weg: die Regel steht jetzt in
 *    shared/satzgrenzen.ts, und zwar EINMAL. Niemand ausserhalb hat sie
 *    benutzt (geprueft), also bleibt auch kein Name als Falle stehen.
 */
/** 17.10.2026: der Abbruchgrund, wenn eine andere Seite den zurückgehaltenen Beitrag gesprochen hat (seiten-abgleich.ts „Geparkt“). */
export const ANDERSWO_GESPROCHEN = "anderswo-gesprochen";

export const MIN_CHUNK_LEN = 6;

/**
 * Wie kurz das ERSTE gesprochene Stueck sein darf. (09.09.2026)
 *
 * ## Die Messung, die diese Zahlen setzt
 *
 * 1.690 Vertonungen aus dem Journal (96 h, `[voice-preview] soniox <ms>
 * <bytes>b`), Bytes bei 192 kbit/s in Tonsekunden umgerechnet:
 *
 *     Vertonung = 373 ms + 817 ms je Tonsekunde
 *
 * Gesprochen wird mit rund 15,5 Zeichen je Sekunde. Also je Zeichen:
 * **52,7 ms erzeugen gegen 64,5 ms sprechen.** Die Vertonung ist schneller
 * als die Wiedergabe — sie holt auf, solange das Stueck den Fixanteil
 * traegt:
 *
 *     373 / (64,5 - 52,7) = 32 Zeichen
 *
 * Ab 32 Zeichen entsteht keine Luecke mehr. `FIRST_SPEAKABLE_MIN` liegt mit
 * 40 bewusst darueber — Rechenwerte ohne Rand halten in diesem Haus selten.
 *
 * ## Warum das etwas bringt
 *
 * Gemessen war das erste Stueck im Median 76 Zeichen lang, also
 * 373 + 52,7 * 76 = **4,4 s Stille**, bevor der erste Ton kam. Bei 45
 * Zeichen sind es 2,7 s. Die Zahl steht in unserem Code, nicht beim
 * Anbieter.
 *
 * ⚠️ Der Kommentar an `splitLongSentence` sagt das Gegenteil („30 Zeichen
 *    7,3 s, 120 Zeichen 16,9 s" — also Fixkosten-dominiert). Diese Messung
 *    ist vom 27.07. und galt fuer Soniox tts-rt v1; v1 ist seit dem 06.09.
 *    abgeschaltet (HTTP 400). Nachgemessen an v2 gilt sie nicht mehr.
 */
export const FIRST_SPEAKABLE_MIN = 40;
/** Ein FERTIGER erster Satz bis hierher bleibt ganz (`ersterBrocken`). */
export const FIRST_SPEAKABLE_MAX = 90;

/**
 * Bis wohin das erste Stück auf ein Satzende oder ein Satzzeichen wartet,
 * bevor es am bloßen Wortrand geschnitten wird. (27.09.2026, Ticket 2692)
 *
 * Soniox spricht jedes Stück als eigenen Satz; ein Schnitt am Wortrand
 * mitten im Satz klingt wie Stocken. Bis zum 90. Zeichen kam bei rund
 * jeder siebten Antwort kein Satzzeichen, und der Schnitt vor einem
 * Bindewort (pre.3375) traf nur zur Hälfte eine Satzteilgrenze („ist ‖ als
 * Kontakt angelegt"). Den kurzen ersten Satz formt seitdem das Modell
 * (SPRECHSTIL in server/chat.ts); der Wortrand ist nur noch der seltene
 * Rückfall.
 *
 * ## Der Preis, nachgerechnet (Prüfung von pre.3391)
 *
 * „Etwas später" war zu wenig. Kommt das erste Satzende oder Satzzeichen
 * erst zwischen dem 90. und dem 140. Zeichen, wird alles bis dorthin EIN
 * Stück, statt am Wortrand ab 40 zu enden. An 793 Antworten seit dem
 * 16.09. (24er-Häppchen, noch ohne die Prompt-Regel) traf das 108: das
 * erste Stück im Median 57 Zeichen länger, bei neun von zehn bis 86, das
 * längste 137 Zeichen. Nach der Formel an `FIRST_SPEAKABLE_MIN` (52,7 ms
 * je Zeichen) sind das rund 3 s mehr Stille vor dem ersten Ton, bei neun
 * von zehn bis 4,5 s. Begann die Antwort mit einem Satz von höchstens zehn
 * Wörtern, traf es 1 von 243: Hält sich das Modell an die Regel, fällt der
 * Preis weitgehend weg. Nachmessen: `[tts-t] seg=0 synth= len=`.
 *
 * ⚠️ Eine eigene Zahl, nicht FIRST_SPEAKABLE_MAX: Jene sagt, wann ein
 *    fertiger Satz ganz bleibt (Wartezeit), diese, wie lange auf eine
 *    Stelle zum Luftholen gewartet wird (Tonfall). Siehe KLAUSEL_MIN.
 */
export const ERSTER_WORTRAND_AB = 140;

/**
 * Wo `splitLongSentence` fruehestens schneiden darf.
 *
 * ⚠️ Das war bis zum 09.09.2026 dieselbe Zahl wie `FIRST_SPEAKABLE_MIN` —
 *    und das war eine Falle: die eine Bedeutung ist PROSODIE (nicht am
 *    fruehen Komma trennen, „Morgen,"), die andere LATENZ (wie schnell der
 *    erste Ton kommt). Eine Zahl fuer zwei Entscheidungen heisst, dass die
 *    zweite Entscheidung die erste unbemerkt mitzieht. Genau dieses Muster
 *    hat hier schon dreimal Schaden angerichtet.
 */
export const KLAUSEL_MIN = 80;

type FetchTts = (text: string, signal: AbortSignal) => Promise<string>;


// 2026-07-05 (Latenz-Audit): Satz > MAX an Klausel-Grenze (sonst Leerzeichen)
// in Stuecke teilen — kuerzere Chunks = schnellere Synthese, weniger Pausen.
/**
 * Lange Saetze an Klausel-Grenzen teilen, damit die Stuecke UEBERLAPPEND
 * vertont werden koennen.
 *
 * 2026-07-27 (Ticket 768) — KORRIGIERT nach neuer Messung. Frueher am Tag
 * wurde die Schwelle 120 -> 90 gesenkt, in der Annahme, die Vertonungsdauer
 * wachse linear mit der Laenge. Sie tut es NICHT. Gemessen (Erstvertonung,
 * je einzigartig, kein Cache):
 *    30 Zeichen:  7,3 s
 *    60 Zeichen: 11,5 s
 *   120 Zeichen: 16,9 s
 *   200 Zeichen: 19,6 s
 * Fuer 6,7-mal mehr Text nur 2,7-mal die Zeit — der feste Overhead je
 * Anfrage dominiert. Jeder zusaetzliche Schnitt zahlt ihn also ein weiteres
 * Mal. Kleine Stuecke sind damit TEURER, nicht billiger; die Senkung auf 90
 * wirkte gegen den Anbieter. Zurueck auf 140: weniger Anfragen, weniger
 * Overhead, und die Prosodie bleibt zusammenhaengender.
 * Der eigentliche Hebel liegt nicht hier, sondern in der Schleuse weiter
 * unten (MAX_GLEICHZEITIG) — siehe die Parallelitaets-Messung dort.
 */
/**
 * Warum die Wiedergabe gescheitert ist — in einem Wort. (19.09.2026, #2329)
 *
 * ## Woher das kommt
 *
 * `[tts-gap] via=error` kam bei Albert an drei Tagen 216-mal vor, bei einer
 * Quote von 10 bis 15 % aller Zuege: jede siebte gesprochene Antwort ergab
 * NULL Sekunden Ton. Die Zeile sagte nur `error`, daneben aber:
 *
 *     via=error audio=6.00s echt=0.00s rs=4
 *
 * `rs=4` ist HAVE_ENOUGH_DATA — die Datei war vollstaendig geladen, und die
 * Wiedergabe scheiterte trotzdem in null Sekunden. Das kann ein blockiertes
 * `play()` sein (fehlende Nutzergeste), ein Dekodierfehler, ein abgelehntes
 * Format oder ein Netzabbruch. Aus `error` allein ist keines davon zu
 * unterscheiden.
 *
 * ⚠️ Dieselbe Luecke gab es beim ABBRUCH schon einmal (Ticket 2232). Er hat
 *    seither einen Grund; der Fehler war der letzte namenlose Ausgang.
 *
 * ⚠️ Der MediaError des Elements wiegt schwerer als die Ablehnung von
 *    `play()`: beim blockierten Abspielen feuert oft beides, und der
 *    konkrete Code sagt mehr als ein allgemeiner Ablehnungsgrund.
 */
export function fehlerGrund(
  ablehnung: unknown,
  elementFehler: { code?: number } | null,
): string {
  const code = elementFehler?.code;
  if (code === 1) return "geladen-abgebrochen";
  if (code === 2) return "netz";
  if (code === 3) return "dekodieren";
  if (code === 4) return "quelle";
  const name =
    ablehnung && typeof (ablehnung as { name?: unknown }).name === "string"
      ? (ablehnung as { name: string }).name
      : "";
  if (name === "NotAllowedError") return "nicht-erlaubt";
  if (name === "NotSupportedError") return "format";
  if (name === "AbortError") return "abgebrochen";
  // ⚠️ Einen unbekannten Namen weiterreichen statt verschlucken — sonst
  //    steht der naechste Fall wieder ohne Grund da.
  return name || "unbekannt";
}

/**
 * Wie lange ein Stück aktiv laufen darf, bevor der Wachhund es beendet.
 * (23.09.2026, Ticket 2547)
 *
 * Bekannte Dauer: Dauer + 10 s, wie bisher. Bei UNBEKANNTER Dauer standen
 * hier pauschal 90 s. Die trafen Martin am 23.09. um 23:19: ein Stück von
 * 7,27 s startete im Hintergrund, seine Dauer war beim Start noch nicht
 * geladen — danach 88 s Stille im Vordergrund, und so lange blieb der Ton
 * belegt (`[wachwort] wartet: tonbelegung`).
 *
 * Jetzt schätzt die Textlänge die Dauer. Gemessen an 4.606 Stücken seit dem
 * 01.09. (`[tts-t] len=` gegen `[tts-gap] audio=`): das langsamste sprach
 * 3,97 Zeichen je Sekunde (22.09. 22:46, 16 Zeichen in 4,03 s; Median 15).
 * Länge / 4 + 10 s lag bei keinem der 4.383 normal beendeten Stücke unter
 * der echten Laufzeit, der knappste Abstand waren 9,7 s. Ohne Text bleibt
 * es bei 90 s, und 90 s bleiben die Obergrenze.
 */
const WACHHUND_NACHLAUF_MS = 10_000;
export const WACHHUND_OHNE_ANGABE_MS = 90_000;
const LANGSAMSTE_ZEICHEN_JE_S = 4;

/** Eine gleichlautende Wurf-Zeile (`meldeWurf`) höchstens so oft ins Journal (wie `SPUR_ABSTAND_MS` der App-Spur). */
export const WURF_ABSTAND_MS = 30_000;
const wurfZuletzt = new Map<string, number>();
/** Nur für Tests: die Drossel der Wurf-Zeilen vergessen. */
export function _wurfDrosselVergessen(): void {
  wurfZuletzt.clear();
}

export function wachhundDeckelMs(dauerS: number, textLaenge: number): number {
  if (Number.isFinite(dauerS) && dauerS > 0) {
    return dauerS * 1000 + WACHHUND_NACHLAUF_MS;
  }
  if (textLaenge > 0) {
    return Math.min(
      WACHHUND_OHNE_ANGABE_MS,
      Math.round((textLaenge / LANGSAMSTE_ZEICHEN_JE_S) * 1000) +
        WACHHUND_NACHLAUF_MS,
    );
  }
  return WACHHUND_OHNE_ANGABE_MS;
}

/**
 * Ticket 2547 (23.09.2026): So lange darf ein Stück nach der Rückkehr in den
 * Vordergrund stillstehen, bevor es frisch gestartet wird.
 */
const STILLSTAND_PRUEF_MS = 2000;
/**
 * Ticket 2382 (23.09.2026): Nach dieser Zeit muss ein Stück hörbar laufen,
 * sonst schreibt es `[tts-start] steht`.
 */
export const START_PRUEF_MS = 3000;
/**
 * Ticket 2919 (Sperren/Unterbrechen): So lange wartet ein Stück, das bei verstecktem Bildschirm oder nach einer
 * Unterbrechung beginnt, auf seinen ersten Ton. Danach steht ein sichtbarer Hinweis „Tippen zum Weiterspielen“, und
 * die erste Berührung setzt es fort — statt still bis zum Wachhund zu warten (Journal 10:59:50: 208 s für 6,9 s Ton).
 */
const TIPP_FRIST_MS = 8000;
/*
 * ⚠️ 05.10.2026: `TIPP_FRIST_MS` gilt nur noch, wenn der Wiederanlauf am Gerät abgeschaltet ist
 *    (vorlese-wiederanlauf.ts, `wiederanlaufAn`). Sonst fragt ein Stück nur noch, wenn `play()` wirklich abgelehnt
 *    wurde oder es nach `ANLAUF_MAX` frischen Starts immer noch steht.
 */

export function splitLongSentence(sentence: string, max = 140): string[] {
  if (sentence.length <= max) return [sentence];
  const out: string[] = [];
  let rest = sentence;
  // 2245: nie am frühen Komma („Morgen,“). Schnitt erst ab ~80 Zeichen.
  /*
   * ⚠️ 24.09.2026 (Ticket 2559): ab KLAUSEL_MIN galt nur für den Deckel
   *    140. Bei einem kleineren Deckel (die Rampe hinter dem ersten Stück
   *    sieht 72 bis 86 vor) lag KEINE Stelle ab 80 im Kopf, und es wurde
   *    hart mitten im Wort gekappt. Deshalb stand in `zerlegeMitRampe` ein
   *    Boden von 100 — und der ließ hinter einem Kopf von 42 Zeichen ein
   *    Stück von 100 zu, Faktor 2,4. Die Untergrenze wächst jetzt mit dem
   *    Deckel, im selben Verhältnis wie bisher (80 zu 140); bei 140 und
   *    darüber bleibt es bei 80.
   */
  const ab = Math.min(KLAUSEL_MIN, Math.round((max * KLAUSEL_MIN) / 140));
  while (rest.length > max) {
    const head = rest.slice(0, max);
    let cut = -1;
    const re = new RegExp(KLAUSEL_GRENZE.source, "g");
    let mm: RegExpExecArray | null;
    while ((mm = re.exec(head))) cut = mm.index + mm[0].length;
    if (cut < ab) {
      /*
       * ⚠️ 24.09.2026 (Ticket 2559): KEIN ZWERG HINTER EINEM WORTENDE.
       *
       * Ohne Komma oder Strich wird am letzten Leerzeichen vor `max`
       * geschnitten, egal was übrig bleibt. Mit der Rampe hinter dem Kopf
       * (`teilGrenze`) liegt der Deckel oft knapp unter dem Rest. Im ersten
       * Anlauf dieses Baus fiel dann das letzte Wort als eigenes Stück ab,
       * rot geworden am Testsatz „Der erste Satz hier …" (tts-queue.test.ts):
       * Deckel 77, Rest 78, heraus kamen 65 Zeichen und „verschaffen."
       * allein. Ein Schnitt mitten im Satz für zwölf Zeichen, ohne Halt, an
       * dem man Luft holt.
       *
       * Jetzt bleiben hinter einem Wortende mindestens FIRST_SPEAKABLE_MIN
       * Zeichen. Geht das nicht, bleibt der Rest am Stück — er überragt den
       * Deckel dann um weniger als ein Kopf. Ein Komma oder Strich darf
       * weiter spät liegen: dort ist die Pause natürlich.
       */
      const spaetestens = Math.min(max, rest.length - FIRST_SPEAKABLE_MIN);
      if (spaetestens < ab) break;
      const sp = rest.slice(0, spaetestens).lastIndexOf(" ");
      if (sp >= ab) {
        cut = sp + 1;
      } else {
        /*
         * ⚠️ 24.09.2026 (Ticket 2559, Prüfung des ersten Baus): NIE IM WORT.
         *
         * Hier stand `cut = spaetestens` — ein harter Schnitt, sobald im
         * Fenster [ab, spaetestens) kein Leerzeichen lag. Mit dem kleinen
         * Deckel hinter dem Kopf ist das Fenster oft nur wenige Zeichen
         * breit. Heraus kamen zwei Wortfetzen für die Sprachausgabe,
         * „… für die Jahresversa" | „mmlung unseres …". Wo `takeSpeakable`
         * Teile wieder zusammensetzt (`join(" ")`, Schwanz plus Rest), stand
         * dann ein Leerzeichen mitten im Wort. Dieselbe Falle gab es schon
         * vorher beim Leeren (Deckel ab 100, Fenster ab 80): Ein langes
         * Wort, das das ganze Fenster überdeckte, wurde zerschnitten.
         *
         * Jetzt gilt das nächste Wortende HINTER dem Fenster, wenn danach
         * noch ein Kopf übrig bleibt. Sonst bleibt der Rest am Stück. Ein
         * etwas zu langes Stück kostet höchstens eine kurze Lücke; ein
         * zerschnittenes Wort hört man immer.
         */
        const weiter = rest.indexOf(" ", spaetestens);
        if (weiter < 0 || rest.length - (weiter + 1) < FIRST_SPEAKABLE_MIN) break;
        cut = weiter + 1;
      }
    }
    out.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut);
  }
  if (rest.trim()) out.push(rest.trim());
  return out;
}

/**
 * Einen fertigen Text in sprechbare Stücke teilen. (Ticket 2047, 04.09.2026)
 *
 * ## Warum
 *
 * Der Vorlese-Weg holte den ganzen Text als EINE Vertonung: gemessen
 * **11.800 ms Stille bei 272 Zeichen**, der einzige zweistellige
 * Sekundenwert im System. Die Live-Sprache hat dieses Problem nie gehabt —
 * sie zerlegt seit jeher. Diese Funktion trägt dasselbe Muster in den
 * Vorlese-Weg.
 *
 * ## Warum das erste Stück kürzer ist
 *
 * Es entscheidet, wann der erste Ton kommt; jedes weitere hat den Vorlauf
 * des laufenden Stücks. Kurz beginnen, dann größer werden, spart Fixkosten
 * ohne die Stille am Anfang zu verlängern.
 *
 * ⚠️ Kurze Texte bleiben EIN Stück. Zwei Anfragen für drei Sätze wären
 * langsamer als eine — jede Vertonung hat ihre Fixkosten.
 */
/**
 * Wie stark ein Stueck gegenueber dem vorigen wachsen darf. (09.09.2026)
 *
 * ## Der Fall
 *
 * Martin: *„Es gibt noch eine kleine Schwachstelle beim Vorlesen: die
 * Verzoegerung und dann auch meistens nach dem ersten Satz eine laengere
 * Pause."*
 *
 * Gemessen an 80 Vorlese-Laeufen aus vier Tagen (`[tts-t]`-Zeilen):
 *
 *     erstes Stueck     57 Zeichen  ->  3,8 s Vertonung
 *     Folgestuecke     183 Zeichen  -> 10,0 s Vertonung
 *
 * Das erste Stueck SPIELT 3,7 s, das zweite braucht 10,0 s zum Erzeugen.
 * Nachgerechnet mit der Schleuse (zwei gleichzeitig): **3,0 s Stille nach dem
 * ersten Satz**. Und Martins „meistens" stimmt genau — faellt der erste Satz
 * laenger aus (ab rund 120 Zeichen), traegt er lange genug und die Pause
 * bleibt aus.
 *
 * ## Warum eine Rampe und keine feste Zahl
 *
 * Vertonung und Wiedergabe sind beide linear in der Laenge, aber mit
 * verschiedenen Steigungen: 52,7 ms je Zeichen erzeugen gegen 64,5 ms
 * sprechen. Erzeugen ist also SCHNELLER — die Vertonung holt auf, sobald sie
 * einmal Vorsprung hat. Am Anfang hat sie keinen. Ein Stueck darf deshalb nur
 * so viel groesser sein als sein Vorgaenger, wie dessen Spielzeit deckt:
 *
 *     0,373 + 0,0527 * L_neu  <=  0,373 + 0,0527 * L_alt + 0,0645 * L_alt
 *     L_neu <= 2,22 * L_alt
 *
 * Simuliert mit den gemessenen Werten, Stuecke 57, 183, 183, …:
 *
 *     ohne Rampe    3,0 s Luecke
 *     Faktor 2,0    0,0 s nach Stueck 1, aber 1,4 s spaeter
 *     Faktor 1,8    0,0 s — durchgehend
 *
 * 1,8 statt der rechnerischen 2,22: Rand fuer schwankende Anbieterzeiten.
 */
export const VORLESE_WACHSTUM = 1.8;

/**
 * Wie lang das NAECHSTE Stueck hoechstens sein darf. (10.09.2026)
 *
 * ⚠️ Diese Regel gab es seit dem 09.09. — aber nur in `zerlegeFuerVorlesen`,
 *    also im Vorlese-Player. Der CHAT schneidet mit `takeSpeakable`, und dort
 *    stand eine feste Sammel-Grenze. Martins Messung vom 10.09., 15:05:
 *
 *        seg=0  len= 44  synth= 2922ms  wait=2920ms
 *        seg=1  len=164  synth=10794ms  wait=5316ms   <-- die Luecke
 *        seg=2  len=101  synth= 8374ms  wait=0ms
 *
 *    Stueck 1 war fast VIERMAL so lang wie Stueck 0. Es brauchte 10,8 s,
 *    das erste sprach aber nur 2,8 s: **5,3 Sekunden Stille** — genau die
 *    „zehnsekuendige Pause", die er zweimal gemeldet hat.
 *
 *    Die Rechnung stand die ganze Zeit oben bei VORLESE_WACHSTUM. Sie galt
 *    nur nicht dort, wo Martin sie hoert. Deshalb steht sie jetzt als EINE
 *    Funktion, die BEIDE Wege benutzen — eine zweite Kopie waere derselbe
 *    Fehler ein drittes Mal.
 */
export function naechsteGrenze(letzteLaenge: number, deckel = 420): number {
  if (letzteLaenge <= 0) return deckel;
  return Math.max(
    MIN_CHUNK_LEN,
    Math.min(deckel, Math.round(letzteLaenge * VORLESE_WACHSTUM)),
  );
}

/**
 * Kürzer wird ein Teil hinter einem ZWERG nicht. (24.09.2026, Ticket 2559)
 *
 * Die 100 stand bisher zweimal als nackte Zahl in `zerlegeMitRampe`. Nach
 * einem sehr kurzen Vorgänger („Ja.") sähe die Rampe 6 Zeichen vor — lauter
 * Zwergstücke mit je 0,37 s Fixkosten. Zwerge sind meist das Ende eines
 * Satzes; den Vorsprung tragen dann die Stücke davor.
 */
export const TEIL_BODEN = 100;

/**
 * Wie lang das nächste Teil sein darf, nachdem `letzteLaenge` Zeichen
 * abgegeben wurden. (24.09.2026, Ticket 2559)
 *
 * EINE Regel für beide Wege: den Chat (`takeSpeakable`) und das Leeren
 * (`zerlegeMitRampe`, über `gibRestAb`).
 *
 * ## Hinter einem Kopf gilt die Rampe unverkürzt
 *
 * Der erste Schnitt einer Antwort lag absichtlich früh, mitten im Satz
 * (`ersterSchnitt`, ab 40 Zeichen; seit Ticket 2692 an einem Satzzeichen).
 * Gemessen an Martins Antworten seit dem 17.09. (`[tts-t]`, Stück 1 gegen
 * Stück 0, Datei-Weg):
 *
 *     Faktor bis 1,8     153 Mal    4 % mit Lücke ab 0,5 s
 *     Faktor 1,8–2,1      44 Mal   14 %
 *     Faktor 2,1–2,4      45 Mal   42 %
 *     Faktor über 2,4     54 Mal   87 %
 *
 * Der Boden von 100 ließ hinter einem Kopf von 42 Zeichen genau die 2,4
 * zu. Ab einem Vorgänger von FIRST_SPEAKABLE_MIN liegt die Rampe bei 72
 * oder mehr, und `splitLongSentence` findet dort seine Klausel-Grenzen —
 * der Boden wird nicht gebraucht.
 *
 * ## Ohne Vorgänger
 *
 * Die Rampen-Obergrenze (420). Ein Satz, der hineinpasst, bleibt ganz,
 * statt wie bisher bei festen 140 Zeichen zu reißen.
 */
export function teilGrenze(letzteLaenge: number, vorrat = 0): number {
  return Math.max(mitBoden(naechsteGrenze(letzteLaenge)), deckungsGrenze(vorrat));
}

/**
 * Wie viel länger ein Satz sein darf als der Ton, der schon vor ihm in der
 * Schlange wartet. (28.09.2026, Ticket 2559)
 *
 * Martin, 28.09.: Pausen sitzen „quer über den Text verteilt" mitten im
 * Satz, die Stimme wirkt zerrissen, „Pause erst bei gesetztem Punkt".
 *
 * ## Gemessen (Journal, uid 1, 28.09. bis 16:45)
 *
 * 671 Nähte zwischen zwei Stücken, 172 davon mitten im Satz (26 %), in 86
 * von 107 Antworten mit mehr als einem Stück. An diesen Nähten wartete das
 * nächste Stück NIE auf seine Vertonung (`wait` p90 0 ms, nur 4 über
 * 200 ms), und kein Stück stockte in sich (`echt` − `audio` nie über
 * 0,5 s). Die Lücke kommt also weder aus der Vertonung noch aus der
 * Wiedergabe, sondern vom Schnitt selbst: Soniox spricht jedes Stück als
 * eigenen Satz, mit Satzmelodie am Ende und rund 0,12 s Stille davor und
 * 0,15 s dahinter (ffmpeg silencedetect), dazu 0,08 s Start.
 *
 * ## Warum die Rampe hier zu vorsichtig war
 *
 * `naechsteGrenze` misst nur am VORIGEN Stück. Das stimmt am Anfang einer
 * Antwort, wo das nächste Stück parallel zum vorigen vertont wird. Später
 * liegt aber viel mehr Ton davor: Der Text kommt mit rund 190 Zeichen je
 * Sekunde, gesprochen werden 15. Nachgerechnet an 976 Antworten seit dem
 * 16.09. (Vertonung 197 ms + 57,7 ms je Zeichen, Sprechen 66,2 ms je
 * Zeichen, zwei zugleich) war das nächste Stück an einer Naht mitten im
 * Satz ab dem dritten Stück im Median 12,7 s zu früh fertig. Der Schnitt
 * war dort oft unnötig.
 *
 * ## Die Regel
 *
 * Ein Satz bleibt ganz, wenn die zwei Stücke direkt vor ihm seine
 * Vertonung decken (`deckenderVorrat`). Das laufende Stück zählt nicht mit
 * (sein Rest ist Rand). Die Rampe bleibt die Untergrenze; am Anfang einer
 * Antwort ändert sich darum nichts.
 *
 * ⚠️ Nur die letzten ZWEI, nicht alles, was wartet (Prüfung 28.09.): Die
 *    wartenden Stücke sind oft selbst noch nicht vertont, und die Schleuse
 *    lässt höchstens `MAX_GLEICHZEITIG` zugleich rechnen. Muss das neue
 *    Stück warten, bekommt es seinen Platz erst, wenn eines der zwei
 *    laufenden fertig ist. Beide gehören zu Stücken vor ihm und sind noch
 *    nicht gespielt, also auch alles, was hinter ihnen steht, mindestens
 *    die letzten zwei. Mehr ist ohne Uhr nicht sicher: Im ersten Bau zählte
 *    alles Wartende, und bei 25 % langsamerer Vertonung kamen Lücken bis
 *    5 s dazu.
 *
 * ⚠️ 0,8 statt der rechnerischen 1,15 (66,2 zu 57,7 ms): Rand für langsame
 *    Vertonung. Seit dem 26.09. brauchte das erste Stück einer Antwort bei
 *    p90 das 1,31-fache der Kennlinie, bei p95 das 1,49-fache; 0,8 hält bis
 *    1,43. Nachgerechnet an 980 Antworten seit dem 16.09., 25 % langsamer:
 *    mit 0,8 keine zusätzliche Lücke, mit 1,0 zehn (bis 0,9 s). Nähte
 *    mitten im Satz 1.820 statt 2.058, ab der vierten Naht 608 statt 846.
 */
export const DECKUNG = 0.8;

/** Ticket 2559: was der wartende Ton deckt, höchstens die Rampen-Obergrenze. */
function deckungsGrenze(vorrat: number): number {
  return Math.min(naechsteGrenze(0), Math.floor(vorrat * DECKUNG));
}

/**
 * Ticket 2559: die Zeichen, die ein neues Stück sicher decken — die
 * letzten `MAX_GLEICHZEITIG` Stücke vor ihm, ob schon vertont oder nicht.
 * Ohne `wartend` (Vorlese-Player) nichts.
 */
function deckenderVorrat(
  wartend: readonly number[] | undefined,
  eigene: readonly string[],
): number {
  if (wartend === undefined) return 0;
  const alle = [...wartend, ...eigene.map((s) => s.length)];
  return alle.slice(-MAX_GLEICHZEITIG).reduce((n, l) => n + l, 0);
}

/** Dieselbe Regel, an der Rampe statt an der Länge des Vorgängers gemessen. */
function mitBoden(rampe: number): number {
  return rampe >= naechsteGrenze(FIRST_SPEAKABLE_MIN)
    ? rampe
    : Math.max(TEIL_BODEN, rampe);
}

/**
 * Wie kurz das ERSTE Stueck hoechstens sein darf.
 *
 * ⚠️ Ein sehr kurzer Einstiegssatz („Ja." „Und dann?") traegt die Rampe
 *    nicht: Er spielt 2 Sekunden, und schon das naechste Stueck braucht
 *    laenger zum Erzeugen. Gemessen in der Simulation: 1,2 s Stille bei
 *    einem 30-Zeichen-Einstieg. Ab rund 50 Zeichen traegt er.
 *
 * ⚠️ Das ist eine UNTERgrenze zum Sammeln, kein Schnitt: kurze Saetze werden
 *    zusammengefasst, keiner wird zerteilt.
 */
export const VORLESE_ERSTE_MIN = 50;

export function zerlegeFuerVorlesen(
  text: string,
  /*
   * ⚠️ 09.09.2026: 180 -> 120. Zweierlei haengt daran. Erstens deckelt es die
   *    Stille vor dem ersten Ton (180 Zeichen waeren 9,9 s, 120 sind 6,7 s).
   *    Zweitens entscheidet `ersteLaenge * 1,6` darunter, ob ein Text
   *    ueberhaupt zerlegt wird — ein Text von 250 Zeichen blieb bisher EIN
   *    Stueck und liess 13,5 s auf den ersten Ton warten; jetzt sind es 6,7.
   */
  ersteLaenge = 120,
  spaetereLaenge = 420,
): string[] {
  const ganz = (text ?? "").trim();
  if (ganz.length <= ersteLaenge * 1.6) return ganz ? [ganz] : [];
  return zerlegeMitRampe(ganz, ersteLaenge, spaetereLaenge, VORLESE_ERSTE_MIN);
}

/**
 * Die Rampe auf einen Text anwenden, der einen VORGAENGER hat. (10.09.2026)
 *
 * ⚠️ Der Unterschied zu {@link zerlegeFuerVorlesen} ist die Abkuerzung dort:
 *    Ein kurzer Text bleibt am Stueck, weil vor dem ERSTEN Stueck nichts
 *    klingt, gegen das eine Luecke entstehen koennte. Sobald schon Ton laeuft,
 *    gilt diese Abkuerzung nicht mehr — dann zaehlt allein die Grenze, die der
 *    Vorgaenger deckt. Genau daran hing Martins Rest-Stueck (siehe
 *    `TtsQueue.gibRestAb`).
 *
 * @param ersteGrenze  Was das naechste Stueck hoechstens tragen darf —
 *                     `naechsteGrenze(letzteLaenge)`, wenn schon Ton lief.
 * @param ersteMin     Untergrenze fuer das erste Stueck (nur sinnvoll, wenn
 *                     noch nichts klingt; sonst 0).
 */
export function zerlegeMitRampe(
  text: string,
  ersteGrenze: number,
  spaetereLaenge = 420,
  ersteMin = 0,
  /**
   * Ticket 2559: Längen der Stücke, die in der Schlange noch warten
   * (`TtsQueue`). Ohne Angabe gilt allein die Rampe, wie im Vorlese-Player.
   */
  wartend?: readonly number[],
): string[] {
  const ganz = (text ?? "").trim();
  if (!ganz) return [];
  /*
   * ⚠️ Untergrenze 100 — dieselbe wie unten in `grenzeJetzt`, und aus dem
   *    gleichen Grund: `splitLongSentence` sucht seine Klausel-Grenze erst ab
   *    KLAUSEL_MIN (80 Zeichen). Bei einem kleineren Deckel findet es keine
   *    und kappt HART, mitten im Wort. Nach einem sehr kurzen Vorgaenger
   *    („Ja.") saehe die Rampe 23 Zeichen vor — und Martin hoerte wieder
   *    zerschnittene Woerter. Eine halbe Sekunde Luecke ist das kleinere Uebel.
   *
   * ⚠️ 24.09.2026 (Ticket 2559): Die Untergrenze gilt nur noch hinter einem
   *    Zwerg (`mitBoden`, Regel und Messung bei `teilGrenze`). Hinter einem
   *    Kopf von 42 Zeichen ließ sie 100 zu, Faktor 2,4 — die Lücke fiel
   *    mitten in den Satz. Das harte Kappen verhindert jetzt
   *    `splitLongSentence` selbst: seine Klausel-Untergrenze wächst mit dem
   *    Deckel, und ohne Wortende im Fenster schneidet es am nächsten
   *    Wortende dahinter oder gar nicht (Prüfung des ersten Baus, 24.09.).
   */
  const ersteLaenge = mitBoden(ersteGrenze);
  // An Satzenden trennen.
  // ⚠️ Frueher `ganz.split(/(?<=[.!?…])\s+/)` — derselbe Fehler wie im Chat:
  //    „am 18. September" wurde am Punkt zerrissen. Eine Regel fuer beide.
  const saetze = teileInSaetze(ganz)
    .map((satz) => satz.trim())
    .filter((x) => x.length > 0);

  const stuecke: string[] = [];
  let puffer = "";
  /**
   * Wie lang das NAECHSTE Stueck hoechstens werden darf.
   *
   * ⚠️ Gemessen am ZULETZT ABGEGEBENEN Stueck, nicht an der Grenze, die
   *    dafuer galt: Ein Satz, der seine Grenze nicht ausfuellt, darf den
   *    naechsten Schritt nicht groesser machen, als seine eigene Spielzeit
   *    deckt.
   *
   * ⚠️ Untergrenze 100, damit `splitLongSentence` seine Klausel-Grenzen noch
   *    findet (KLAUSEL_MIN = 80). Ohne sie schneidet es bei kleinem Deckel
   *    hart mitten im Wort.
   *
   * ⚠️ 24.09.2026 (Ticket 2559): dieselbe Regel wie im Chat, `teilGrenze`.
   *    Hier stand die Rechnung ein zweites Mal von Hand.
   */
  const grenzeJetzt = (): number => {
    // Ticket 2559: Was hier schon abgegeben ist, wartet danach ebenfalls.
    const vorrat = deckenderVorrat(wartend, stuecke);
    if (stuecke.length === 0) return Math.max(ersteLaenge, deckungsGrenze(vorrat));
    const vorher = stuecke[stuecke.length - 1].length;
    return Math.min(spaetereLaenge, teilGrenze(vorher, vorrat));
  };
  const gibAb = (): void => {
    if (puffer) {
      stuecke.push(puffer);
      puffer = "";
    }
  };

  for (const satz of saetze) {
    /*
     * ⚠️ 09.09.2026 — DIE RAMPE MUSS AUCH EINZELNE SAETZE HALTEN.
     *
     * Im ersten Anlauf wurde nur das SAMMELN begrenzt: Ein Satz, der die
     * Grenze allein sprengt, wanderte trotzdem ungeteilt ins naechste
     * Stueck. Gemessen blieb die Luecke dadurch bei 4,1 s statt 0 — die
     * Rampe stand im Code und wirkte nicht.
     *
     * Deshalb wird HIER geteilt, mit der Grenze DIESES Schrittes, nicht
     * vorab mit `spaetereLaenge`.
     */
    /*
     * ⚠️ IMMER NUR DAS NAECHSTE TEIL, mit der GERADE gueltigen Grenze.
     *
     * Im zweiten Anlauf wurde der Satz EINMAL zerlegt und die Teile dann
     * abgearbeitet — aber die Grenze aendert sich waehrenddessen, sobald ein
     * Stueck abgegeben wird. Gemessen kam so ein 209-Zeichen-Stueck hinter
     * einem 80er heraus (Faktor 2,6): 11,4 s Vertonung gegen 9,7 s Deckung,
     * also wieder eine Luecke. Die Rampe stand im Code und wirkte nur halb.
     */
    let rest = satz;
    while (rest) {
      const g = grenzeJetzt();
      /*
       * ⚠️ 09.09.2026, DRITTER ANLAUF — ERST ABGEBEN, DANN SCHNEIDEN.
       *
       * In den ersten beiden Anlaeufen wurde geschnitten und danach
       * abgegeben. Der uebrig gebliebene Teil war dann mit der ALTEN Grenze
       * gemessen und wanderte ungeprueft ins naechste Stueck. Im Ablauf
       * mitgeschrieben:
       *
       *     grenze=214  rest=300  -> teile 209,90   puffer=80
       *
       * Die 214 stammen vom vorletzten Stueck (119), nicht vom aktuellen
       * (80). Nach dem Abgeben galt 144 — die 209 waren da schon gesetzt.
       * Ergebnis: 209 hinter 80, Faktor 2,6, und wieder eine Luecke.
       *
       * `continue` schneidet denselben Rest mit der DANN gueltigen Grenze
       * neu. Keine Endlosschleife: nach `gibAb()` ist der Puffer leer, die
       * Bedingung also beim naechsten Durchlauf falsch.
       */
      const zuKurzFuerDenAnfang =
        stuecke.length === 0 && puffer.length < ersteMin;
      if (puffer && !zuKurzFuerDenAnfang) {
        const naechstes = splitLongSentence(rest, g)[0];
        if ((puffer + " " + naechstes).length > g) {
          gibAb();
          continue;
        }
      }
      const teile = splitLongSentence(rest, g);
      puffer = puffer ? `${puffer} ${teile[0]}` : teile[0];
      rest = teile.slice(1).join(" ");
    }
  }
  if (puffer) stuecke.push(puffer);
  return stuecke;
}

/**
 * Ticket 768 (2026-07-27, Martin: "Es kam schon sehr viel Text im Screen, aber
 * erst viel spaeter kam dann die Audio-Spur"): Bisher startete JEDES Stueck
 * seine Vertonung sofort. Bei einer laengeren Antwort liefen damit fuenf, sechs
 * Soniox-Anfragen gleichzeitig — und genau da bricht der Anbieter ein.
 *
 * Gemessen am 27.07. mit demselben Satz (95 Zeichen, je einzigartig, kein Cache):
 *   1 gleichzeitig: 13,2 s
 *   2 gleichzeitig: 14,1 s   (die zweite ist also praktisch geschenkt)
 *   4 gleichzeitig: 27,2 s   (ab hier kostet jede weitere volle Zeit)
 *
 * Die Warteschlange hat sich also SELBST ausgebremst: je mehr Text, desto mehr
 * parallele Anfragen, desto langsamer jede einzelne — der Rueckstand wuchs mit
 * der Laenge der Antwort. Zwei gleichzeitig ist das gemessene Optimum.
 */
const MAX_GLEICHZEITIG = 2;
let laufendeVertonungen = 0;
/*
 * ⚠️ 17.10.2026 — DIESE SCHLEUSE GILT FÜR ALLE SCHLANGEN DER SEITE (Vespera UND Schmied), nicht je Schlange.
 *
 * Bis heute war sie streng der Reihe nach (`shift()`). Der Schmied im Ganz-Modus reiht alle Stücke seines Beitrags auf
 * einmal ein; auf dem Datei-Weg (`/api/voice-preview`, die Antwort ist die fertige Datei) hält jeder Abruf seinen Platz
 * hier für die ganze Vertonung. Vesperas Stück stand dann HINTER allen Schmied-Stücken — schon im Gerät, bevor der Server
 * es sah; den Nachrang von `vorab` in server/soniox-schleuse.ts erreichte es nie.
 *
 * Jetzt: ein Wartender mit `nachrang()` (die Schlange lädt gerade vorab, `TtsQueue.vorabLaedt`, beim Freiwerden gefragt)
 * tritt hinter jeden anderen Wartenden zurück. Unter Gleichrangigen bleibt die Ankunft. Ein schon laufender Abruf wird
 * nicht abgebrochen. Der frei werdende Platz geht DIREKT an den Geweckten über (kein Zählen nach dem Aufwachen) — sonst
 * könnte ein Neuer in derselben Runde an ihm vorbei einen dritten Platz nehmen.
 */
const wartende: Array<{ weiter: () => void; nachrang: () => boolean }> = [];

async function mitSchleuse<T>(fn: () => Promise<T>, nachrang: () => boolean = () => false): Promise<T> {
  if (laufendeVertonungen >= MAX_GLEICHZEITIG) {
    // Geweckt wird mit dem Platz des Gehenden (siehe `finally`) — hier nicht noch einmal zählen.
    await new Promise<void>((weiter) => wartende.push({ weiter, nachrang }));
  } else {
    laufendeVertonungen++;
  }
  try {
    return await fn();
  } finally {
    const vorn = wartende.findIndex((w) => !w.nachrang());
    const [naechster] = wartende.splice(vorn >= 0 ? vorn : 0, 1);
    if (naechster) naechster.weiter();
    else laufendeVertonungen--;
  }
}


/**
 * Der frueheste natuerliche Halt ab `FIRST_SPEAKABLE_MIN`. 0 = noch keiner.
 *
 * ⚠️ 09.09.2026 — DIE RICHTUNG IST DER PUNKT. Diese Schleife lief bis heute
 *    RUECKWAERTS (`for (let i = window.length - 1; i >= MIN; i--)`) und nahm
 *    damit das LAENGSTE Stueck, das ins Fenster passte. Fuer die Prosodie war
 *    das richtig gedacht, fuer die Wartezeit ist es genau verkehrt herum:
 *    jedes Zeichen im ersten Stueck kostet 52,7 ms Stille, bevor ueberhaupt
 *    Ton kommt. Vorwaerts gesucht wird das KUERZESTE zulaessige Stueck.
 *
 * Eine Klausel-Grenze wird nicht gesucht: das naechste Stueck folgt im
 * Median nach 0,09 s (gemessen, `[tts-gap]`), der Halt ist also nicht als
 * Satzende hoerbar.
 *
 * ⚠️ 24.09.2026 (Ticket 2559): Der Median stimmt, für das Stück direkt
 *    hinter DIESEM Schnitt stimmte er nicht. Dort kam in 41 von 131
 *    Antworten (22.–24.09.) eine Lücke ab 0,5 s, weil der Rest des Satzes an
 *    der Rampe vorbeiging. Das regelt jetzt `teilGrenze`; hier bleibt der
 *    Schnitt, wo er war, mit einer Ausnahme: nicht VOR einem Gedankenstrich.
 *    Drei von sieben ersten Stücken am 24.09. früh endeten so („… an" |
 *    „— …"): der Halt fiel vor die Stelle, an der man Luft holt, und das
 *    nächste Stück begann mit dem Strich. Zwei Zeichen weiter liegt er
 *    richtig.
 *
 * ⚠️ 27.09.2026 (Ticket 2692): „nicht als Satzende hörbar" oben stimmt
 *    nicht. Soniox spricht jedes Stück als eigenen Satz mit eigenem
 *    Tonfall; ein Schnitt am Wortrand mitten im Satz klingt wie Stocken.
 *    Martin hörte „Ich prüfe kurz, ob es dafür schon ein Ticket" | „gibt.",
 *    das erste Stück lag seit dem 16.09. jeden Tag im Median bei 42 bis 44
 *    Zeichen. Jetzt gilt ab `FIRST_SPEAKABLE_MIN` nur ein Satzende oder eine
 *    `KLAUSEL_GRENZE`, geschnitten HINTER dem Zeichen. Ein bloßer Wortrand
 *    nur, wenn bis 90 (seit der dritten Runde `ERSTER_WORTRAND_AB`) keine
 *    solche Stelle kommt; dann der erste ab `FIRST_SPEAKABLE_MIN`, wie
 *    bisher (kürzestes Stück, frühester Ton).
 *
 * ⚠️ 27.09.2026 (Ticket 2692, dritte Runde): Der Wortrand blieb häufig,
 *    bei 121 von 777 Antworten seit dem 16.09. kam bis 90 kein
 *    Satzzeichen. pre.3375 schnitt dann vor einem Bindewort; das traf nur
 *    zur Hälfte eine Satzteilgrenze („als"/„wie" 7 von 7 falsch) und ist
 *    wieder entfernt. Nicht weiter an dieser Regel drehen: Den kurzen
 *    ersten Satz formt das Modell (SPRECHSTIL in server/chat.ts), und
 *    der Wortrand gilt erst, wenn bis `ERSTER_WORTRAND_AB` weder Satzende
 *    noch Satzzeichen kam.
 *
 *    Die älteren Regeln hält das ein: 2245 verbot das FRÜHE Komma
 *    („Morgen," als eigenes Stück), ein Komma zählt erst ab 40 Zeichen.
 *    2559 verbot den Halt VOR dem Gedankenstrich; der Halt liegt jetzt
 *    dahinter, dort, wo man Luft holt.
 */
/** Ticket 2559: Gedankenstrich (auch als Bindestrich mit Leerzeichen) direkt voraus. */
const STRICH_FOLGT = /^\s*[\u2014\u2013-]/;

function ersterSchnitt(text: string): number {
  if (text.length < FIRST_SPEAKABLE_MIN) return 0;
  const fenster = text.slice(0, ERSTER_WORTRAND_AB);
  // Dieselbe Satzregel wie `takeSpeakable` (shared/satzgrenzen.ts).
  const satzEnde = new Set(satzEnden(text, true));
  /** Ticket 2692: der erste bloße Wortrand, nur als Notlösung. */
  let wortrand = 0;
  for (let i = FIRST_SPEAKABLE_MIN; i < fenster.length; i++) {
    if (fenster[i] !== " ") continue;
    /*
     * Ticket 2559: erst schneiden, wenn feststeht, was folgt. Im Strom
     * steht das Leerzeichen oft am Pufferende, und der Strich kommt mit dem
     * nächsten Häppchen — das kostet ein Häppchen Wartezeit, sonst nichts.
     */
    const danach = text.slice(i + 1);
    if (!/\S/.test(danach) || STRICH_FOLGT.test(danach)) continue;
    if (satzEnde.has(i) || KLAUSEL_GRENZE.test(text.slice(i - 1, i + 1))) return i + 1;
    if (!wortrand) wortrand = i + 1;
  }
  // Ticket 2692: kann im Fenster noch ein Satzzeichen kommen, warten.
  if (text.length <= ERSTER_WORTRAND_AB) return 0;
  // Ein einzelnes Wort laenger als das Fenster: hart kappen.
  return wortrand || ERSTER_WORTRAND_AB;
}

/**
 * 2245: erster Brocken nicht am FRÜHEN Komma. Wartet auf ein Satzende oder
 * eine Klausel-Grenze ab `FIRST_SPEAKABLE_MIN`, einen bloßen Wortrand erst
 * über `ERSTER_WORTRAND_AB` (Ticket 2692, siehe `ersterSchnitt`).
 */
export function firstSpeakableCut(buf: string): number {
  if (buf.trim().length < MIN_CHUNK_LEN) return 0;
  return ersterSchnitt(buf);
}

/**
 * Einen fertigen ERSTEN Satz in Kopf und Rest teilen. (09.09.2026)
 *
 * ## Warum es diese Funktion braucht
 *
 * `firstSpeakableCut` griff nur, solange noch KEIN Satzende im Puffer stand.
 * Kam der erste Satz vollstaendig an, nahm `takeSpeakable` ihn ganz — bis zu
 * 140 Zeichen, also bis 7,8 s Stille. Gemessener Median des ersten Stuecks:
 * 76 Zeichen. Der Hebel lag damit gar nicht im Fenster, sondern hier.
 *
 * ⚠️ Kopf UND Rest gehen als zwei Stuecke zurueck, nie ueber den Sammler.
 *    Der Sammler merkt sich mit `sammlerStart` eine Stelle, hinter der noch
 *    nichts abgegeben wurde — nach einem Kopf stimmt das nicht mehr, und
 *    genau daran hat Martin am 07.09. Saetze doppelt gehoert.
 */
export function ersterBrocken(satz: string): [string, string] {
  if (satz.length <= FIRST_SPEAKABLE_MAX) return [satz, ""];
  const schnitt = ersterSchnitt(satz);
  if (!schnitt) return [satz, ""];
  const kopf = satz.slice(0, schnitt).trim();
  const rest = satz.slice(schnitt).trim();
  return rest.length >= MIN_CHUNK_LEN ? [kopf, rest] : [satz, ""];
}

/**
 * Ab hier holt die Vertonung wieder auf. (04.09.2026, Ticket 2047)
 *
 * Martin: *„Wenn Vespera einen Text vorliest, lässt sie zwischen den Sätzen
 * oft eine Pause von ein paar Sekunden. Das ist vom Lesefluss her eigenartig."*
 *
 * GEMESSEN, warum:
 *
 *     56 Zeichen → 3.176 ms      114 Zeichen → 5.478 ms
 *    176 Zeichen → 8.715 ms
 *
 * Daraus: 2,4 s Fixkosten plus 0,036 s je Zeichen. Gesprochen wird mit rund
 * 15,5 Zeichen pro Sekunde. Beides gleichgesetzt:
 *
 *     Gleichstand bei 84 Zeichen
 *
 * Ein Stück UNTER dieser Länge braucht länger zum Erzeugen als zum Sprechen —
 * die Vertonung fällt zurück, und man hört die Lücke. `takeSpeakable` nahm
 * aber jeden Satz ab `MIN_CHUNK_LEN` (6 Zeichen!) als eigenes Stück. „Alles
 * klar." ist elf Zeichen: 2,8 s erzeugen für 0,7 s Ton.
 *
 * Deshalb werden kurze Sätze jetzt gesammelt, bis das Stück Vorsprung hat.
 * Mit Puffer über den Gleichstand — bei 110 Zeichen bleiben rund 1,1 s je
 * Stück übrig, und der Vorsprung wächst mit jedem weiteren.
 *
 * ⚠️ Das ERSTE Stück ist ausgenommen (`firstDone`). Es entscheidet, wann
 * überhaupt Ton kommt; dort ist kurz richtig. Genau dafür gibt es
 * `firstSpeakableCut`.
 */
export const SAMMEL_MIN = 110;

/** Isolierter Splitter: fertige Brocken + Rest. Ohne Audio, ohne Fetch. */
export function takeSpeakable(
  buffer: string,
  firstDone: boolean,
  /**
   * Wie lang das zuletzt abgegebene Stueck war — daraus waechst die Grenze
   * fuer das naechste (siehe `naechsteGrenze`). 0 = noch nichts abgegeben.
   */
  letzteLaenge = 0,
  /**
   * Ticket 2559: Längen der Stücke, die in der Schlange noch warten. Ohne
   * Angabe gilt allein die Rampe.
   */
  wartend?: readonly number[],
): { pieces: string[]; rest: string; firstDone: boolean; letzteLaenge: number } {
  const pieces: string[] = [];
  /** Waechst mit jedem abgegebenen Stueck — die Rampe (siehe naechsteGrenze). */
  let letzte = letzteLaenge;
  let lastEnd = 0;
  /*
   * ⚠️ 06.09.2026 — hier stand `/([.!?]["')\]]?)\s+/g`, und die hielt JEDEN
   *    Punkt mit Leerzeichen dahinter fuer ein Satzende. „Der Termin ist am
   *    18. September" zerfiel damit in „…am 18." und „September …", und die
   *    Sprachausgabe bekam „…am 18." als vollstaendige Anfrage. Martin: *„Es
   *    werden Saetze abgeschnitten teilweise … er kann nicht einmal mehr die
   *    Woerter richtig aussprechen."* Gemessen gegen Soniox: derselbe Text am
   *    Stueck kommt vollstaendig und richtig zurueck — geschnitten haben wir.
   *    Die Regel steht jetzt an EINER Stelle, in shared/satzgrenzen.ts.
   */
  const enden = satzEnden(buffer, true);
  let done = firstDone;
  /** Sammelt kurze Sätze, bis das Stück die Vorsprungsgrenze reißt. */
  let sammler = "";
  /**
   * Wo im Puffer der Sammler ANGEFANGEN hat.
   *
   * ⚠️ 07.09.2026 — der eigentliche Fehler stand weiter unten: Dort wurde
   *    die Stelle ZURÜCKGERECHNET (`lastEnd -= sammler.length + 1`). Das
   *    `+ 1` ist die Annahme, zwischen zwei Sätzen stehe genau EIN
   *    Zeichen Zwischenraum. Bei einem Absatz („\n\n") sind es zwei — und
   *    dann beginnt der Rest ein Zeichen zu spät.
   *
   *    Martin hat genau das gehört: *„Er hat immer wieder den ersten
   *    Buchstaben abgeschnitten, dann kam statt ‚Der' ein ‚er'."* An seinem
   *    Text nachgestellt, Stück 3 lautete wörtlich „er Inhalt kommt
   *    hinterher …".
   *
   *    Eine Stelle, die man sich MERKEN kann, muss man nicht ausrechnen.
   */
  let sammlerStart = 0;
  const gibAb = (): void => {
    if (sammler) {
      pieces.push(sammler);
      letzte = sammler.length;
      sammler = "";
      done = true;
    }
  };
  for (const ende of enden) {
    // Den Zwischenraum hinter dem Satzzeichen mitnehmen, damit der Rest
    // nicht mit einem Leerzeichen beginnt (so hielt es die alte Regel auch).
    const abstand = /^\s*/.exec(buffer.slice(ende))?.[0].length ?? 0;
    const cutAt = ende + abstand;
    const sentence = buffer.slice(lastEnd, cutAt).trim();
    /*
     * Wurde aus DIESEM Satz schon etwas abgegeben? Dann darf der Sammler
     * den Rest nicht zurueckhalten — siehe die Regel unter der Schleife.
     */
    let ausDiesemSatzAbgegeben = false;
    /*
     * ⚠️ 24.09.2026 (Ticket 2559) — DIE RAMPE GILT AUCH FÜR EINEN SATZ.
     *
     * Hier stand `for (const piece of splitLongSentence(sentence))`: der
     * Satz wurde EINMAL mit festen 140 Zeichen zerlegt. Die Rampe begrenzte
     * damit nur das Sammeln mehrerer Sätze, nie den einen Satz. Und der
     * Kopf-Schnitt gab seinen Rest ungeprüft ab.
     *
     * Gemessen 22.–24.09. bei Martin: nach dem ersten Stück (40–48 Zeichen,
     * mitten im Satz geschnitten) kam in 41 von 131 Antworten eine Lücke ab
     * 0,5 s, Median rund 2 s, bei späteren Stücken in 6 %. Bei diesen
     * Lücken war das Stück danach im Median 2,7-mal so lang wie sein
     * Vorgänger. Die Lücke fiel damit genau auf den Schnitt mitten im Satz.
     *
     * Jetzt wird wie in `zerlegeMitRampe` immer nur das NÄCHSTE Teil
     * geschnitten, mit der gerade gültigen Grenze (`teilGrenze`). Umgekehrt
     * reißt ein Satz, der unter die Rampe passt, nicht mehr bei 140.
     */
    let rest = sentence;
    while (rest) {
      /*
       * Ticket 2559 (28.09.2026): Decken die Stücke davor den ganzen Satz,
       * bleibt er ganz (`DECKUNG`). Was dieser Aufruf schon abgegeben hat,
       * wartet danach ebenfalls.
       */
      const teile = splitLongSentence(
        rest,
        teilGrenze(letzte, deckenderVorrat(wartend, pieces)),
      );
      const piece = teile[0] ?? "";
      const danach = teile.slice(1).join(" ");
      if (!piece) break;
      /*
       * ⚠️ 07.09.2026 — hier stand `if (piece.length < MIN_CHUNK_LEN) continue;`
       *    und darunter rueckte `lastEnd` trotzdem weiter. Ein Satz unter
       *    sechs Zeichen war damit LAUTLOS WEG: „Ja." „Nein." „Kurz."
       *    Gefangen von der breiten Probe in vorlesen-kein-verlust.test.ts.
       *
       *    Die Absicht der Grenze war, fuer drei Zeichen keine eigene
       *    Sprachanfrage zu stellen — nicht, sie wegzuwerfen. Der Sammler
       *    kann genau das: Er nimmt sie mit und gibt sie zusammen mit dem
       *    naechsten Satz ab.
       */
      // Solange noch kein Ton lief, zählt Tempo mehr als Fluss.
      if (piece.length >= MIN_CHUNK_LEN && !done && !sammler) {
        const [kopf, schwanz] = ersterBrocken(piece);
        pieces.push(kopf);
        letzte = kopf.length;
        done = true;
        ausDiesemSatzAbgegeben = true;
        // Ticket 2559: der Schwanz läuft durch die Rampe, nicht an ihr vorbei.
        rest = schwanz && danach ? `${schwanz} ${danach}` : schwanz || danach;
        continue;
      }
      if (!sammler) sammlerStart = lastEnd;
      /*
       * ⚠️ 10.09.2026: Hier stand `if (sammler.length >= SAMMEL_MIN)` — also
       *    erst NACH dem Anhaengen geprueft, und mit einer festen Grenze.
       *    Beides zusammen ergab Martins 164-Zeichen-Stueck: zwei Saetze
       *    wurden zusammengelegt, weil der Sammler nach dem ersten noch
       *    unter 110 lag. Jetzt waechst die Grenze mit dem Vorsprung, und
       *    ein Stueck, das sie reissen wuerde, wird VORHER abgegeben.
       */
      /*
       * ⚠️ Zwei verschiedene Zahlen, und der erste Anlauf hat sie verwechselt.
       *
       *    OBERGRENZE (`deckel`) — was die Rampe sagt: So lang darf das
       *    naechste Stueck hoechstens sein, damit es fertig ist, bevor das
       *    vorige ausgesprochen ist. Ohne Vorgaenger gibt es sie nicht.
       *
       *    UNTERGRENZE (`abGeben`) — wozu der Sammler da ist: Unter
       *    SAMMEL_MIN lohnt keine eigene Sprachanfrage; kurze Saetze werden
       *    zusammengelegt. Sie darf die Obergrenze nie ueberschreiten.
       *
       *    Mein erster Anlauf nahm die Rampe als UNTERgrenze — dann blieb
       *    ein vollstaendiger Satz von 118 Zeichen liegen, weil er die 140
       *    nicht erreichte. Genau das faengt der Test von 2245 ab.
       */
      /*
       * Ticket 2559: dieselbe Deckung wie beim Teilen oben. Sonst gibt der
       * Sammler hinter einem kurzen Stück jeden kurzen Satz einzeln ab, die
       * Rampe bleibt klein, und der nächste lange Satz reißt.
       */
      const deckel =
        letzte > 0
          ? Math.max(
              naechsteGrenze(letzte),
              deckungsGrenze(deckenderVorrat(wartend, pieces)),
            )
          : Infinity;
      const abGeben = Math.min(SAMMEL_MIN, deckel);
      const zusammen = sammler ? sammler.length + 1 + piece.length : piece.length;
      // Was den Deckel sprengen wuerde, geht vorher raus.
      if (sammler && zusammen > deckel) {
        gibAb();
        ausDiesemSatzAbgegeben = true;
        /*
         * Ticket 2559: und das Teil wird danach NEU geschnitten — dieselbe
         * Lehre wie „ERST ABGEBEN, DANN SCHNEIDEN" in `zerlegeMitRampe`.
         * Nach dem Abgeben gilt eine andere Grenze; `rest` bleibt, wie er
         * war, `sammlerStart` setzt der nächste Durchlauf.
         */
        continue;
      }
      rest = danach;
      sammler = sammler ? `${sammler} ${piece}` : piece;
      if (sammler.length >= abGeben) {
        gibAb();
        ausDiesemSatzAbgegeben = true;
      }
    }
    /*
     * ⚠️ 07.09.2026 — EIN SATZ GANZ ODER GAR NICHT.
     *
     * Martin: *„Er spricht immer noch Doppelsaetze."* Nachgestellt mit
     * seinem eigenen Satz („Dann lag ich falsch, und zwar an der
     * entscheidenden Stelle, denn du hast das Haus heruntergerissen …"):
     * Der Satz ist laenger als 140 Zeichen, `splitLongSentence` macht zwei
     * Stuecke daraus. Das erste ging sofort raus (Tempo vor Fluss), das
     * zweite blieb im Sammler — und `sammlerStart` zeigte auf den ANFANG
     * des Satzes, also auf Text, der schon klang. Der Rest begann damit
     * erneut beim ersten Stueck, und Martin hoerte es zweimal.
     *
     * `sammlerStart` kann nur eine Stelle bezeichnen, hinter der noch
     * NICHTS abgegeben wurde. Also gilt: Wer aus einem Satz etwas
     * abgegeben hat, gibt den Satz zu Ende ab. Dann stimmt `lastEnd =
     * cutAt` immer, und der Sammler haelt ausschliesslich ganze Saetze ab
     * `sammlerStart`.
     */
    if (ausDiesemSatzAbgegeben && sammler) gibAb();
    lastEnd = cutAt;
  }
  // Was im Sammler bleibt, ist noch nicht lang genug — es wandert in den
  // Rest und wächst mit dem nächsten Textstück weiter. Nur so entsteht
  // kein Zwerg-Stück am Ende jeder Runde.
  if (sammler) {
    // Die gemerkte Stelle, nicht die gerechnete — siehe sammlerStart.
    lastEnd = sammlerStart;
    sammler = "";
  }
  let rest = lastEnd > 0 ? buffer.slice(lastEnd) : buffer;
  if (!done) {
    const cut = firstSpeakableCut(rest);
    if (cut > 0) {
      const frag = rest.slice(0, cut).trim();
      if (frag.length >= MIN_CHUNK_LEN) {
        pieces.push(frag);
        letzte = frag.length;
        rest = rest.slice(cut);
        done = true;
      }
    }
  }
  return { pieces, rest, firstDone: done, letzteLaenge: letzte };
}

/**
 * Wie lange es vor dem Absatz nach einem Werkzeug mindestens still ist.
 * (24.09.2026, Ticket 2558)
 *
 * Martin: Zwischen dem Text vor einem Werkzeug und dem Text danach soll
 * „eine hörbare Pause von etwa einer Sekunde" liegen. Der Server setzt dort
 * einen Absatz (shared/absatzgrenze.ts); die Schlange liest ihn am Text ab.
 *
 * ⚠️ MINDESTENS, nicht zusätzlich: Gezählt wird ab dem Ende des vorigen
 *    Tons. Lief das Werkzeug zehn Sekunden, war es längst still — dann
 *    kommt nichts dazu. Nur wenn der Text danach schneller da ist, als der
 *    Satz davor ausklingt, wird bis zu dieser Zeit gewartet.
 *
 * ⚠️ NUR an der Werkzeug-Naht, also nach `flushPending` (Chat und
 *    Sprechtaste rufen es bei jedem Werkzeugaufruf). Ein Absatz, den das
 *    Modell mitten in den Strom schreibt, bekommt diese Pause NICHT.
 *    „Puffer leer" taugt NICHT als Ersatz für die Naht: Ob der Puffer an
 *    einem Modell-Absatz leer ist, hängt davon ab, wie der Strom die
 *    Umbrüche verteilt. Nachgestellt pausierte eine Antwort damit am ersten
 *    Absatz und am zweiten nicht. Wo Pausen sonst hingehören, ist
 *    Ticket 2559.
 */
export const ABSATZ_PAUSE_MS = 1000;

/**
 * Wie lange es nach einem Satzende mindestens still ist, bevor das nächste
 * Stück beginnt. (27.09.2026, Ticket 2692)
 *
 * Martin: Das Vorlesen wirkt hektisch, fast keine Pausen zwischen den
 * Sätzen. Gemessen: Soniox lässt INNERHALB einer Datei 0,43 bis 0,68 s
 * zwischen zwei Sätzen, unsere Stücke folgen aber nahtlos (`[tts-gap]`,
 * rund 0,1 s samt Start). Endet ein Stück mit einem Satz, fehlt an der
 * Naht genau diese Pause, und je kürzer die Stücke, desto öfter.
 *
 * ⚠️ Derselbe Mechanismus wie ABSATZ_PAUSE_MS (`pauseVorStueck`): gezählt
 *    ab dem Ende des vorigen Tons, MINDESTENS, nicht zusätzlich. Eine
 *    Vertonung, die ohnehin nachhinkt, verlängert die Stille nicht.
 */
export const SATZ_PAUSE_MS = 400;

/**
 * `bereit` der Zeile `[tts-t]` (17.10.2026): ms ab der Freigabe des Stücks — eingereiht UND an der Reihe (`dran`) —
 * bis es bereit war. Nie negativ (lag es schon fertig da, 0). Rein. Die Spalte steht am ENDE der Zeile: die Leser
 * (scripts/tts-luecken.mjs, scripts/pcm-vorher-nachher.mjs, server/latenz-geraet.ts) bleiben, wie sie sind.
 */
export function tonBereitMs(o: { eingereiht: number; dran: number; bereitAm: number }): number {
  return Math.max(0, Math.round(o.bereitAm - Math.max(o.eingereiht, o.dran)));
}

/**
 * Die Mindeststille vor einem Stück. EIN Besitzer für die Pause
 * (Ticket 2692): Absatz nach einem Werkzeug 1000 ms, nach einem Satzende
 * 400 ms, sonst keine. Treffen beide zusammen, gilt die Absatzpause, nicht
 * die Summe.
 */
function pauseVorStueck(vorigesStueck: string, absatz: boolean): number {
  if (absatz) return ABSATZ_PAUSE_MS;
  return vorigesStueck && endetMitSatz(vorigesStueck) ? SATZ_PAUSE_MS : 0;
}

/** Sichtbarkeit der Seite fürs Journal (`visible` | `hidden` | `-` ohne Dokument). */
function sichtbarkeit(): string {
  return typeof document === "undefined" ? "-" : document.visibilityState;
}

/** Wie oft und wie lange bei `motor-steht…` (neu) gefragt wird: stimme-nativ.ts (V8 und V9 teilen sie). */
export { NATIV_FRAGEN_MAX_MS, NATIV_NEU_FRAGEN_MS, NATIV_ZURUECK_FRIST_MS, NATIV_ZURUECK_MAX };

/** 17.10.2026 (stimme-nativ): die Datei eines Stücks, früh geholt — `abbrechen` beendet den Abruf. */
interface DateiVorab {
  url: string;
  fertig: Promise<{ bytes: Uint8Array; typ: string } | string>;
  abbrechen(): void;
}

export class TtsQueue {
  private buffer = "";
  private playing: HTMLAudioElement | null = null;
  /**
   * Ticket 2547 (24.09.2026): Spielt gerade ein Stück NATIV (Bildschirm aus,
   * siehe vorlesen-nativ.ts)? Dann ist `playing` null, und trotzdem klingt
   * etwas — jede Frage „spielt etwas?" geht darum über `spieltEtwas()`.
   */
  private nativSpielt = false;
  /**
   * Ticket 2919 (02.10.2026): Spielt gerade ein Stück als PCM über Web Audio?
   * Dann ist `playing` null — wie beim nativen Spieler. Ein Besitzer: `spielePcm`.
   */
  private pcmSpielt: PcmStueck | null = null;
  /**
   * 17.10.2026 (Stimme nativ, stimme-nativ.ts): Spielt gerade ein Stück über die App (PawbotStimme)? Dann klingt nichts
   * in WebKit. `gespieltMs`/`dauerS` für die Bilanz bei einem Abbruch. Ein Besitzer: `spieleUeberApp`.
   */
  private stimmeNativ: { gespieltMs: number; dauerS: number } | null = null;
  /** Die Antwort fragt die App EINMAL, ob sie die Stimme spielen kann (`stimmeBereit`). */
  private nativEntscheid: Promise<boolean> | null = null;
  /**
   * Dieses Stück läuft auf dem Web-Weg (die App lehnte ab oder kann nicht) — seine Fortsetzungen (Wiederanlauf,
   * Nachladen, PCM→Datei) fragen die App nicht noch einmal. Der Abspiel-Lauf setzt es je Stück zurück.
   */
  private nurWeb = false;
  /** Die App sagte für diese Antwort nein (`stimmeBereit`): alles im Web, mit Vorladen wie heute. */
  private webAntwort = false;
  /**
   * 27.10.2026: …und zwar nur, weil der Mikro-Motor stand (`motor-steht…`). Dann fragt jedes weitere Stück die App noch
   * einmal (`nativZurueck`) — der Motor kommt oft mitten in der Antwort wieder (Journal 13:01:56 → 13:02:29).
   */
  private webWegenMotor = false;
  /** Wie oft diese Antwort schon zur App zurückging (`nativZurueck`, höchstens `NATIV_ZURUECK_MAX`). */
  private zurueckZahl = 0;
  /** Die vorgeladene Datei des Stücks, das gerade dran ist (stimme-nativ). Der Abspiel-Lauf setzt sie je Stück. */
  private dateiVorab: DateiVorab | null = null;
  /** Das Stück, das gerade dran ist (der Abspiel-Lauf setzt es) — nur dessen Datei gehört in `dateiVorab`. */
  private aktuellesStueck: object | null = null;
  /** Das Hi-Fi-Vorwärmen wurde für den nativen Weg ausgelassen — fällt die Antwort auf den Web-Weg, kommt es nach. */
  private hifiAufgeschoben = false;
  /**
   * 11.10.2026 („Vorlesen hakt“): was den PCM-Stücken DIESER Antwort zugestoßen ist — lief ein Vorrat leer, warten die
   * folgenden Stücke auf mehr Ton, bevor sie losspielen (tts-pcm-vorrat.ts). Eine Schlange ist eine Antwort.
   */
  private readonly pcmVorrat = new PcmVorrat();
  /**
   * Ticket 2547, Nachprüfung Build 77: Der Halt gehört JEDER Warteschlange
   * für sich (vorlesen-nativ.ts) — und seine Journal-Zeilen gehen über
   * dieselbe `meldeZeile` wie `[tts-gap]`.
   */
  private readonly haltProtokoll = (zeile: string): void => this.meldeZeile(zeile);
  private playQueue: Array<{
    urlPromise: Promise<string>;
    text: string;
    enqueuedAt: number;
    readyAt?: number;
    audio?: HTMLAudioElement;
    /** Ticket 2919: das Stück als PCM-Strom — schon beim Einreihen heruntergeladen (statt `audio`). */
    pcm?: PcmStueck;
    /** Ticket 2919: Adresse des PCM-Stroms (daraus die Datei-Adresse für die Vorladung). */
    pcmUrl?: string;
    /** Grund, falls die Vertonung endgueltig scheiterte (siehe enqueue). */
    fehler?: string;
    /** 17.10.2026 (stimme-nativ): die Datei, schon beim Einreihen für die App geholt (statt `audio`). */
    dateiVorab?: DateiVorab;
    /** Ticket 2558/2692: Mindeststille vor diesem Stück (Absatz, Satzende), sonst 0. */
    pauseDavorMs: number;
  }> = [];
  private ttsSeg = 0;
  /**
   * Ticket 2558: `flushPending` hat den Puffer an einer Werkzeug-Naht
   * geleert, und seitdem kam nur Zwischenraum. `flushPending` öffnet die
   * Naht, `push` schließt sie beim ersten echten Text.
   */
  private werkzeugNaht = false;
  /**
   * Ticket 2558: Beginnt mit dem nächsten Stück der Absatz nach einem
   * Werkzeug? Ein Schreiber (`push`), ein Leser (`enqueue`, das ihn wieder
   * zurücksetzt).
   */
  private absatzVorNaechstem = false;
  /**
   * Ticket 2692: Text des zuletzt eingereihten Stücks — endet er mit einem
   * Satz, kommt vor das nächste die Satzpause. Ein Schreiber: `enqueue`.
   */
  private vorigesStueck = "";
  /** Ticket 2558: Wann der zuletzt gespielte Ton zu Ende war (0 = noch keiner). */
  private letztesTonEnde = 0;
  /**
   * Ticket 2547 (23.09.2026): Textlänge des Stücks, das gerade spielt — für
   * den Wachhund, wenn die Dauer beim Start unbekannt ist. Ein Schreiber:
   * der Abspiel-Lauf, wenn er das Stück entnimmt.
   */
  private stueckTextLaenge = 0;
  /** 19.10.2026 (vorlese-fortsetzen.ts): der Text des Stücks, das gerade spielt — für die Fortsetz-Spur. */
  private stueckText = "";
  /** Merkt je ganz gespieltes Stück die Stelle im Text (nur wenn der Aufrufer `fortsetzen` übergab). */
  private readonly fortsetzSpur: FortsetzSpur | null;
  /**
   * 05.10.2026: Wie weit das Stück, das gerade spielt, wirklich gekommen ist (vorlese-wiederanlauf.ts,
   * `gesprocheneZeichen`). Der Abspiel-Lauf legt es je Stück neu an; geschrieben wird es nur über `merkeStand`
   * und `stueckGanz` von dem Spieler, der das Stück gerade trägt.
   */
  private stueckStand: StueckStand = { bisS: 0, dauerS: NaN, ganz: false };
  /** Spielt gerade ein Stück, das noch nicht in der Bilanz steht? `zaehleStueck` zählt es genau einmal. */
  private stueckOffen = false;
  private playLoopRunning = false;
  private abort = new AbortController();
  private finished = false;
  private idleResolvers: Array<() => void> = [];
  private fetchTts: FetchTts;
  /** Ticket 2448: Durchreich-Weg (Beta je Nutzer) — dann faengt playOne dessen Schwaechen. */
  private direktBeta: (() => boolean) | undefined;
  private onAudioStart?: () => void;
  private onAudioEnd?: () => void;
  /** 15.10.2026: Spricht diese Schlange für den Schmied? Dann entscheidet ihr Ende das Mikro nicht (frontend/lib/schmied-warten.ts). */
  /** Wer spricht, wenn nicht Vespera (15.10.2026) — die Pille in app.ts liest es. */
  /** 17.10.2026: „probe“ = die Hörprobe der Sofort-Sätze (lib/vespera-satz.ts) — ihr Ende entscheidet über kein Mikro. */
  readonly sprecher: "schmied" | "probe" | undefined;
  /** 20.10.2026: Anzeigename des Sprechers (nur für die Pille) — siehe Option `sprecherName`. */
  readonly sprecherName: string | undefined;
  /** 17.10.2026: die Kennung des Beitrags, den diese Schlange spricht (seiten-abgleich.ts „Geparkt“) — fehlt sie: wie bisher. */
  readonly kennung: string | undefined;
  /**
   * 17.10.2026: `true`, sobald diese Schlange GEPARKT ist (nicht vorn, sie wartet, ob diese Seite vorn wird), `false` mit dem
   * Ergebnis, sobald das Parken endet. Wer Pille und Mikro führt (app.ts), hängt sich an. Ohne Hörer ändert sich nichts.
   */
  beiGeparkt: ((geparkt: boolean, ergebnis?: ParkErgebnis) => void) | null = null;
  // 2026-07-09 (Martin): merkt, ob in diesem Zug wirklich TTS lief — nur dann
  // beim Leerlauf das tts-audio-end-Signal feuern (Mikro-Vorwaermung).
  private playedThisTurn = false;
  /*
   * Ticket 2403: Was dieser Zug gesprochen hat — um am Ende sagen zu
   * koennen, ob die Antwort mit einer Frage schliesst.
   *
   * ⚠️ HIER und nicht beim Aufrufer: den Text an den drei Stellen
   *    mitzufuehren, die eine Antwort erzeugen (Chat, Sprache, Anruf),
   *    waere derselbe zweite Besitzer wie beim Zeitstempel. Die Schlange
   *    WEISS, was sie vorgelesen hat.
   */
  private gesprochenDieserZug = "";
  // 2026-07-09 (Audit Wurzel #2): bei Abbruch (Barge-in per PTT) KEIN
  // tts-audio-end feuern — das startete eine Vorwaermung parallel zum
  // PTT-getUserMedia (Race). Der PTT-Start holt sich das Mikro selbst.
  private wasCanceled = false;
  // 2026-07-19 (Martin, "Pause druecken koennen, nicht von vorne anfangen"):
  // Nutzer-Pause. Haelt das laufende Audio an UND verhindert, dass der
  // Play-Loop das naechste Segment startet. resume() spielt exakt dort
  // weiter, wo pausiert wurde.
  private paused = false;
  /*
   * Ticket 2232 (14.09.2026) — die zweite Haelfte der Bilanz.
   *
   * Die Instrumentierung vom 13.09. hat eine Frage vorab festgelegt: Stimmt
   * am Zug-Ende `gesprochenBis` mit der vollen Antwortlaenge ueberein, liegt
   * ein Abbruch NICHT am Nachschub, sondern an der Wiedergabe.
   *
   * GEMESSEN am 14.09. im Journal: sechs Zug-Enden, JEDES mit bisher ===
   * botText (2471/2471, 2864/2864, 2083/2083, 4514/4514, 50/50, 427/427) —
   * zwei davon mit Werkzeug-Aufrufen davor, also genau Martins Fehlerfall.
   * Kein einziger Ueberschuss-Alarm. Der Nachschub ist damit entlastet.
   *
   * Also zaehlt jetzt die Wiedergabe selbst mit: Wie viele Zeichen wurden
   * eingereiht, wie viele wirklich zu Ende gesprochen, wie viele auf dem Weg
   * verloren? Die Luecke zwischen eingereicht und gesprochen ist der Ort, an
   * dem Martins Antwort abbricht — falls sie hier abbricht.
   */
  /*
   * ⚠️ 14.09.2026 (Ticket 2232) — WER ABGEBROCHEN HAT, STAND NIRGENDS.
   *
   * Gemessen ueber 30 Stunden: 21 Wiedergaben, 18 davon vollstaendig
   * (gesprochen == eingereiht, ausnahmslos). Die drei unvollstaendigen
   * (15,7 %, 18,0 %, 81,4 %) tragen ALLE dieselbe Marke — `via=?` in der
   * Zeile davor. Und `?` bleibt genau EIN Weg uebrig: der Abbruch. Jeder
   * andere Ausgang traegt seinen Namen (ended, error, poll, visible,
   * WACHHUND).
   *
   * Zwei der drei waren Martin selbst: PTT-Druck mitten in der Antwort,
   * genau wie gewuenscht (04.07.: „Brauche die Moeglichkeit, dass er
   * stoppt"). Beim dritten steht im Protokoll kein Ausloeser.
   *
   * Solange der Abbruch namenlos ist, sieht ein gewolltes Dazwischenreden
   * genauso aus wie ein Fehler — und der Fehler versteckt sich dahinter.
   */
  private abbruchGrund: string | null = null;
  private zeichenEingereiht = 0;
  private zeichenGesprochen = 0;
  private zeichenVerloren = 0;
  /** 15.10.2026: Zahlen dieses Zuges für EINE Journalzeile am Ende (vorlese-zugmessung.ts). */
  private readonly zugMessung = new ZugMessung();

  /*
   * ═══ ERST GANZ VERTONEN, DANN SPRECHEN (16.10.2026, nur der Schmied) ═══
   *
   * Martin, 17:18: „die Stimme setzt immer wieder aus und fängt neu an“ — er will den Beitrag des Schmieds in einem Stück.
   * Journal 17:13–17:19: Stücke warteten bis 4,5 s auf ihre Vertonung (`wait_max=4499ms`), denn vertont wurde nur ein
   * Stück voraus (`PCM_VORLADEN`), Soniox rechnet etwa in Echtzeit. Ein Beitrag des Schmieds ist kurz (≤ 2000 Zeichen)
   * und kommt fertig — also: alle Stücke im Hintergrund vertonen (PCM ganz herunterladen, höchstens `GANZ_GLEICHZEITIG`
   * Abrufe zugleich), erst dann das erste spielen. Dauert es länger als `ganzVorabMs` ab dem ersten Stück, geht es wie
   * bisher stückweise weiter — nie bleibt es still, weil ein Stück hängt.
   * Dazu gilt das Stromfehler-Gedächtnis (tts-pcm-gedaechtnis.ts) für diese Schlange nicht (app.ts `fetchTtsChunk`, `spielePcm`):
   * belegt 17:18:37–17:19:14 — nach dem Gedächtnis ging der Schmied als `blob:`-Datei über `<audio>`, iOS hielt das Element bei
   * jeder Unterbrechung der Tonsitzung an (`[tts-weiter]` ×6), und der Wiederanlauf einer `blob:`-Adresse beginnt ihr Stück von
   * vorn (`[tts-neu] grund=mitte stand=4.84s`, danach `stand=1.11s`, vorlese-wiederanlauf.ts). Ganz geladenes PCM kann kein
   * Abriss mehr lückenhaft machen.
   * ⚠️ Vesperas Schlangen setzen `ganzVorab` nie: dort bleibt jede Zeile hier unwirksam (Echtzeit-Strom).
   */
  /** Obergrenze in ms, oder null (= Vespera, unverändert). Ein Schreiber: der Konstruktor. */
  private readonly ganzVorabMs: number | null;
  /** Wann das erste Stück eingereiht wurde (Beginn der Vertonung). */
  private ganzSeit: number | null = null;
  /** Ist das Tor vor dem ersten Stück schon durch? Danach spielt die Schlange wie jede andere. */
  private ganzErledigt = false;
  /** So viele PCM-Abrufe laufen im Ganz-Modus höchstens zugleich — so viele Plätze hat Soniox (server/soniox-schleuse.ts). */
  private static readonly GANZ_GLEICHZEITIG = 2;
  /** Takt, in dem das Tor nachsieht, ob alles da ist (ms). */
  private static readonly GANZ_TAKT_MS = 100;

  /**
   * Lädt diese Schlange gerade VORAB (Ganz-Modus, Tor vor dem ersten Stück noch zu)? Dann ohne Vorrang in der
   * Soniox-Schleuse (17.10.2026). Ist das Tor durch — alles da ODER Obergrenze gerissen, er spricht stückweise —,
   * laden die restlichen Stücke mit Vorrang: sonst überholte Vespera ihn mitten im Sprechen (Prüfauflage 17.10.).
   */
  vorabLaedt(nr: number = this.abrufNr): boolean {
    // 19.10.2026 (Martin: Entwickler-Antworten warteten bis zu 2 min): das ERSTE Stück lädt immer mit Vorrang — es bestimmt, wann der Ton beginnt.
    return nr !== 1 && this.ganzVorabMs !== null && !this.ganzErledigt && !this.abort.signal.aborted;
  }
  /** Wie viele Stücke eingereiht wurden (Nummer ab 1) und welches gerade abgerufen wird (synchron vor `fetchTts` gesetzt). */
  private stueckNr = 0;
  private abrufNr = 0;

  /** Die Messzeile dieses Zuges (`[tts-zug] …`, nur Zahlen) — app.ts schreibt sie neben die Bilanz. */
  zugMessZeile(): string {
    return this.zugMessung.zeile(this.abbruchGrund);
  }

  /**
   * Was aus dem Text dieses Zuges wirklich geworden ist. Reiner Lesezugriff.
   */
  sprechBilanz(): {
    eingereiht: number;
    gesprochen: number;
    verloren: number;
    offen: number;
    abbruch: string | null;
  } {
    return {
      eingereiht: this.zeichenEingereiht,
      gesprochen: this.zeichenGesprochen,
      verloren: this.zeichenVerloren,
      offen: this.playQueue.reduce((n, i) => n + i.text.length, 0),
      abbruch: this.abbruchGrund,
    };
  }

  /** Kennung dieser Warteschlange für die Miete des Seitentons (seiten-ton.ts). */
  private readonly seitenTonId = naechsteSeitenTonId();

  /**
   * 17.10.2026 (lange-spur.ts): `true`, sobald diese Schlange auf eine LANGE SPUR wartet, `false`, sobald das Warten endet
   * (frei, Frist, Abbruch, verworfen) — genau einmal je Warten. Wer die Pille zeigt (app.ts), hängt sich hier an. Nach der
   * Parallel-Arbeit (`beiWartenAufStimme`) lässt sich beides zu einem Hörer zusammenführen.
   */
  beiWartenAufLangeSpur: ((wartet: boolean) => void) | null = null;
  /** 17.10.2026: Die Frist ist abgelaufen — die Antwort wird NICHT mehr gesprochen; app.ts markiert die Blase „nicht vorgelesen“. */
  beiLangeSpurFrist: (() => void) | null = null;

  /** Die Hörer der Anzeige dürfen das Vorlesen nie aufhalten. */
  private meldeLangeSpur(f: ((w: boolean) => void) | (() => void) | null, wartet?: boolean): void {
    try {
      (f as ((w?: boolean) => void) | null)?.(wartet);
    } catch {
      /* Anzeige ist Bonus */
    }
  }

  /**
   * 17.10.2026: von Martin selbst ausgelöst (Abspielknopf) — überspringt das Warten auf eine lange Spur. 28.10.2026: lesbar für
   * seiten-abgleich.ts („Besitz“) — Martins Knopf hat Vorrang vor der Reihe eines anderen Geräts.
   */
  readonly ausdruecklich: boolean;

  constructor(opts: {
    fetchTts: FetchTts;
    direktBeta?: () => boolean;
    onAudioStart?: () => void;
    onAudioEnd?: () => void;
    /** 15.10.2026: wer spricht, wenn nicht Vespera („schmied“, shared/schmied.ts) — steht im Ende-Ereignis. */
    sprecher?: "schmied" | "probe";
    /** 20.10.2026: der Anzeigename dessen, der spricht (Zimmermann, Hüter …) — die Pille sagt „<Name> spricht“; fehlt er: der Schmied. */
    sprecherName?: string;
    /**
     * 16.10.2026 (Martin: „in einem Stück“): erst ALLES vertonen, dann sprechen — höchstens `maxMs` ab dem ersten Stück,
     * danach wie immer stückweise. Nur die Schlange des Schmieds setzt das (lib/schmied-stimme.ts); fehlt es, ändert sich nichts.
     */
    ganzVorab?: { maxMs: number };
    /** 17.10.2026: Martin hat es selbst ausgelöst (Abspielknopf) — dann wartet die Schlange NICHT auf eine lange Spur (lange-spur.ts). */
    ausdruecklich?: boolean;
    /** 17.10.2026: die Kennung des Beitrags (nur der Schmied) — nicht vorn: zurückhalten statt verwerfen (seiten-abgleich.ts „Geparkt“). */
    kennung?: string;
    /**
     * 19.10.2026: die Stelle merken, damit ein erneutes Vorlesen DERSELBEN Nachricht dort fortsetzt (vorlese-fortsetzen.ts).
     * `gesamtText` = der ganze Sprechtext, `basis` = so viele Zeichen davon wurden dieser Schlange NICHT vorgelegt (Fortsetzung),
     * `beiStand` meldet den Versatz nach jedem fertigen Stück (0 = zu Ende gehört).
     */
    fortsetzen?: { gesamtText: string; basis: number; beiStand?: (versatz: number) => void };
  }) {
    this.fortsetzSpur = opts.fortsetzen
      ? new FortsetzSpur(opts.fortsetzen.basis, opts.fortsetzen.gesamtText, opts.fortsetzen.beiStand)
      : null;
    this.sprecher = opts.sprecher;
    this.sprecherName = opts.sprecherName;
    this.kennung = opts.kennung ? opts.kennung : undefined;
    this.ausdruecklich = opts.ausdruecklich === true;
    this.ganzVorabMs = opts.ganzVorab && Number.isFinite(opts.ganzVorab.maxMs) && opts.ganzVorab.maxMs > 0 ? opts.ganzVorab.maxMs : null;
    this.fetchTts = opts.fetchTts;
    this.direktBeta = opts.direktBeta;
    this.onAudioStart = opts.onAudioStart;
    this.onAudioEnd = opts.onAudioEnd;
  }

  push(chunk: string): void {
    // Ein neuer Vorlese-Vorgang darf wieder melden, wenn er scheitert —
    // ohne das bliebe es nach dem ersten Ausfall fuer immer still.
    if (this.playQueue.length === 0 && !this.spieltEtwas()) neuerVorlesevorgang();
    if (this.finished) return;
    /*
     * Ticket 2558 (24.09.2026): Der erste echte Text nach einer
     * Werkzeug-Naht. Beginnt er einen Absatz, und klang davor schon etwas,
     * kommt vor sein erstes Stück die Absatzpause (ABSATZ_PAUSE_MS).
     * Geprüft VOR dem Anhängen, danach stünde er schon im Rohtext.
     *
     * ⚠️ OB dort ein Absatz beginnt, sagt der TEXT, den der Server gesetzt
     *    hat, nicht das Werkzeug-Ereignis. So pausiert die Stimme genau
     *    dort, wo Blase und Verlauf den Absatz zeigen, und nirgends sonst.
     *
     * ⚠️ `gesprochenDieserZug` statt `buffer` für die Naht: `flushPending`
     *    leert den Puffer samt Zeilenumbruch am Ende. Der Rohtext weiß noch,
     *    wie viel Zwischenraum schon da war.
     */
    if (this.werkzeugNaht && /\S/.test(chunk)) {
      this.werkzeugNaht = false;
      if (
        this.zeichenEingereiht > 0 &&
        beginntAbsatz(this.gesprochenDieserZug, chunk)
      ) {
        this.absatzVorNaechstem = true;
      }
      /*
       * Ticket 2692 (29.09.2026): Wartet in der Schlange nichts mehr, ist
       * der Vorsprung verbraucht. Die Rampe (`letzteStueckLaenge`) rechnet
       * aber noch mit dem Stück vor dem Werkzeug, als würde es gleich erst
       * spielen. Der Text danach beginnt darum wie eine neue Antwort:
       * erster Satz als Kopf (`ersterBrocken`), Rampe ab ihm.
       *
       * Gemessen 27.09. 20:10 bis 29.09. 08:20 (Konto 1, Datei-Weg): 116
       * von 198 Stücken nach einem Werkzeug warteten über 300 ms auf ihre
       * Vertonung, im Mittel 4,5 s, Länge im Median 87. Nachgerechnet an
       * 176 dieser Nähte mit der echten Zerlegung (trifft das Journal:
       * 171 von 176 Stücken gleich lang): Stille über der gewollten
       * Sekunde je Naht im Mittel 1,96 → 1,27 s.
       *
       * ⚠️ Nur bei leerer Schlange. Immer neu anzusetzen brächte 1,0 s,
       *    schnitte aber 72 Sätze mehr mitten durch, 23 davon ohne jeden
       *    Gewinn, weil das Wartende die Vertonung deckte. So sind es 32,
       *    3 davon ohne Gewinn (Ticket 2559: Pause erst am Punkt).
       */
      if (this.playQueue.length === 0) {
        this.firstDone = false;
        this.letzteStueckLaenge = 0;
      }
    }
    this.buffer += chunk;
    this.gesprochenDieserZug += chunk;
    this.fortsetzSpur?.push(chunk);
    this.drainSentences();
  }

  flush(): void {
    if (this.finished) return;
    const rest = this.buffer.trim();
    this.buffer = "";
    this.gibRestAb(rest);
    this.finished = true;
    this.firstDone = false;
    if (this.playQueue.length === 0 && !this.spieltEtwas()) this.resolveIdle();
  }

  /**
   * 2026-07-13 (Martin): Den bereits gestreamten, aber noch gepufferten Text
   * (letzter Satz OHNE nachfolgendes Leerzeichen) SOFORT aussprechen — z.B.
   * bevor ein Tool-Aufruf laeuft. Schliesst die Queue NICHT (finished bleibt
   * false), damit der Text NACH dem Tool weiter gepusht werden kann. Ohne
   * das wurde der Satz vor einem Tool verschluckt (nur angezeigt, nicht
   * gesprochen).
   */
  flushPending(): void {
    if (this.finished) return;
    const rest = this.buffer.trim();
    this.buffer = "";
    this.gibRestAb(rest);
    // Ticket 2558 (24.09.2026): Hier läuft ein Werkzeug — ob danach ein
    // Absatz mit Pause kommt, entscheidet der nächste Text (siehe push).
    this.werkzeugNaht = true;
  }

  /**
   * Der Text bis hier war ein VORSATZ: ein Satz, den Vespera selbst vor die
   * Antwort stellt („Einen Moment.“ der Warte-Brücke, „Moment, ich schaue
   * nach.“ der Vorantwort; Server-Ereignis `vorsatz`). Was danach kommt,
   * beginnt wie eine neue Antwort — erstes Stück mit dem schnellen Schnitt
   * (`ersterBrocken`, `firstSpeakableCut`), die Rampe ab ihm.
   * (06.10.2026, Ticket 2738, Prüfbefund zur Warte-Brücke)
   *
   * Ohne das belegte der Vorsatz den Platz des ersten Stücks (`firstDone`,
   * Rampe ab 13 Zeichen): die Antwort ging erst nach ihrem ganzen ersten Satz
   * hinaus, ein langer einzelner Satz erst am Ende des Stroms. Gemessen im
   * echten WebKit: tests/browser/vorsatz-neuanfang-webkit.mts.
   *
   * ⚠️ Grenzen dieser Messung (Prüfbefund 06.10.2026): Sie läuft mit `<audio>` und einer
   *    Attrappe für die Vertonung — der echte PCM-Spieler (`PcmStueck`, Web Audio, Vorladen,
   *    Rückfall auf den Datei-Weg) ist nicht dabei, und es ist WebKit auf dem Rechner, nicht
   *    das iPhone. Auf dem Datei-Weg beginnt die Antwort so früh wie ohne Vorsatz. Auf dem
   *    schnellen Weg (Martins Konto) aber nur, wenn der Satz schon zu Ende gesprochen ist: Beginnt
   *    das Modell, während er noch vertont wird („knapp“), klingt die Antwort erst gleich nach
   *    dem Satz plus Satzpause — rund 0,6 bis 0,8 s später als ohne (736 → 1 511 ms, 928 → 1 533 ms).
   *    Hörtest am Telefon vor dem Live-Schalten.
   *
   * ⚠️ Anders als an der Werkzeug-Naht (Ticket 2692, `push`) AUCH, wenn der
   *    Vorsatz noch in der Schlange wartet: ein Satz von 13 bis 30 Zeichen
   *    deckt die Vertonung eines Rampen-Stücks nie (dieselbe Messung).
   * ⚠️ Keine Absatzpause, kein Werkzeug-Ton — das ist keine Werkzeug-Naht.
   *    Nach dem Vorsatz gilt die Satzpause wie nach jedem Satz.
   */
  neuerAnfang(): void {
    if (this.finished) return;
    const rest = this.buffer.trim();
    this.buffer = "";
    this.gibRestAb(rest);
    this.firstDone = false;
    this.letzteStueckLaenge = 0;
  }

  /** @param grund Wer abbricht — steht danach in `[tts-gap] via=abbruch:…`. */
  cancel(grund?: string): void {
    // 19.10.2026: ein vorgezogenes Mikro dieser Antwort wieder freigeben (mikro-vorstart.ts).
    vorstartAbbruch("abbruch", this);
    this.abbruchGrund = grund ?? "unbekannt";
    this.paused = false; // ein haengendes Pause-Gate wuerde den Loop blockieren
    this.finished = true;
    this.buffer = "";
    // Bilanz (05.10.2026): der gehörte Teil des laufenden PCM-Stücks — festgehalten, BEVOR der Abbruch es stoppt.
    if (this.pcmSpielt) {
      this.merkeStand(this.pcmSpielt.gespieltS(), this.pcmSpielt.fertigGeladen() ? this.pcmSpielt.vorratS() : NaN);
    }
    // 17.10.2026: ebenso ein Stück, das die App spielt — der Abbruch ruft dort `stimmeStopp` (stimme-nativ.ts).
    if (this.stimmeNativ) this.merkeStand(this.stimmeNativ.gespieltMs / 1000, this.stimmeNativ.dauerS);
    this.abort.abort();
    /*
     * …und gezählt wird JETZT, im selben Zug (auf dem Datei-Weg hat `onAbort` die Stelle eben festgehalten): app.ts
     * schreibt die Bilanz, sobald `idle()` auflöst — das ist vor der Fortsetzung des Abspiel-Laufs. Gemessen im
     * echten Bundle: `ct=2.17s` in der Zeile des Stücks, aber `gesprochen=0` in der Bilanz.
     */
    this.zaehleStueck();
    /*
     * Ticket 2448 (23.09.2026): Ein vorgeladenes Strom-Element lädt sonst
     * weiter, auch pausiert. Ohne Quelle schließt der Browser die
     * Verbindung; wartet das Stück am Server noch auf einen Soniox-Platz,
     * verlässt es seit Ticket 2285 die Reihe. Ein schon laufender Strom
     * läuft zu Ende und landet im Zwischenspeicher (server/soniox-tts.ts,
     * sonioxStream). `blob:`-Adressen sind fertige Dateien und bleiben
     * unberührt.
     */
    const entlade = (a?: HTMLAudioElement | null): void => {
      if (!a || !istStromAdresse(a.src ?? "")) return;
      try {
        a.removeAttribute("src");
        a.load();
      } catch {
        /* ignore */
      }
    };
    if (this.playing) {
      try {
        this.playing.pause();
      } catch {
        /* ignore */
      }
      entlade(this.playing);
      this.playing = null;
    }
    // Ticket 2919 / 2882: auch die PCM-Quellen stoppen — „eine Tonspur" gilt für Web Audio genauso.
    this.pcmSpielt?.stoppen();
    for (const it of this.playQueue) {
      entlade(it.audio);
      it.pcm?.verwerfen();
    }
    this.playQueue = [];
    this.wasCanceled = true;
    this.resolveIdle();
  }

  /** 2026-07-19 (Martin): Wiedergabe anhalten — Position bleibt stehen. */
  pause(): void {
    if (this.abort.signal.aborted) return;
    this.paused = true;
    if (this.playing) {
      try {
        this.playing.pause();
      } catch {
        /* egal */
      }
    }
    // Ticket 2547: ein nativ spielendes Stück hält ebenso an.
    if (this.nativSpielt) pausiereNativ(true);
    // Ticket 2919: ein PCM-Stück ebenso (der Kontext steht, die Position bleibt).
    this.pcmSpielt?.pausieren(true);
    // 17.10.2026: ein Stück in der App ebenso.
    if (this.stimmeNativ) stimmeNativPause(true);
  }

  /**
   * 19.10.2026 (Mikro-Vorstart): was die Uhr in mikro-vorstart.ts über das Ende wissen muss. `restMs` nur, wenn das LETZTE
   * Stück spielt (Antwort zu Ende gereicht, nichts mehr in der Reihe, nicht pausiert, nicht abgebrochen) UND seine Länge
   * bekannt ist (Datei: `duration`; PCM: erst wenn ganz geladen). Sonst `null` — dann gibt es keinen Vorstart.
   */
  vorstartLage(): VorstartLage | null {
    if (!this.finished || this.playQueue.length > 0 || this.paused || this.abort.signal.aborted) return null;
    const text = this.gesprochenDieserZug;
    return {
      restMs: this.restMsLetztesStueck(),
      sprecher: this.sprecher,
      endetMitFrage: endetMitFrage(text),
      schmiedAngesprochen: this.sprecher ? false : sprichtSchmiedAn(text),
      zusatzAngesprochen: this.sprecher ? null : zusatzAmEnde(text),
    };
  }

  private restMsLetztesStueck(): number | null {
    try {
      if (this.pcmSpielt) {
        if (!this.pcmSpielt.fertigGeladen()) return null;
        return Math.max(0, (this.pcmSpielt.vorratS() - this.pcmSpielt.gespieltS()) * 1000);
      }
      const a = this.playing;
      if (a && !a.paused && !a.ended && Number.isFinite(a.duration) && a.duration > 0) {
        return Math.max(0, (a.duration - a.currentTime) * 1000);
      }
    } catch {
      /* unbekannt ist unbekannt */
    }
    return null;
  }

  /** Der Text, den diese Schlange in diesem Zug bekommen hat (für „spricht schon genau das?", gleiche-wiedergabe.ts). */
  sprichtText(): string {
    return this.gesprochenDieserZug;
  }

  /** Genau dort weiterspielen, wo pausiert wurde (Segment-Mitte inklusive). */
  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    if (this.playing) {
      void this.playing.play().catch(() => {
        /* dann greifen Poll/Wachhund regulaer */
      });
    }
    if (this.nativSpielt) pausiereNativ(false);
    this.pcmSpielt?.pausieren(false);
    if (this.stimmeNativ) stimmeNativPause(false);
    // 02.10.2026: nach einer langen Pause gilt die Miete des Seitentons wieder (seiten-ton.ts) — auch für ein PCM-Stück (Ticket 2919).
    if (this.playing || this.nativSpielt || this.pcmSpielt) seitenTonMelder.ttsWeiter(this.seitenTonId);
    // Zwischen zwei Segmenten pausiert: das Gate im Play-Loop loest sich
    // von selbst, sobald paused false ist.
  }

  isPaused(): boolean {
    return this.paused;
  }

  /** Klingt gerade etwas — im Web-Element oder nativ (Ticket 2547)? */
  private spieltEtwas(): boolean {
    return (
      this.playing !== null || this.nativSpielt || this.pcmSpielt !== null || this.stimmeNativ !== null || this.wartetAufStimme
    );
  }

  /** 16.10.2026: das Stück wartet, bis der andere Sprecher fertig ist (seiten-abgleich.ts `stimmeDarfBeginnen`). */
  private wartetAufStimme = false;
  /**
   * 17.10.2026 (Gutachten: die Pille sagte „Stimme wird vorbereitet“, während nur gewartet wurde): `true`, sobald diese
   * Schlange auf den ANDEREN Sprecher wartet, `false`, sobald das Warten endet (frei, Frist, Abbruch) — genau einmal je
   * Warten. Wer die Pille zeigt (app.ts), setzt es; ohne Hörer ändert sich nichts.
   */
  beiWartenAufStimme: ((wartet: boolean) => void) | null = null;
  /** Diese Schlange hat zu sprechen begonnen (`stimmeBeginnt`) — dann hält `pcmZurueck` nichts mehr zurück. */
  private stimmeBegonnen = false;
  /**
   * 17.10.2026 (Journal: 5–10 s Pause vor einzelnen Sätzen, zwei Seiten offen): steht diese Seite hinter einem vorderen
   * Gerät und hat noch nicht gesprochen, startet sie KEINEN Soniox-Strom und lädt keine Datei vor — sie parkt oder verwirft.
   * Sonst hielte sie die Render-Sperre des Servers (`renderImBau`), die vordere Seite bekäme 409 `im-bau` und renderte
   * denselben Text ein zweites Mal über die Datei. Einzelne Seite / kein vorderes Gerät gemeldet: `false`, alles wie bisher.
   */
  private pcmZurueck(): boolean {
    return !this.stimmeBegonnen && seiteNichtVorn();
  }
  /** Die Zeile `[stimme-geraet]` steht höchstens einmal je Schlange im Journal. */
  private vornGemeldet = false;

  /**
   * Darf diese Schlange beginnen (seiten-abgleich.ts)? Wartet ggf. auf den anderen Sprecher (zählt dabei als „spielt“) und
   * schreibt die Journalzeilen `[stimme-geraet]` (einmal je Schlange) und `[stimme-schlange]`.
   */
  private async stimmeEntscheiden(): Promise<StimmeEntscheid> {
    let entscheid = stimmeEntscheidSofort(this, this.abort.signal);
    if (!entscheid) {
      this.wartetAufStimme = true;
      this.meldeWartenAufStimme(true);
      try {
        entscheid = await stimmeDarfBeginnen(this, this.abort.signal);
      } finally {
        this.wartetAufStimme = false;
        this.meldeWartenAufStimme(false);
      }
    }
    if (entscheid.vornZeile && !this.vornGemeldet) {
      this.vornGemeldet = true;
      this.meldeZeile(entscheid.vornZeile);
    }
    if (entscheid.freigabe !== "frei") {
      this.meldeZeile(
        `[stimme-schlange] sprecher=${this.sprecher ?? "vespera"} ergebnis=${entscheid.freigabe} gewartet=${entscheid.gewartetMs}ms`,
      );
    }
    return entscheid;
  }

  private meldeGeparkt(geparkt: boolean, ergebnis?: ParkErgebnis): void {
    try {
      this.beiGeparkt?.(geparkt, ergebnis);
    } catch {
      /* Anzeige ist Bonus */
    }
  }

  /** Der Hörer der Anzeige darf das Vorlesen nie aufhalten. */
  private meldeWartenAufStimme(wartet: boolean): void {
    try {
      this.beiWartenAufStimme?.(wartet);
    } catch {
      /* Anzeige ist Bonus */
    }
  }

  /** Fertig + nichts spielt — Signatur einer haengengebliebenen Queue. */
  isSilentIdle(): boolean {
    return this.finished && !this.spieltEtwas();
  }

  /** 2026-07-19 (Martin, Pausen): Spielt gerade Ton oder wartet welcher in
   *  der Queue? Fuer die gesprochenen Werkzeug-Hinweise — die duerfen NUR in
   *  echte Stille hinein sprechen, nie in laufende Ausgabe. */
  isAudioActive(): boolean {
    return this.spieltEtwas() || this.playQueue.length > 0;
  }

  idle(): Promise<void> {
    if (this.finished && this.playQueue.length === 0 && !this.spieltEtwas()) {
      return Promise.resolve();
    }
    return new Promise((res) => {
      this.idleResolvers.push(res);
    });
  }

  /**
   * ⚠️ `letzteLaenge` wandert ueber die Aufrufe hinweg mit: Der Chat ruft
   *    diese Stelle bei jedem eintreffenden Textstueck erneut, und die Rampe
   *    darf dabei nicht jedes Mal von vorn beginnen — sonst waere das
   *    Stueck nach einer Pause wieder zu gross (genau Martins Befund vom
   *    10.09.).
   */
  private letzteStueckLaenge = 0;

  /** Ticket 2559: Längen der Stücke, die eingereiht sind und noch nicht spielen. */
  private wartendeLaengen(): number[] {
    return this.playQueue.map((i) => i.text.length);
  }

  /**
   * Was am Ende im Puffer liegt, ebenfalls ueber die Rampe abgeben.
   * (10.09.2026)
   *
   * ⚠️ HIER stand `this.enqueue(rest)` — der Rest ging als EIN Stueck raus,
   *    an `splitLongSentence` UND an der Rampe vorbei. Bei Martins Antwort vom
   *    15:05 waren das 209 Zeichen hinter einem 56er (Faktor 3,7): 11,4 s
   *    Vertonung gegen 3,6 s Deckung — die Pause, die er gemeldet hat.
   *
   *    Die Rampe in `takeSpeakable` allein reicht also nicht: Der LETZTE Satz
   *    einer Antwort hat kein Satzende mit Zwischenraum dahinter und laeuft
   *    darum immer ueber diesen Weg. Dieselbe Sorte Fehler wie die Regel, die
   *    nur im Player galt — eine Tuer repariert, die zweite offen gelassen.
   */
  private gibRestAb(rest: string): void {
    if (rest.length < MIN_CHUNK_LEN) return;
    for (const stueck of zerlegeMitRampe(
      rest,
      naechsteGrenze(this.letzteStueckLaenge),
      undefined,
      0,
      this.wartendeLaengen(),
    )) {
      if (stueck.length < MIN_CHUNK_LEN && this.playQueue.length) continue;
      this.letzteStueckLaenge = stueck.length;
      this.enqueue(stueck);
    }
  }

  private drainSentences(): void {
    const taken = takeSpeakable(
      this.buffer,
      this.firstDone,
      this.letzteStueckLaenge,
      this.wartendeLaengen(),
    );
    this.buffer = taken.rest;
    this.firstDone = taken.firstDone;
    this.letzteStueckLaenge = taken.letzteLaenge;
    for (const piece of taken.pieces) this.enqueue(piece);
  }

  private firstDone = false;

  private enqueue(text: string): void {
    /*
     * Ticket 2736 (29.09.2026, Nachbesserung): Ein Stück ohne Buchstabe und
     * ohne Ziffer hat nichts zu sagen — etwa eine Zeile „🎉✨🌈“ vor einem
     * Werkzeug. Der Server lässt Emojis und Auszeichnung auf dem Weg zur
     * Stimme aus (markdownToNarration), übrig bliebe ein leerer Text: der
     * Anbieter lehnt ihn ab, und das Stück stünde als verloren im Bericht.
     * /api/tts/prepare antwortet dafür schon `leer` — „Der Aufrufer
     * überspringt es und liest weiter.“ Hier geschieht genau das.
     */
    if (!/[\p{L}\p{N}]/u.test(text)) return;
    // Ticket 2421 (21.09.2026): Der Bluetooth-Umschlag HFP->A2DP dauert ~1 s.
    // Bisher wurde er erst beim ERSTEN TON angestossen (tts-audio-start gibt
    // das Mikro frei, playback-hifi schaltet um) — die erste Sekunde ging in
    // einen Weg, den es noch nicht gab; ein kurzer erster Satz war ganz weg.
    // Jetzt geschieht beides beim SYNTHESE-Start des Vorgangs: das Ereignis
    // laesst ptt.ts das Mikro freigeben, prewarmHiFi() stoesst den Umschlag
    // an. Ein Besitzer fuer den Zeitpunkt, zwei Hoerer.
    if (this.playQueue.length === 0 && !this.spieltEtwas()) {
      try {
        window.dispatchEvent(new CustomEvent("voice-v8:tts-synthese-start"));
      } catch {
        /* ohne window (Tests) */
      }
      if (!stimmeNativMoeglich()) prewarmHiFi();
      // 17.10.2026: spielt gleich die App (stimme-nativ.ts), fasst die Seite die Tonsitzung nicht an — kein Vorwärmen. Die
      // App wird schon jetzt gefragt: sagt sie nein, laden die Stücke wie bisher als `<audio>` vor (`aufWebUmstellen`).
      else {
        this.hifiAufgeschoben = true;
        void this.nativEntscheiden();
      }
    }
    this.firstDone = true;
    // Ticket 2232: Bilanz — hier geht Text in die Wiedergabe hinein.
    this.zeichenEingereiht += text.length;
    const enqueuedAt = Date.now();
    // 16.10.2026 (Ganz-Modus): ab hier läuft die Obergrenze — die Vertonung beginnt mit dem ersten Stück.
    if (this.ganzVorabMs !== null && this.ganzSeit === null) this.ganzSeit = enqueuedAt;
    // Ticket 2558/2692: Absatz- oder Satzpause vor diesem Stück.
    const pauseDavorMs = pauseVorStueck(this.vorigesStueck, this.absatzVorNaechstem);
    this.absatzVorNaechstem = false;
    this.vorigesStueck = text;
    const nr = ++this.stueckNr;
    const item: {
      urlPromise: Promise<string>;
      text: string;
      enqueuedAt: number;
      readyAt?: number;
      audio?: HTMLAudioElement;
      pcm?: PcmStueck;
      pcmUrl?: string;
      /** Gesetzt, wenn die Vertonung endgueltig scheiterte (siehe unten). */
      fehler?: string;
      dateiVorab?: DateiVorab;
      pauseDavorMs: number;
    } = { urlPromise: Promise.resolve(''), text, enqueuedAt, pauseDavorMs };
    // #534 (2026-07-24, Martin: "letzter Satz wird verschluckt"): EIN
    // Retry, bevor ein Segment aufgegeben wird — ein transienter Fetch-
    // Fehler (Netz/429/Timeout) liess sonst genau den letzten Satz still
    // aus der Wiedergabe fallen (Play-Loop: `if (!url) continue`).
    //
    /*
     * ⚠️ 08.09.2026 (Ticket 2140, Befund 2): Ein Stueck fiel am 07.09. trotz
     *    dieses Versuchs aus — „Stueck NICHT vertont (tts http 502)". Zwei
     *    Dinge stimmten daran nicht:
     *
     *    1. 400 ms sind zu kurz. Ein 502 heisst „der Anbieter ist gerade
     *       ueberlastet"; nach vier Zehntelsekunden ist er das immer noch.
     *       Der zweite Versuch war praktisch derselbe wie der erste.
     *    2. Ein 4xx wurde genauso wiederholt wie ein 502. Eine falsche
     *       Anfrage wird beim zweiten Mal nicht richtig — der Versuch kostet
     *       nur Zeit und verdeckt den echten Grund.
     *
     *    Die WURZEL des 502 lag woanders und ist seit pre.2740 behoben (die
     *    Warteschlange kannte keinen Vorrang, siehe server/soniox-schleuse.ts).
     *    Das hier ist das Netz darunter, nicht die Reparatur.
     */
    const VERSUCH_PAUSEN_MS = [400, 1800];
    const lohntWiederholung = (err: unknown): boolean => {
      const m = err instanceof Error ? err.message : String(err);
      const status = Number(/\b(\d{3})\b/.exec(m)?.[1] ?? NaN);
      // Kein Status = Netzfehler oder Abbruch der Gegenseite: wiederholen.
      if (!Number.isFinite(status)) return true;
      // 408/429 und alles ab 500 sind voruebergehend. 4xx sonst nicht.
      return status === 408 || status === 429 || status >= 500;
    };
    const fetchMitRetry = async (): Promise<string> => {
      let letzter: unknown;
      for (let i = 0; i <= VERSUCH_PAUSEN_MS.length; i++) {
        try {
          this.abrufNr = nr;
          return await this.fetchTts(text, this.abort.signal);
        } catch (err) {
          letzter = err;
          if (this.abort.signal.aborted) throw err;
          if (!lohntWiederholung(err)) throw err;
          const pause = VERSUCH_PAUSEN_MS[i];
          if (pause === undefined) break;
          await new Promise((r) => setTimeout(r, pause));
          if (this.abort.signal.aborted) throw err;
        }
      }
      throw letzter;
    };
    // Ticket 768: durch die Schleuse — hoechstens zwei Vertonungen zugleich.
    // Die Reihenfolge bleibt erhalten (wartende werden in Eingangsreihenfolge
    // geweckt), die Wiedergabe-Logik unten aendert sich nicht.
    // 17.10.2026: eine Vorabladung (Schmied, Ganz-Modus) tritt in der Schleuse hinter Vesperas Stücke zurück.
    item.urlPromise = mitSchleuse(fetchMitRetry, () => this.vorabLaedt(nr))
      .then((u) => {
        item.readyAt = Date.now();
        // Gapless: Audio-Element SOFORT vorladen (buffert im Hintergrund,
        // waehrend die vorigen Segmente noch spielen) -> beim Uebergang
        // kein Neu-Aufsetzen/canplay-Warten mehr.
        // Ticket 2448: nach einem Abbruch nichts mehr anstossen — ein
        // Strom-Element würde sonst für niemanden vertonen lassen.
        if (u && !this.abort.signal.aborted && istPcmAdresse(u)) {
          // Ticket 2919: PCM — den Abruf JETZT starten (der Vorrat der späteren Stücke ist das Gegenmittel
          // zum alten Stottern), aber kein `<audio>`-Element anlegen.
          item.pcm = new PcmStueck(u, this.abort.signal);
          item.pcmUrl = u;
          // 17.10.2026: spielt gleich die App, bekommt sie die rohen Bytes (stimme-nativ.ts).
          item.pcm.rohMerken = this.nativKandidat();
          // 17.10.2026: ohne Vorrang nur, solange der Ganz-Modus wirklich vorab lädt (`vorabLaedt`), gefragt beim Abruf.
          if (this.ganzVorabMs !== null) item.pcm.vorab = () => this.vorabLaedt(nr);
          // Ticket 2919: läuft der Kontext während des Stücks nicht mehr, die Dateien der wartenden vorladen.
          item.pcm.beiKontextWeg = () => this.hintergrundVorbereiten(true);
          // Scheitert das Stück vor dem ersten Paket (Absage, Fehler), die Datei SOFORT vorladen — nicht erst, wenn der Satz dran ist.
          item.pcm.beiVorFehler = () => this.ladeDateiVor(item, u);
          // 16.10.2026 (Ganz-Modus): ist ein Abruf fertig, beginnt der nächste — ohne auf den Abspiel-Lauf zu warten.
          if (this.ganzVorabMs !== null) item.pcm.beiLadeEnde = () => this.starteVorladen();
          // Nur die nächsten Stücke laden vor: PCM braucht 384 kbit/s je Strom, im Mobilnetz zählt jedes (Nachprüfung 2919).
          // 11.10.2026: Im Datei-Weg des Störungs-Gedächtnisses (nach einem Stromfehler) lädt `starteVorladen` keinen Strom — das Stück
          // lädt gleich seine DATEI vor (siehe unten und `spielePcm`).
          // 16.10.2026: Im Ganz-Modus nicht — dort wird der Strom VOR dem Abspielen ganz geladen, ein Abriss reißt keine Lücke in den Ton.
          this.starteVorladen();
          if (pcmGedaechtnis.dateiWeg() && this.ganzVorabMs === null) this.ladeDateiVor(item, u);
          // Kommt die Adresse bei verstecktem Bildschirm, die Datei gleich mit vorladen (Ticket 2919).
          if (typeof document !== "undefined" && document.visibilityState === "hidden") this.ladeDateiVor(item, u, true);
        } else if (u && !this.abort.signal.aborted && this.nativKandidat()) {
          // 17.10.2026: spielt gleich die App (stimme-nativ.ts), lädt die Seite die DATEI schon jetzt vor — kein `<audio>`. So
          // hängt die Lücke zwischen zwei Stücken nicht an der Ladezeit (wie das vorgeladene Element des Web-Wegs).
          // Nicht vorn (`pcmZurueck`): kein Abruf, der die Render-Sperre hielte — `spieleUeberApp` holt die Datei, wenn sie dran ist.
          if (!this.pcmZurueck()) item.dateiVorab = this.dateiVorladen(u);
        } else if (u && !this.abort.signal.aborted) {
          try {
            const a = new Audio();
            a.preload = 'auto';
            a.src = u;
            a.load();
            item.audio = a;
          } catch {
            /* Vorladen best-effort */
          }
        }
        return u;
      })
      .catch((err) => {
        /*
         * ⚠️ 07.09.2026 — hier ging Martins Block verloren.
         *
         * *„Habe gerade meinen Spiegel vorlesen lassen, da faellt ein ganzer
         * Block. Der Teil mit dem Grab, mit dem Blut. Der Text ist da, aber
         * er ist nicht auf Voice uebersetzt worden."*
         *
         * Auf dem Server war an dem Abend KEIN Stueck gescheitert (13
         * Soniox-Aufrufe, alle mit Ton). Verloren ging es hier: Nach zwei
         * Fehlversuchen gab diese Stelle `""` zurueck, und der Abspiel-Weg
         * machte daraus ein `continue`. Ein ganzer Absatz verschwand, ohne
         * dass irgendwo etwas stand ausser einer Konsolenzeile, die niemand
         * sieht.
         *
         * Jetzt wird nichts mehr verworfen: Der Fehler wird gemerkt, beim
         * Abspielen NOCH EINMAL versucht (das Netz kann sich in den
         * Sekunden erholt haben), und wenn auch das scheitert, erfaehrt es
         * der Server — und Martin.
         */
        item.fehler = err instanceof Error ? err.message : String(err);
        console.warn(
          "[tts-queue] fetch endgueltig gescheitert (Segment stumm):",
          text.slice(0, 40),
          err,
        );
        item.readyAt = Date.now();
        return "";
      });
    this.playQueue.push(item);
    // Ein Wurf im Abspiel-Lauf darf nie still verschwinden (17.10.2026: „.then is not a function“ → stumm, nichts im Journal).
    void this.runPlayLoop().catch((e: unknown) => this.meldeWurf("tts-queue", "abspiel-lauf wurf", e));
  }

  private async runPlayLoop(): Promise<void> {
    if (this.playLoopRunning) return;
    this.playLoopRunning = true;
    // 2026-07-19 (Martin, ENTSCHEIDUNG "Sofort sprechen"): Die BOT-STIMME
    // fordert KEIN Hi-Fi mehr an. Beweis aus zwei Tagen Messdaten: am
    // Lautsprecher kostet ein kalter PTT-Start 300-600ms, am Bluetooth-
    // Geraet 1,4-5,5s — weil jede A2DP-Antwort den Rueckweg ins
    // Telefonprofil (HFP) neu aushandeln muss. Bluetooth Classic kann
    // physikalisch nur EINES: Hi-Fi ODER Mikrofon. Martins Wahl fuer den
    // Dialog: sofort sprechen koennen schlaegt schoenen Klang. Kapitel-
    // Vorlesen und Musik (narration-engine/audio-player) behalten ihr
    // prewarmHiFi/hookHiFiPlayback — dort gibt es kein Mikrofon-Hin-und-Her.
    try {
      while (this.playQueue.length > 0) {
        if (this.abort.signal.aborted) return;
        // 16.10.2026 (Ganz-Modus, nur der Schmied): vor dem ERSTEN Stück warten, bis alles vertont ist (oder die Obergrenze).
        // VOR der Pause: die Schlange des Schmieds wartet angehalten auf Stille und vertont in dieser Zeit schon weiter.
        if (this.ganzVorabMs !== null && !this.ganzErledigt) {
          await this.warteAufGanz();
          if (this.abort.signal.aborted) return;
        }
        // Nutzer-Pause zwischen Segmenten: warten statt weiterspielen.
        while (this.paused && !this.abort.signal.aborted) {
          await new Promise((r) => setTimeout(r, 200));
        }
        if (this.abort.signal.aborted) return;
        // 2026-07-09 (Martin, Freihand-'?'-Geister): Satz erst NACH dem Fetch
        // entnehmen — vorher war die Queue waehrend des Fetches leer und
        // idle()/Guards meldeten 'fertig', obwohl die Ausgabe gleich startet
        // (bei kurzen Antworten oeffnete Freihand das Mikro mitten hinein).
        // Nach dem await ist der Weg bis this.playing synchron -> kein Fenster.
        const item = this.playQueue[0];
        if (!item) break;
        const waitStart = Date.now();
        const url = await item.urlPromise;
        if (this.abort.signal.aborted) return;
        /*
         * Ticket 2558 (24.09.2026): Absatzpause — nur der Teil, den die
         * Stille seit dem letzten Ton noch nicht gedeckt hat. Steht NACH der
         * Vertonung, damit Vertonen und Warten sich überlappen statt sich
         * zu addieren. Ticket 2692: ebenso die Satzpause.
         */
        const pauseRest = item.pauseDavorMs - (Date.now() - this.letztesTonEnde);
        const gewollteStille = pauseRest > 0 ? pauseRest : 0;
        if (gewollteStille > 0) {
          await new Promise((r) => setTimeout(r, gewollteStille));
          if (this.abort.signal.aborted) return;
        }
        // AUDIT-Fund 2 (20.07.): Pause im SYNTHESE-FENSTER gedrueckt (der
        // Loop wartete gerade auf urlPromise, hinter dem Kopf-Gate) ->
        // ohne dieses zweite Gate startete der naechste Satz trotz
        // "Pausiert"-Anzeige und spielte komplett durch.
        while (this.paused && !this.abort.signal.aborted) {
          await new Promise((r) => setTimeout(r, 200));
        }
        if (this.abort.signal.aborted) return;
        // Sonde: synth = Synthese-Dauer, wait = wie lange der Play-Loop
        // auf das Audio warten musste (0 = war vorgeladen => TTS haelt
        // Schritt; gross => Engpass). starved unterscheidet die Ursache.
        /*
         * ⚠️ 23.09.2026, Ticket 2448: Auf dem Strom-Weg steht die Adresse,
         *    lange bevor ein Ton kommt (/api/tts/prepare, Millisekunden).
         *    Dort meldet die Sonde erst beim ersten Ton — sonst wäre synth
         *    nur die Dauer von prepare, und server/antwort-zug.ts (Ton =
         *    `[tts-t]`) zählte einen Ton, der noch gar nicht klingt.
         *    `weg=strom` trennt die Zeilen im Journal.
         *
         * ⚠️ Messlatte (Prüfrunde): Bei `weg=strom` ist synth nur für den
         *    ersten Satz einer Antwort eine Wartezeit auf den Ton. Ab dem
         *    zweiten läuft es vom Einreihen bis zum ersten Ton und enthält
         *    das Abspielen aller Sätze davor — nicht mit blob-Zeilen
         *    vergleichen. Für Lücken zwischen Sätzen gilt dort `wait`.
         */
        const strom = istStromAdresse(url);
        /*
         * Ticket 2919 (02.10.2026): Beim PCM-Weg gilt dasselbe wie beim Strom —
         * die Adresse steht lange vor dem Ton, die Sonde meldet erst beim
         * ersten hörbaren Ton. Format der Zeile UNVERÄNDERT (`seg= synth=
         * wait= len=`, sonst liest leseTtsSatz in server/latenz-store.ts sie
         * nicht und antwort_zug verliert `tts_synth`); angehängt wird nur
         * `weg=pcm paket= vorlauf=`: Text → erstes Paket (`paket`) und die
         * Reserve beim Start (`vorlauf`). `synth` ist Text → erster Ton.
         */
        const pcmWeg = istPcmAdresse(url);
        const seg = this.ttsSeg++;
        let gemeldet = false;
        const sonde = (m?: PcmMessung): void => {
          if (gemeldet) return;
          gemeldet = true;
          if (strom || pcmWeg) item.readyAt = Date.now();
          const synth = (item.readyAt ?? Date.now()) - item.enqueuedAt;
          // Ticket 2558: die gewollte Absatzstille ist keine Lücke — `wait`
          // misst nur, was die Vertonung schuldig blieb.
          const wait = Date.now() - waitStart - gewollteStille;
          /*
           * 17.10.2026 (Gutachten): `synth` enthält ab dem 2. Stück das Abspielen der Stücke davor (Korrelation mit der
           * Stücknummer 0,98) — es bleibt als historische Zahl stehen. `bereit` misst ab der FREIGABE des Stücks: es ist
           * eingereiht UND an der Reihe (die Stücke davor sind gespielt, die gewollte Stille ist vorbei) — bis zur
           * Bereitschaft (Datei: vertont; Strom/PCM: erster Ton). Lag es vorher schon fertig da: 0.
           */
          const bereit = tonBereitMs({
            eingereiht: item.enqueuedAt,
            dran: waitStart + gewollteStille,
            bereitAm: item.readyAt ?? Date.now(),
          });
          // Ticket 2692: die Mindestpause davor, damit sie im Journal steht.
          const pause = item.pauseDavorMs > 0 ? ` pause=${item.pauseDavorMs}` : "";
          const weg = m
            ? ` weg=pcm paket=${m.paketMs}ms vorlauf=${m.vorlaufMs}ms`
            : pcmWeg
              ? " weg=pcm-datei"
              : strom
                ? " weg=strom"
                : "";
          this.meldeZeile(
            `[tts-t] seg=${seg} synth=${synth}ms wait=${wait}ms len=${item.text.length}${pause}${weg} bereit=${bereit}ms`,
          );
          this.zugMessung.stueck(synth, wait, m ? "pcm" : "datei");
        };
        if (!strom && !pcmWeg) sonde();
        const preAudio = item.audio;
        this.playQueue.shift();
        // 19.10.2026 (Mikro-Vorstart): läuft das LETZTE Stück, zieht die Uhr das Mikro kurz vor dem Ende vor (mikro-vorstart.ts).
        beobachteWiedergabe(this);
        // Ein Platz in der Reihe ist frei geworden: das nächste PCM-Stück darf laden.
        this.starteVorladen();
        this.stueckTextLaenge = item.text.length;
        this.stueckText = item.text;
        meldeWiedergabeName(item.text); // 20.10.2026: nur ein Bool an die App (Echo-Schutz Wach-Wort)
        this.stueckStand = { bisS: 0, dauerS: NaN, ganz: false };
        this.stueckOffen = true;
        this.nurWeb = false; // je Stück neu: die App darf es wieder versuchen (stimme-nativ.ts)
        // Ein fremder Abruf (für ein vorbeigegangenes Stück) bleibt nicht verwaist liegen.
        if (this.dateiVorab && this.dateiVorab !== item.dateiVorab) this.dateiVorab.abbrechen();
        this.dateiVorab = item.dateiVorab ?? null;
        this.aktuellesStueck = item;
        if (!url) {
          // Letzter Versuch, jetzt: zwischen dem Fehlschlag und diesem
          // Moment liegen Sekunden, in denen das Netz zurueckkam.
          const spaet = await this.fetchTts(item.text, this.abort.signal).catch(
            () => "",
          );
          if (spaet) {
            await this.playOne(spaet);
            this.letztesTonEnde = Date.now();
            // 05.10.2026: Dieser Weg zählte bisher gar nicht — ein spät doch noch gesprochenes Stück fehlte in der Bilanz.
            this.zaehleStueck();
            continue;
          }
          this.stueckOffen = false; // nie gespielt: es zählt unten als verloren, nicht über `zaehleStueck`
          this.zeichenVerloren += item.text.length;
          this.meldeVerlust(item.text, item.fehler ?? "unbekannt");
          continue;
        }
        // 2245: nächster Brocken ist schon in playQueue + urlPromise
        // (enqueue startet die Synthese sofort). Nicht erst nach onended.
        await this.playOne(url, preAudio, strom || pcmWeg ? sonde : undefined, item.pcm, seg === 0);
        // Ticket 2558: ab hier zählt die Stille vor einem Absatz.
        this.letztesTonEnde = Date.now();
        // Ticket 2232: erst NACH playOne zaehlen — ein Abbruch (Barge-in,
        // Fehler) laesst die Zahl absichtlich zurueckbleiben. Genau diese
        // Luecke ist der gesuchte Ort.
        this.zaehleStueck();
      }
    } finally {
      this.playLoopRunning = false;
      if (this.finished && this.playQueue.length === 0) this.resolveIdle();
    }
  }

  /**
   * Was aus dem eben gespielten Stück geworden ist. (05.10.2026)
   *
   * ⚠️ Bis heute zählte ein Stück ganz oder gar nicht. Journal 05.10. 10:55: `eingereiht=298 gesprochen=0
   *    abbruch=pille` — ob vom ersten Stück etwas zu hören war, sagte die Zeile nicht. Umgekehrt galt ein Stück, das
   *    der Wachhund nach 14 s Stille übersprang oder das mit einem Fehler endete, als gesprochen (29.09.–05.10.: 15
   *    und 4 von 658 Wiedergaben standen so als vollständig im Journal). Jetzt zählt der Anteil, den die Zeit
   *    wirklich vorrückte (vorlese-wiederanlauf.ts, `gesprocheneZeichen`).
   * ⚠️ Der Rest eines ABGEBROCHENEN Stücks bleibt wie bisher ungezählt (er war gewollt still). Ohne Abbruch ist er
   *    verloren und steht jetzt auch so da: `eingereiht = gesprochen + verloren + offen`.
   * ⚠️ Zwei Rufer, EIN Zählen (`stueckOffen`): der Abspiel-Lauf nach dem Stück, und `cancel` sofort beim Abbruch.
   */
  private zaehleStueck(): void {
    if (!this.stueckOffen) return;
    this.stueckOffen = false;
    const laenge = this.stueckTextLaenge;
    const gesprochen = gesprocheneZeichen(laenge, this.stueckStand);
    this.zeichenGesprochen += gesprochen;
    if (!this.abort.signal.aborted) this.zeichenVerloren += laenge - gesprochen;
    // 19.10.2026: nur ein GANZ gespieltes Stück rückt die Fortsetz-Stelle vor; ein Abbruch lässt sie am Anfang dieses Stücks.
    if (this.fortsetzSpur && gesprochen >= laenge && !this.abort.signal.aborted) this.fortsetzSpur.stueckFertig(this.stueckText);
  }

  /**
   * Auf welchem Element ein stehendes Stück frisch anläuft. (05.10.2026)
   *
   * In der App ein NEUES: Ton braucht dort keine Geste, und im Journal half seit Ticket 2547 jedes Mal genau das
   * (`via=stillstand`, danach `via=ended`: 02.10. 08:05, 03.10. 17:41, 04.10. 13:20 und 14:56).
   *
   * Im Browser DASSELBE, neu geladen: Safari lässt ein Element, das NACH der letzten Berührung entstand, nicht ohne
   * Geste spielen — gemessen in WebKit 26.5: 4,5 s nach der Geste spielt ein neues Element, 6 s danach lehnt es ab
   * (NotAllowedError); ein Element, das in der Geste entstand oder schon gespielt hat, darf jederzeit, auch mit
   * neuer Quelle. Das Element, das eben stand, hatte seine Erlaubnis; ein neues hätte sie oft nicht, und aus dem
   * Wiederanlauf würde ein „Tippen zum Weiterspielen“. Das Neuladen gibt ihm einen frischen Spieler.
   */
  private elementFuerAnlauf(stehendes: HTMLAudioElement, adresse: string): HTMLAudioElement | undefined {
    if (isNativePlatform()) return undefined;
    try {
      stehendes.pause();
      stehendes.src = adresse; // lädt neu (die Zuweisung stößt das Laden selbst an)
      return stehendes;
    } catch {
      return undefined;
    }
  }

  /** Der Spieler meldet, bis wohin die Zeit im Stück vorrückte und wie lang es ist. Nur nach vorn. */
  private merkeStand(bisS: number, dauerS: number): void {
    if (Number.isFinite(bisS) && bisS > this.stueckStand.bisS) this.stueckStand.bisS = bisS;
    if (Number.isFinite(dauerS) && dauerS > 0) this.stueckStand.dauerS = dauerS;
  }

  private async playOne(
    url: string,
    preAudio?: HTMLAudioElement,
    /** Ticket 2448: läuft, sobald der erste Ton wirklich spielt (Sonde des Strom-Wegs; beim PCM-Weg mit Messwerten). */
    beiTon?: (m?: PcmMessung) => void,
    /** Ticket 2919: das schon laufende Herunterladen eines PCM-Stücks. */
    pcm?: PcmStueck,
    /** Ticket 2919: erstes Stück seiner Antwort (zählt für den Rückfall des PCM-Wegs gesondert). */
    erstesStueck = false,
    /** Ticket 2919: Datei-Weg nach einer Unterbrechung — sofort starten; lehnt der Browser `play()` ab: „Tippen zum Weiterspielen“. */
    hinten = false,
    /** 05.10.2026: der wievielte frische Anlauf dieses Stücks das ist (vorlese-wiederanlauf.ts, höchstens `ANLAUF_MAX`). */
    anlauf = 0,
  ): Promise<void> {
    /*
     * ⚠️⚠️ 01.10.2026, Ticket 2882 — EINE STIMME, ZU JEDEM ZEITPUNKT.
     *
     * Martin, 08:49: *„Doppel-Ausgabe von Voice … es darf immer nur eine
     * geben … da hat es irgendwie zwei Spuren gleichzeitig gespielt."* Zwei
     * Züge antworteten binnen einer Sekunde, jeder in seiner Schlange, und
     * jede Schlange spielte ihr Stück — nebeneinander. `stopCurrentTts` räumt
     * nur ab, wenn ein Mensch oder ein neuer Zug es ruft; zwei Schlangen, die
     * nichts voneinander wissen, tun es nie.
     *
     * Jetzt meldet sich jede Schlange, BEVOR ihr erstes Stück klingt
     * (seiten-abgleich.ts): wer zuletzt zu sprechen beginnt, spricht — die
     * ältere endet mit einem Abbruch (`abbruch:neue-antwort` /
     * `abbruch:andere-seite`, dann `tts-audio-abbruch`, nie „Ende"), auch
     * wenn sie auf einer anderen Seite desselben Browsers läuft — und seit
     * dem 05.10.2026 auch auf einem anderen Gerät desselben Kontos
     * (`abbruch:anderes-geraet`, über den Server: shared/stimme-konto.ts).
     *
     * ⚠️ Nur, solange diese Schlange selbst noch lebt: eine abgebrochene
     *    meldet sich nicht mehr und löst keine andere ab.
     * ⚠️ BEWUSSTE WAHL: es gilt „wer zuletzt zu sprechen BEGINNT, spricht" —
     *    nicht „die NEUESTE Antwort spricht". Eine ältere Antwort, die langsam
     *    vertont wird und erst nach der neueren beginnt, schneidet diese ab
     *    (Begründung und Test: seiten-abgleich.ts `stimmeBeginnt`, eine-stimme-2882).
     */
    /*
     * 16.10.2026 (Martin: „eine laufende Spur darf nicht unterbrochen werden“): VOR dem Beginn fragen, ob die Stimme darf
     * (seiten-abgleich.ts `stimmeDarfBeginnen`). Spricht gerade der ANDERE Sprecher (Vespera ↔ Schmied), wartet diese
     * Schlange, bis er fertig ist; ist ein anderes Gerät vorn, spricht sie gar nicht. Ein Stück, das schon zu dieser Stimme
     * gehört, geht sofort durch. Während des Wartens zählt die Schlange als „spielt“ (`wartetAufStimme`): sonst löste ein
     * `flush()` ihr Ende aus, während der Abspiel-Lauf noch hier steht.
     */
    /*
     * 17.10.2026 (Martin: „Meditation läuft … darf durch keine andere überdeckt werden“, lange-spur.ts): läuft eine LANGE
     * SPUR (Meditation, Spiegel, Biografie, Musik), beginnt diese Stimme nicht — Vesperas Schlange WARTET, die des Schmieds
     * wird verworfen (die Blase bleibt). Ausnahme: Martins eigener Abspielknopf. VOR `stimmeBeginnt`, damit eine wartende
     * Schlange keine andere Stimme ablöst. Während des Wartens zählt sie als „spielt“ (`wartetAufStimme`, wie unten).
     * Ein Abbruch (`cancel`) beendet das Warten; es gibt dann kein „Ende“ (tts-audio-abbruch), das Mikro bleibt zu.
     */
    if (!this.abort.signal.aborted && !this.ausdruecklich && langeSpurLaeuft()) {
      const t0 = Date.now();
      this.wartetAufStimme = true;
      const verwerfen = this.sprecher === "schmied";
      if (!verwerfen) this.meldeLangeSpur(this.beiWartenAufLangeSpur, true);
      let r: Awaited<ReturnType<typeof warteAufLangeSpur>>;
      try {
        r = await warteAufLangeSpur(this.abort.signal, { verwerfen });
      } finally {
        this.wartetAufStimme = false;
        if (!verwerfen) this.meldeLangeSpur(this.beiWartenAufLangeSpur, false);
      }
      this.meldeZeile(
        `[lange-spur] stimme sprecher=${this.sprecher ?? "vespera"} ergebnis=${r} gewartet=${Date.now() - t0}ms`,
      );
      if (r === "frist") this.meldeLangeSpur(this.beiLangeSpurFrist);
      if (r === "verworfen" || r === "frist") {
        this.cancel(r === "verworfen" ? "lange-spur" : "lange-spur-frist");
        return;
      }
      if (this.abort.signal.aborted) return;
    }
    if (!this.abort.signal.aborted) {
      /*
       * 17.10.2026 (seiten-abgleich.ts „Geparkt“): nicht vorn + Beitrags-Kennung → nicht verwerfen, sondern zurückhalten, bis
       * diese Seite vorn wird (dann neu entscheiden), eine andere ihn spricht oder die Frist um ist. Ohne Kennung wie bisher.
       */
      const parkBis = Date.now() + STIMME_PARK_MS;
      let entscheid: StimmeEntscheid;
      for (;;) {
        entscheid = await this.stimmeEntscheiden();
        if (entscheid.freigabe !== "nicht-vorn") break;
        const rest = parkBis - Date.now();
        if (!this.kennung || rest <= 0) {
          this.cancel("nicht-vorn");
          return;
        }
        const t0 = Date.now();
        this.wartetAufStimme = true;
        this.meldeGeparkt(true);
        // stimmeParken wirft nie (seiten-abgleich.ts) — das Ende geht mit seinem Ergebnis an den Hörer.
        const park: ParkErgebnis = await stimmeParken(this, this.abort.signal, rest);
        this.wartetAufStimme = false;
        this.meldeGeparkt(false, park);
        this.meldeZeile(`[stimme-park] sprecher=${this.sprecher ?? "vespera"} ergebnis=${park} gewartet=${Date.now() - t0}ms`);
        if (park === "abbruch" || this.abort.signal.aborted) return;
        if (park !== "vorn") {
          this.cancel(park === "anderswo" ? ANDERSWO_GESPROCHEN : "nicht-vorn");
          return;
        }
      }
      if (this.abort.signal.aborted) return;
      // 26.10.2026: derselbe Beitrag läuft schon auf einem anderen Gerät → gar nicht erst beginnen (nie doppelt, nie abschneiden).
      // Martins Knopf (`ausdruecklich`) spricht trotzdem.
      if (!this.ausdruecklich && beitragAnderswoLaeuft(this.kennung)) {
        this.cancel(ANDERSWO_GESPROCHEN);
        return;
      }
      stimmeBeginnt(this, Date.now(), entscheid.freigabe === "frist" ? "schlange-frist" : "neue-antwort");
      this.stimmeBegonnen = true;
      // Die zurückgehaltenen Abrufe der wartenden Stücke (`pcmZurueck`) dürfen jetzt los.
      this.starteVorladen();
    }
    /*
     * 17.10.2026 (Stimme nativ): hält die App das Mikro beim Vorlesen offen und kann sie spielen, spielt SIE das Stück
     * (stimme-nativ.ts) — NACH der Freigabe der einen Stimme, VOR jedem Web-Weg. Lehnt sie ab: dasselbe Stück unten weiter.
     */
    if (!this.nurWeb) {
      const r = await this.spieleUeberApp(url, pcm, beiTon);
      if (r === null) return;
      this.nurWeb = true;
      url = r;
    }
    /*
     * Ticket 2919 (02.10.2026): PCM-Stück — NACH `stimmeBeginnt` (eine Stimme),
     * VOR dem `<audio>`-Weg. Alles Weitere (Hintergrund, Rückfall auf die
     * Datei) entscheidet `spielePcm`.
     */
    if (istPcmAdresse(url)) {
      await this.spielePcm(url, pcm, beiTon, erstesStueck);
      return;
    }
    /*
     * Ticket 2547 (24.09.2026): Beginnt ein Stück, während die Seite im
     * Hintergrund liegt (Bildschirm aus), spielt es NATIV. Ein `<audio>`,
     * das dort beginnt, kommt nicht in Gang — belegt am 23.09. um 23:19:44
     * (Martin) und 15:33:17 (Albert), beide erst vom Wachhund beendet.
     * Siehe vorlesen-nativ.ts. Im Vordergrund bleibt es beim Web-Weg.
     */
    if (document.visibilityState === "hidden" && nativerVorleserDa()) {
      await this.spieleStueckNativ(url, 0, beiTon);
      return;
    }
    // Ticket 2547: Die Tonsitzung für die Dauer der Vorlesung halten (und
    // die Frist verlängern) — damit sie beim Sperren noch steht.
    vorleseHalt(true, this, this.haltProtokoll);
    // Vorgeladenes Element nutzen (schon gebuffert) -> gapless. Nur wenn
    // keins da ist, frisch erzeugen (Fallback, z.B. Ton-Hinweise).
    const audio = preAudio ?? new Audio();
    if (!preAudio) {
      audio.preload = "auto";
      audio.src = url;
    }
    this.playing = audio;
    // 2026-07-19: hookHiFiPlayback hier BEWUSST entfernt (s. Kommentar im
    // Play-Loop) — die Bot-Stimme bleibt im Telefonprofil, damit PTT am
    // Bluetooth-Geraet sofort reagiert.
    //
    // ⚠️ 19.09.2026 — DER ZWEITE HALBSATZ WAR SEIT DEM 28.07. FALSCH. Hier
    //    stand: "Nebeneffekt: das warme Mikro wird waehrend der Antwort
    //    nicht mehr freigegeben, der naechste PTT-Druck trifft den warmen
    //    Pfad." Seit dem 28.07. gibt ptt.ts das Mikro bei
    //    `voice-v8:tts-audio-start` sehr wohl frei (ptt.ts, Grund
    //    "tts-start-hifi"). Am 19.09. gemessen: 25 von 71 Druecken waren
    //    genau deshalb kalt. Zwei Besitzer, der spaetere gewinnt — und der
    //    Kommentar hier beschrieb den frueheren.
    //
    // ⚠️ Was BLEIBT: kein Hi-Fi fuer die Stimme. Was DAZUKOMMT: sie meldet
    //    sich bei der Halt-Diagnose an. Melden ist nicht Fordern —
    //    hookStimmeDiagnose traegt in eine eigene Menge ein, die applyHiFi
    //    nicht anfasst. Ohne das meldete `wiedergabeHaltStand()` waehrend
    //    jeder gesprochenen Antwort "nichts offen", und der Abriss der
    //    Tagesaufnahme am 19.09. um 15:56:37 sah aus wie ein Raetsel.
    hookStimmeDiagnose(audio);
    this.onAudioStart?.();
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        audio.removeEventListener("canplay", finish);
        audio.removeEventListener("canplaythrough", finish);
        resolve();
      };
      // Vorgeladen + schon abspielbar (readyState>=3 HAVE_FUTURE_DATA) ->
      // sofort los, kein Warten. Sonst auf canplay warten (250ms-Deckel).
      // Ticket 2919: Nach einer Unterbrechung nicht auf `canplay` oder den 250-ms-Zeitgeber warten — in einer
      // versteckten Seite laufen die gedrosselt oder gar nicht; `play()` darf sofort, es reiht sich selbst ein.
      if (audio.readyState >= 3 || hinten) {
        finish();
        return;
      }
      audio.addEventListener("canplay", finish);
      audio.addEventListener("canplaythrough", finish);
      window.setTimeout(finish, 250);
      // load() NUR fuer frisch erzeugte Elemente — ein erneutes load() auf ein
      // vorgeladenes Element wuerde den Puffer verwerfen (Gapless-Killer).
      if (!preAudio) {
        try {
          audio.load();
        } catch {
          /* ignore */
        }
      }
    });
    if (this.abort.signal.aborted) return;
    // AUDIT-Fund 2 (20.07.): drittes Gate — Pause im canplay-Wartefenster
    // (bis 250 ms) gedrueckt: ohne diese Schleife spielte play() trotzdem.
    while (this.paused && !this.abort.signal.aborted) {
      await new Promise((r) => setTimeout(r, 200));
    }
    if (this.abort.signal.aborted) return;
    // 2026-07-19 (Martin, "viele Pausen zwischen den Saetzen"): Sonde. Gemessen
    // ist bereits: Synthese normal schnell, Dateien ohne Stille, waehrend der
    // Wiedergabe KEINE Netzanfrage, nur EIN Bluetooth-Wechsel. Bleibt die
    // Frage, WIE das Warten je Abschnitt endet — sauber ueber "ended" oder
    // erst ueber den Wachhund (Dauer + 10 s). Genau das haelt diese Zeile fest.
    const playStart = Date.now();
    let exitVia = "?";
    /*
     * 05.10.2026: Wo das Element stand, als es endete — festgehalten, BEVOR ein Abbruch die Quelle entlädt. `cancel`
     * nimmt einem Strom-Element die Adresse; danach sind Dauer, Zeit und readyState weg, und die Zeile unten schrieb
     * `audio=-1.00s … rs=0` (zuletzt am 05.10. um 10:55:43 — ob das Stück je vorrückte, stand nirgends).
     */
    const endStand = { fest: false, ct: 0, dauer: -1, rs: 0 };
    const haltFest = (): void => {
      if (endStand.fest) return;
      endStand.fest = true;
      endStand.ct = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
      endStand.dauer = Number.isFinite(audio.duration) ? audio.duration : -1;
      endStand.rs = audio.readyState;
    };
    // Der Wiederanlauf (vorlese-wiederanlauf.ts) ist am Gerät abschaltbar; aus = Stand von pre.3636.
    const neu = wiederanlaufAn();
    /*
     * Prüfung 06.10.2026: Die 3-s-Regel (ein Stück steht vorn, obwohl sein Ton da ist → frisch, unten im Takt) gilt
     * NUR IN DER APP. Im Mac-Browser gab es keinen gemessenen Stillstand, und dort überstimmte sie eine Pause von
     * außen (Medientaste, AirPods heraus) nach 3 s. Vorgabe „Mac-Browser unverändert“: dort bleibt main, den Anlauf
     * nach der Rückkehr ausgenommen (Ticket 2547).
     */
    const dreiSekundenRegel = neu && isNativePlatform();
    /** Der wievielte Anlauf der nächste Lauf dieses Stücks ist — setzt `wiederanlauf` (unten), bevor es diesen Lauf beendet. */
    let naechsterAnlauf = anlauf + 1;
    // Der Anfang DIESES Laufs im Stück: mit `#t=` springt das Element nach dem Laden dorthin — erst darüber hinaus ist es Ton.
    // Die Zeit des Elements gilt nur, wenn es schon etwas geladen hat: ein eben umgestecktes zeigt sonst noch die alte Stelle.
    const basis = Math.max(
      audio.readyState >= 1 && Number.isFinite(audio.currentTime) ? audio.currentTime : 0,
      startAusAdresse(url),
    );
    // Die höchste Stelle, die dieses Stück VOR diesem Lauf erreicht hatte — kommt dieser Lauf hörbar darüber hinaus,
    // war der letzte Anlauf ein Erfolg (`anlaufZaehltNeu`, im `wiederanlauf` unten).
    const hoechsteVorher = Math.max(this.stueckStand.bisS, basis);
    /** Bilanz: nur was über den Anfang dieses Laufs hinaus vorrückte, war zu hören (vorlese-wiederanlauf.ts). */
    const standMelden = (): void => {
      haltFest();
      this.merkeStand(endStand.ct > basis + FORTGANG_MIN_S ? endStand.ct : 0, endStand.dauer);
    };
    await new Promise<void>((resolve) => {
      const onEnd = (): void => {
        exitVia = exitVia === "?" ? "ended" : exitVia;
        cleanup();
        resolve();
      };
      const onErr = (ablehnung?: unknown): void => {
        if (exitVia === "?") {
          // Ticket 2329: nie mehr nur „error" — siehe fehlerGrund().
          const grund = fehlerGrund(ablehnung, audio.error);
          exitVia = `error:${grund}`;
          /*
           * ⚠️ Und nicht nur protokollieren. Eine gescheiterte VERTONUNG
           *    meldet sich seit pre.3020 beim Menschen; eine gescheiterte
           *    WIEDERGABE tat es nie — obwohl genau sie den Fall enthaelt,
           *    den er selbst beheben kann (einmal tippen). Die Bremse in
           *    meldeVorleseVerlust sorgt dafuer, dass es bei EINER Meldung
           *    je Vorgang bleibt.
           */
          // Ticket 2448: Auf dem Durchreich-Weg laeuft die Anfrage gleich
          // gepuffert nach — erst DEREN Scheitern ist ein Verlust.
          if (!this.nachladenMoeglich(url)) this.meldeVerlust("", `wiedergabe:${grund}`);
        }
        cleanup();
        resolve();
      };
      const onAbort = (): void => {
        // Ticket 2232: Der Abbruch war der einzige namenlose Ausgang — und
        // damit der einzige, bei dem die Bilanz nicht erklaerte, warum sie
        // unvollstaendig ist.
        exitVia = `abbruch:${this.abbruchGrund ?? "unbekannt"}`;
        standMelden(); // sofort: `cancel` zählt das Stück noch im selben Zug
        try {
          audio.pause();
        } catch {
          /* ignore */
        }
        cleanup();
        resolve();
      };
      /*
       * Ticket 2382 (23.09.2026): ERST MESSEN. Scheitert `play()` oder
       * läuft das Stück nicht an, steht das jetzt mit Sichtbarkeit und
       * Fehlername im Protokoll. `[sichtbar-d] wiedergabe=` taugt dafür
       * nicht, es bleibt ständig true. `el=spielt` heißt: das Element
       * meldet `paused=false` und steht trotzdem.
       *
       * ⚠️ Vor `cleanup` angelegt: der synchrone `tts-audio-start` weiter
       *    unten kann schon abbrechen, und `cleanup` räumt dann diese Frist.
       * ⚠️ Über `meldeSpur`, den vorhandenen Kanal (mikro-spur.ts): der
       *    schluckt jeden Fehler beim Senden. Ein eigener Sender ohne
       *    try/catch hätte im `play().catch` unten `onErr` überspringen
       *    können — das Stück hinge dann bis zum Wachhund.
       */
      const sichtBeimStart = document.visibilityState;
      /** Hat dieser Lauf noch keinen Ton hervorgebracht? Nur vorrückende Zeit zählt — `playing` und `paused=false` nicht. */
      const stehtAmAnfang = (): boolean => !(audio.currentTime > basis + FORTGANG_MIN_S);
      const meldeStart = (art: "scheitert" | "steht", ablehnung?: unknown): void => {
        const fehler = art === "scheitert" ? fehlerGrund(ablehnung, audio.error) : "-";
        meldeSpur(
          "tts-start",
          `${art} sicht=${sichtBeimStart} jetzt=${document.visibilityState} ` +
            `fehler=${fehler} nach=${Date.now() - playStart}ms rs=${audio.readyState} ` +
            `el=${audio.paused ? "pausiert" : "spielt"}`,
        );
      };
      const startPruefung = window.setTimeout(() => {
        if (exitVia !== "?" || this.paused || this.abort.signal.aborted) return;
        // Nur die Messung (unverändert seit Ticket 2382). Ob das Stück frisch anläuft, entscheidet der Takt unten.
        if (stehtAmAnfang()) meldeStart("steht");
      }, START_PRUEF_MS);
      /*
       * 05.10.2026 — steht ein Stück VORN, obwohl sein Ton da ist? (vorlese-wiederanlauf.ts) — nur in der App
       * (`dreiSekundenRegel`, Prüfung 06.10.); im Browser misst `stehtSeit` mit, entscheidet aber nichts.
       *
       * `stehtSeit`: seit wann die Zeit nicht vorrückt, während der Ton abspielbar vorliegt (readyState ≥ 3), die
       * Seite sichtbar ist und niemand pausiert hat. Nach `START_PRUEF_MS` läuft das Stück frisch an (der Takt unten,
       * alle 0,5 s) — gleich, ob es von Anfang an stand (`start`) oder mittendrin stehen blieb (`mitte`). Im Journal
       * seit 29.09. stand ein Stück bei sichtbarer Seite und geladenem Ton von Anfang an zweimal, beide Male wirklich
       * (01.10. 16:03 `el=spielt` → Wachhund nach 14,3 s; 05.10. 10:57 `el=pausiert`); drei weitere liefen vorn in
       * den Wachhund, ohne dass das Journal sagt, wo sie standen (09:40, 13:15, 20:56 am 01.10.).
       *
       * ⚠️ Gezählt wird ab dem Augenblick, in dem der Ton DA ist — nicht ab `play()`. Sonst träfe die Prüfung ein
       *    Stück, dessen Datei nach 2,9 s ankam, 0,1 s später „stehend“ an (so scheiterte pre.3278 am 24.09.:
       *    `via=stockt … ct=0.00s`, 0,5 s nach dem ersten Ton). Und ein Stück, dessen Datei erst nach 3,5 s kommt
       *    und DANN steht (der Vorfall von 10:55 ohne den Sichtbarkeitswechsel), bliebe unbemerkt.
       * ⚠️ Nicht nur am Anfang: Rückt die Zeit ein Stück vor und steht dann (das System hält das Element 60 ms nach
       *    dem Start an — so geschehen im WebKit-Test), gäbe es sonst wieder nur den Wachhund, und der Rest ginge verloren.
       * ⚠️ Kein Stillstand ist: Nachladen (readyState fällt unter 3), versteckte Zeit, eine Nutzer-Pause (alle drei
       *    setzen die Uhr zurück) und ein Stück, das kurz vor seinem Ende steht — dem ging nur das `ended` verloren,
       *    es gilt als zu Ende (statt es noch einmal zu sprechen).
       */
      let stehtSeit: number | null = audio.readyState >= 3 && document.visibilityState === "visible" ? playStart : null;
      /** Die Zeit des Elements beim letzten Takt — rückt sie von Takt zu Takt vor, steht es nicht. */
      let standZeit = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
      /*
       * Ticket 2919: Ist es der Datei-Weg nach einer Unterbrechung des PCM-Wegs (versteckter Bildschirm, Sperren,
       * Anruf), kann iOS den Start still verschlucken. Lehnt der Browser `play()` ab, steht ein sichtbarer Hinweis,
       * und die erste Berührung (oder die Rückkehr in den Vordergrund) spielt weiter. Der Wachhund zählt in dieser
       * Zeit nicht: er beendete das Stück sonst nach Dauer + 10 s als Verlust.
       *
       * ⚠️ 05.10.2026: Der Hinweis kam bis heute auch, wenn das Stück nach der Rückkehr nur STAND (`rueckkehr`) oder
       *    nach 8 s keinen Ton hatte (`frist`). Beides läuft jetzt frisch an (`wiederanlauf`, unten) — der Hinweis
       *    bleibt für den Fall, den nur ein Mensch lösen kann: `play()` abgelehnt, oder `ANLAUF_MAX` frische Starts
       *    ohne Ton. Die alten Zweige stehen noch für den Schalter am Gerät (`neu === false`).
       */
      const startetHinten = hinten;
      let hatGespielt = false;
      let tippt = false;
      let tippAb: (() => void) | null = null;
      /** So lange steht der Hinweis schon bei sichtbarer Seite, ohne Nutzer-Pause (zählt der Takt unten). */
      let tippVornMs = 0;
      const zeigeTipp = (grund: string): void => {
        if (tippt || exitVia !== "?" || this.abort.signal.aborted) return;
        // Zurückgenommen (Schalter aus): nur der Datei-Weg nach einer PCM-Unterbrechung, und nicht mehr nach `playing`.
        if (!neu && (!startetHinten || hatGespielt)) return;
        tippt = true;
        this.meldeZeile(`[tts-pcm] tippen-zum-weiterspielen grund=${grund} sicht=${document.visibilityState}`);
        tippVornMs = 0;
        tippAb = beruehrungSetztFort(
          audio,
          () => {
            tippt = false;
            tippAb = null;
            // Jetzt rückt die Zeit vor — das ist der erste Ton dieses Stücks (die Sonde meldet nur einmal). Das
            // `play()` der Berührung trägt sie nicht, und das ursprüngliche wurde abgelehnt oder vom Anhalten abgewiesen.
            try {
              beiTon?.();
            } catch {
              /* eine Sonde darf die Wiedergabe nie stören */
            }
          },
          // Steht es nach `ANLAUF_MAX` frischen Starts, hilft ein weiteres `play()` auf DIESEM Element nicht sicher:
          // die Berührung gibt dem Stück einen frischen Spieler (und zwei neue Anläufe). Nach einem abgelehnten Start
          // ist das Element in Ordnung — dort startet die Berührung es selbst, in der Geste (datei-spieler.ts).
          neu && grund === "steht" ? () => wiederanlauf("beruehrung") : undefined,
        );
      };
      /**
       * EIN Ort entscheidet, was mit einem Stück geschieht, das vorn steht (vorlese-wiederanlauf.ts). Die Aufrufer
       * wissen, DASS es steht: 2 s nach der Rückkehr (`rueckkehr`) oder seit 3 s mit geladenem Ton — von Anfang an
       * (`start`) oder nachdem schon etwas zu hören war (`mitte`).
       * Hinten, in einer Nutzer-Pause und während es auf eine Berührung wartet, geschieht nichts.
       * `beruehrung`: Martin tippt auf den Hinweis eines stehenden Stücks — ein Mensch bittet um einen weiteren
       * Versuch, also beginnt die Zählung der Anläufe neu.
       * Prüfung 06.10.2026: Ebenso, wenn DIESER Lauf hörbar über alles bisher Erreichte hinauskam — der letzte Anlauf
       * hat geholfen (`anlaufZaehltNeu`; nie bei `blob:`). Bis dahin endeten drei kurze Hänger in einem Stück im Hinweis.
       */
      const wiederanlauf = (grund: "rueckkehr" | "start" | "mitte" | "beruehrung"): void => {
        if (exitVia !== "?" || this.paused || this.abort.signal.aborted) return;
        const stand = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
        const bisher = anlaufZaehltNeu(url, stand, hoechsteVorher) ? 0 : anlauf;
        if (grund !== "beruehrung") {
          if (tippt || document.visibilityState !== "visible") return;
          // `ANLAUF_MAX`-mal frisch gestartet, ohne dass einer vorankam, und es steht wieder: nicht weiterprobieren,
          // nicht überspringen — fragen.
          if (bisher >= ANLAUF_MAX) {
            zeigeTipp("steht");
            return;
          }
        }
        naechsterAnlauf = grund === "beruehrung" ? 1 : bisher + 1;
        // `motor=`: der letzte Stand der Tonsitzung laut Mikrofon-Motor (App) — klingt ein frischer Spieler während
        // einer Unterbrechung überhaupt? (Prüfung 06.10.: 18 von 30 Wachhund-Fällen lagen in `unterbrochen`.)
        this.meldeZeile(
          `[tts-neu] grund=${grund} nr=${naechsterAnlauf} stand=${stand.toFixed(2)}s ` +
            `rs=${audio.readyState} el=${audio.paused ? "pausiert" : "spielt"} weg=${startetHinten ? "pcm-datei" : "datei"} ` +
            `motor=${motorSitzungText()}`,
        );
        meldeTonZustand("tts-neu"); // 16.10.2026: wohin der Ton in diesem Augenblick geht (Hörmuschel? playback-hifi.ts)
        this.zugMessung.neu += 1;
        exitVia = "stillstand";
        try {
          audio.pause();
        } catch {
          /* egal */
        }
        onErr();
      };
      /*
       * 15.10.2026 (vorlese-wiederanlauf.ts, `WEITER_MAX`): vom System angehalten → an der Stelle weiter, auf DEMSELBEN
       * Element. `systemAbgebrochen`: das erste `play()` wurde mit AbortError abgewiesen, ohne dass wir pausierten — das
       * Element hat dann evtl. noch nichts geladen (`rs=0` am 15.10. um 21:20:50), weiter soll es trotzdem.
       */
      let weiterVersuche = 0;
      /** Alle `[tts-weiter]` dieses Laufs (Takt und sofort) — die Nummer in der Zeile. */
      let weiterZeilen = 0;
      let systemAbgebrochen = false;
      /*
       * 17.10.2026 — WER hat das Element angehalten? Beim `pause`-Ereignis festgehalten (das Ereignis kommt nach dem
       * Anhalten, als eigene Aufgabe): wer selbst anhält, setzt seinen Zustand VORHER (`this.paused`, Abbruch, `exitVia`,
       * `tippt`). Steht nichts davon, war es fremd — das System (Tonsitzung unterbrochen) oder eine Taste von außen.
       */
      let letztePause: { ursache: string; t: number } | null = null;
      const pauseUrsache = (): string => {
        if (this.abort.signal.aborted) return "eigen-abbruch";
        if (this.paused) return "eigen-pause";
        if (exitVia !== "?") return "eigen-ende";
        if (tippt) return "eigen-tippen";
        if (audio.ended) return "ende";
        return "fremd";
      };
      /** Die Felder für `[tts-weiter]`: nur Zahlen und Wörter (Eingabe, Bewegung, Sichtbarkeit, wer anhielt). */
      const weiterUmstand = (): string => {
        const seit = msSeitBewegung();
        const pause = letztePause ? `${letztePause.ursache}/${Math.max(0, Date.now() - letztePause.t)}ms` : "-";
        return (
          `eingabe=${leseEingabe()} seitScroll=${seit ?? "-"} sicht=${document.visibilityState} ` +
          `el=${audio.paused ? "pausiert" : "spielt"} pause=${pause}`
        );
      };
      /** Sofort-Fortsetzungen dieses Laufs (Zeitpunkte, `sofortErlaubt`); nach einem abgelehnten `play()` keine mehr. */
      const sofortZeiten: number[] = [];
      let sofortGesperrt = false;
      /** Wann zuletzt sofort fortgesetzt wurde — wie ein `play()` im Takt bekommt es einen Takt Zeit (die 3-s-Regel wartet). */
      let letzterSofort = 0;
      const weiterAnDerStelle = (grund: "pausiert" | "abgebrochen", art: "takt" | "sofort" = "takt"): void => {
        if (art === "takt") weiterVersuche += 1;
        weiterZeilen += 1;
        this.zugMessung.weiter += 1;
        const stand = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
        this.meldeZeile(
          `[tts-weiter] grund=${grund} nr=${weiterZeilen} stand=${stand.toFixed(2)}s rs=${audio.readyState} ` +
            `motor=${motorSitzungText()} art=${art} ${weiterUmstand()}`,
        );
        meldeTonZustand("tts-weiter"); // 16.10.2026: Route/Kategorie beim Anhalten durch das System (playback-hifi.ts)
        const abgelehnt = (e: unknown): void => {
          const name = (e as { name?: string } | null)?.name ?? "";
          // Wieder vom System abgewiesen: der nächste Takt versucht es noch einmal (höchstens `WEITER_MAX`-mal).
          // Sonst greift die 3-s-Regel (frischer Spieler) oder — ohne geladenen Ton — der Wachhund.
          if (name === "AbortError" && exitVia === "?" && !this.paused && !this.abort.signal.aborted) {
            systemAbgebrochen = true;
          }
          // 17.10.2026: Verweigert der Browser das sofortige `play()` (NotAllowedError …), probiert es dieser Lauf nicht
          // noch einmal sofort — es bleibt beim Takt und seinen bisherigen Wegen (3-s-Regel, Hinweis). Kein neuer Weg.
          if (art === "sofort" && name !== "AbortError") {
            sofortGesperrt = true;
            this.meldeZeile(`[tts-weiter] sofort-abgelehnt fehler=${fehlerGrund(e, audio.error)} nr=${weiterZeilen}`);
          }
        };
        try {
          void audio.play().then(() => {
            try {
              beiTon?.();
            } catch {
              /* eine Sonde darf die Wiedergabe nie stören */
            }
          }, abgelehnt);
        } catch (e) {
          abgelehnt(e); // ein synchron werfendes `play()` (ältere WebKit) wie eine Ablehnung behandeln
        }
      };
      /*
       * 17.10.2026 (vorlese-wiederanlauf.ts, `SOFORT_MAX`): hält das SYSTEM das Element an, sofort an der Stelle weiter —
       * nicht erst im nächsten Takt. Dieselben Bedingungen wie der Takt (App, sichtbar, Ton da, nicht am Ende), dazu:
       * niemand hat selbst angehalten (`pauseUrsache`), höchstens `SOFORT_MAX` in `SOFORT_FENSTER_MS` und
       * `SOFORT_JE_LAUF_MAX` im ganzen Lauf. Öffnet nichts, meldet nichts an das Mikrofon — nur `play()` auf DEMSELBEN
       * Element.
       *
       * ROUTE (Prüfung 17.10.): Vor dem Weiterspielen fragt die Seite die App nach dem Ausgang (`ausgangJetzt`, höchstens
       * `ROUTE_FRIST_MS`). Ist der Kopfhörer weg (`kopfhoererWeg`: vorher Bluetooth/Kopfhörer/Auto, jetzt Lautsprecher
       * oder Hörmuschel), wird das eine Pause der Schlange — wie der Pause-Knopf, die Stelle bleibt, Fortsetzen über
       * Knopf oder Sperrbildschirm; das gilt für jedes fremde Anhalten in der App, auch hinten und auch ohne Sofort.
       * Hat sich der Ausgang sonst geändert oder ist er nicht zu lesen, geht es nicht sofort weiter (der Takt bleibt wie
       * bisher). Solange die Frage läuft, setzt auch der Takt nicht fort.
       *
       * ANRUF / anderer Ton: Geprüft wird `motorAudioText()` — die Ursache, die der Mikrofon-Motor zur Unterbrechung
       * meldet. Das wirkt NUR, wenn diese Meldung schon da ist, bis die Route gelesen ist. Kommt sie später, ist das
       * Element schon wieder gestartet; hält iOS es erneut an, wirkt die Prüfung ab dann (dazu die Zähler).
       */
      let routeVorher: string | null | undefined = undefined; // undefined: noch nicht gelesen
      let routePruefungLaeuft = false;
      const liesRoute = (): Promise<string | null> =>
        Promise.race([
          ausgangJetzt(),
          new Promise<null>((ok) => window.setTimeout(() => ok(null), ROUTE_FRIST_MS)),
        ]).catch(() => null);
      const merkeRoute = (): void => {
        if (!dreiSekundenRegel) return;
        void liesRoute().then((r) => {
          if (!routePruefungLaeuft) routeVorher = r;
        });
      };
      const sofortDarf = (): boolean => {
        if (sofortGesperrt || exitVia !== "?" || tippt || this.paused || this.abort.signal.aborted) return false;
        if (document.visibilityState !== "visible" || !audio.paused || audio.ended) return false;
        if (audio.readyState < 3) return false; // nachladen: der Takt (und sein `systemAbgebrochen`) entscheidet
        const zeit = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
        if (Number.isFinite(audio.duration) && audio.duration > 0 && zeit >= audio.duration - ENDE_NAH_S) return false;
        return motorAudioText() !== "ja";
      };
      const onPause = (): void => {
        const ursache = pauseUrsache();
        letztePause = { ursache, t: Date.now() };
        if (ursache !== "fremd" || !dreiSekundenRegel || routePruefungLaeuft) return;
        routePruefungLaeuft = true;
        const vorher = routeVorher;
        void liesRoute().then((jetztRoute) => {
          routePruefungLaeuft = false;
          if (exitVia !== "?" || this.paused || this.abort.signal.aborted || !audio.paused) return;
          if (kopfhoererWeg(vorher, jetztRoute)) {
            this.meldeZeile(`[tts-weiter] route-weg vorher=${vorher ?? "-"} jetzt=${jetztRoute ?? "-"} pause=ja`);
            this.pause();
            // Knopf und Pille auf „pausiert“ — wie wenn die App ihre Stimme selbst anhält (app.ts, `fernbedienungAnwenden`):
            // ein Tipp setzt dann sofort fort, statt erst „anzuhalten“.
            stimmeNativPauseMelden();
            return;
          }
          if (vorher === undefined || vorher !== jetztRoute) {
            // Ausgang unbekannt oder gewechselt: nicht sofort (der Takt bleibt wie bisher), ab jetzt gilt der neue.
            this.meldeZeile(`[tts-weiter] sofort-aus route vorher=${vorher ?? "-"} jetzt=${jetztRoute ?? "-"}`);
            routeVorher = jetztRoute;
            return;
          }
          if (!sofortDarf()) return;
          const jetzt = Date.now();
          if (!sofortErlaubt(sofortZeiten, jetzt)) return;
          sofortZeiten.push(jetzt);
          letzterSofort = jetzt;
          weiterAnDerStelle("pausiert", "sofort");
        });
      };
      audio.addEventListener("pause", onPause);
      merkeRoute(); // der Ausgang beim Start dieses Laufs (und bei jedem `playing`, unten)
      const merkeLauf = (): void => {
        hatGespielt = true;
        merkeRoute(); // der Ausgang, über den dieser Lauf gerade klingt
      };
      audio.addEventListener("playing", merkeLauf);
      /*
       * Die Frist HINTEN (Ticket 2919): ein Stück nach einer PCM-Unterbrechung, das nach `TIPP_FRIST_MS` kein
       * `playing` hatte, wartet — der Wachhund zählt dann nicht mehr. 05.10.2026: Das bleibt, solange die Seite
       * versteckt ist, genau so (`wartetHinten`); nur der Hinweis dazu entfällt (hinten sieht ihn niemand, und vorn
       * läuft das Stück jetzt frisch an). Zurückgenommen (Schalter aus) kommt der Hinweis wie in pre.3636.
       */
      let wartetHinten = false;
      const tippFrist = startetHinten
        ? window.setTimeout(() => {
            if (this.paused || hatGespielt) return;
            if (!neu) zeigeTipp("frist");
            else if (document.visibilityState === "hidden") wartetHinten = true;
          }, TIPP_FRIST_MS)
        : undefined;
      let fortgangPruefung: number | undefined;
      const cleanup = (): void => {
        window.clearInterval(endPoll);
        window.clearTimeout(startPruefung);
        window.clearTimeout(fortgangPruefung);
        window.clearTimeout(tippFrist);
        audio.removeEventListener("playing", merkeLauf);
        audio.removeEventListener("pause", onPause);
        tippAb?.();
        document.removeEventListener("visibilitychange", onVisible);
        audio.removeEventListener("ended", onEnd);
        audio.removeEventListener("error", onErr);
        this.abort.signal.removeEventListener("abort", onAbort);
      };
      // 2026-07-05 (Martin): Watchdog — iOS kann nach Audio-Session-
      // Stoerungen weder "ended" noch "error" feuern; ohne Deckel hing die
      // ganze PTT-Kette (und der Alltags-Recorder wurde nie fortgesetzt).
      // Ticket 2547 (23.09.2026): unbekannte Dauer -> aus der Textlänge.
      const capMs = wachhundDeckelMs(audio.duration, this.stueckTextLaenge);
      // AUDIT-Fund 1 (20.07.): Der Wachhund war ein ABSOLUTER Timer — eine
      // Nutzer-Pause bekam keinen Kredit: 8s-Satz, bei Sekunde 2 pausiert,
      // 13s spaeter fortgesetzt -> der Timer feuerte mitten im Satz und
      // verschluckte den Rest. Jetzt ist der Wachhund eine POLL-DEADLINE,
      // die nur AKTIVE (nicht pausierte) Zeit zaehlt — Pausen beliebiger
      // Laenge sind gratis. Der Delta-Deckel (1,5s) verhindert, dass eine
      // Hintergrund-Einfrierung beim Aufwachen als Riesenschritt zaehlt.
      let wachhundAktivMs = 0;
      let wachhundLetzterTick = Date.now();
      // 2026-07-19 (Sonde 16:52:32, erster bewiesener Fall): iOS verliert
      // gelegentlich das ended-EVENT — der Zustand des Elements stimmt aber
      // (ended/currentTime wissen, dass Schluss ist). Ein 3s-Satz kostete so
      // exakt 13,05s (Dauer + 10s Wachhund) Totzeit am Ohr. Deshalb POLL:
      // alle 500ms den Zustand pruefen und binnen ~0,5s aufloesen, wo das
      // Event nie zugestellt wird. Der Wachhund bleibt als letztes Netz
      // fuer wirklich eingefrorene Elemente.
      // 2026-07-19 (Wachhund-Faelle 17:27/17:28 MIT neuem Bundle, einer
      // +10s, einer ~100s = eingefrorener 90s-Timer): Im HINTERGRUND friert
      // iOS die Timer der WebView ein — Poll und Wachhund stehen still, und
      // iOS pausiert die Wiedergabe. Bei Rueckkehr in den Vordergrund:
      // Zustand SOFORT pruefen; einen mitten im Satz pausierten Ton
      // WEITERSPIELEN statt verschlucken (ausser der Zug wurde abgebrochen).
      const onVisible = (): void => {
        if (document.visibilityState !== "visible") return;
        wartetHinten = false; // vorn entscheidet der Wiederanlauf, und der Wachhund zählt wieder
        try {
          const d = audio.duration;
          const amEnde =
            audio.ended ||
            (Number.isFinite(d) &&
              d > 0 &&
              audio.currentTime >= d - 0.05 &&
              audio.paused);
          if (amEnde) {
            if (exitVia === "?") exitVia = "visible";
            onEnd();
            return;
          }
          // Prüfung 17.10. (B): nicht anlaufen, solange die Route gefragt wird — sonst spielte ein Stück, dessen Kopfhörer
          // im Hintergrund wegging, beim Entsperren kurz über den Lautsprecher, bevor die Antwort „Kopfhörer weg“ kommt.
          if (audio.paused && !this.paused && !this.abort.signal.aborted && !routePruefungLaeuft) {
            void audio.play().catch(() => {
              /* dann greifen Poll/Wachhund regulaer */
            });
          }
        } catch {
          /* egal */
        }
        /*
         * Ticket 2547 (23.09.2026): Läuft es danach auch wirklich?
         *
         * Zweimal am 23.09. stand ein Stück nach der Rückkehr weiter still,
         * obwohl die Seite vorn war: Albert 17 s (15:40:45 -> 15:41:02),
         * Martin 88 s (23:19:47 -> 23:21:15), beide Male erst vom Wachhund
         * beendet, und so lange blieb der Ton belegt. Beide Stücke hatten im
         * Hintergrund begonnen. Das `play()` oben half dort nicht. Vermutet,
         * nicht bewiesen: das Element meldet `paused=false` und spielt
         * trotzdem nicht — `[tts-start] el=` (Ticket 2382) zeigt es.
         *
         * Also die Zeit selbst prüfen: Rückt sie in ~2 s nicht vor, wird das
         * Stück beendet und unten einmal frisch gestartet.
         */
        window.clearTimeout(fortgangPruefung);
        // Mit `#t=` springt die Zeit beim Laden auf die Startstelle — das ist kein Fortgang (`basis`).
        const zeitBeimZurueckkommen = Math.max(audio.currentTime, basis);
        fortgangPruefung = window.setTimeout(() => {
          if (exitVia !== "?" || this.paused || this.abort.signal.aborted) return;
          if (document.visibilityState !== "visible") return;
          if (audio.currentTime > zeitBeimZurueckkommen + FORTGANG_MIN_S) return;
          /*
           * 05.10.2026: JEDES Stück läuft hier frisch an — auch das nach einer PCM-Unterbrechung. Für dieses stand
           * bis heute ein Hinweis („Tippen zum Weiterspielen“), dessen `play()` auf einem Element, das schon
           * „spielt“ meldet, nichts tat: 10:55:30 Hinweis, 10:55:43 Abbruch, kein Ton dazwischen belegt.
           * Gehörtes wird dabei nicht noch einmal gesprochen: der Anlauf beginnt an der erreichten Stelle (unten).
           */
          if (neu) {
            wiederanlauf("rueckkehr");
            return;
          }
          // Zurückgenommen (Schalter aus), Stand pre.3636: nach einer PCM-Unterbrechung der Hinweis, sonst frisch von vorn.
          if (startetHinten) {
            zeigeTipp("rueckkehr");
            return;
          }
          exitVia = "stillstand";
          try {
            audio.pause();
          } catch {
            /* egal */
          }
          onErr();
        }, STILLSTAND_PRUEF_MS);
      };
      document.addEventListener("visibilitychange", onVisible);
      /*
       * Ticket 2547 (24.09.2026): Steht ein Stück, das vorn begonnen hat,
       * nach dem Sperren still (am 23.09. um 20:28 endete Stück 0 erst über
       * den Wachhund, 12,7 s für 2,7 s Ton), spielt der REST nativ weiter —
       * statt Stille bis zum Wachhund und danach ein Stück, das im
       * Hintergrund nicht mehr anläuft. Gezählt wird nur Zeit im
       * Hintergrund; vorn gilt die Prüfung beim Zurückkommen (oben).
       */
      let hintenZeit = audio.currentTime;
      let hintenSeit = Date.now();
      const endPoll = window.setInterval(() => {
        const jetzt = Date.now();
        const schritt = Math.min(jetzt - wachhundLetzterTick, 1500);
        if (!this.paused && !tippt && !wartetHinten) {
          wachhundAktivMs += schritt;
        }
        wachhundLetzterTick = jetzt;
        if (this.paused) {
          stehtSeit = null; // nach dem Fortsetzen zählt die Frist des Anlaufs neu
          return; // Nutzer-Pause: Zeit ruht, kein Ende
        }
        /*
         * 05.10.2026: „Tippen zum Weiterspielen“ wartet vorn nicht ewig (vorlese-wiederanlauf.ts,
         * `HINWEIS_WARTET_MAX_MS`). Solange die Schlange wartet, gilt die Seite als „spielt“: die Tagesaufnahme bleibt
         * pausiert und das Wach-Wort ruht. Kommt in einer Minute sichtbarer Zeit keine Berührung, endet das Vorlesen
         * mit einem benannten Abbruch — der Text steht in der Blase. Hinten zählt nichts (dort wartet es wie bisher
         * auf die Rückkehr). Die Frist gilt auch mit abgeschaltetem Wiederanlauf: der Hinweis geht seit heute erst,
         * wenn die Zeit vorrückt (datei-spieler.ts) — ohne Frist wartete ein stehendes Stück dort ohne Ende.
         */
        if (tippt && document.visibilityState === "visible") {
          tippVornMs += schritt;
          if (tippVornMs >= HINWEIS_WARTET_MAX_MS) {
            this.cancel("kein-tipp");
            return;
          }
        }
        // Steht es vorn? (`stehtSeit`, oben): Ton da, Seite sichtbar, und die Zeit rückt seit `START_PRUEF_MS` nicht vor.
        // Nur in der App (`dreiSekundenRegel`): im Browser wäre das auch eine Pause von außen (Medientaste, AirPods).
        const zeit = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
        /*
         * 16.10.2026: Ging in DIESEM Takt ein `play()` an der Stelle hinaus, entscheidet die 3-s-Regel erst im nächsten
         * (≤ 0,5 s) — ob es half, zeigt erst die vorrückende Zeit. Journal 11:53:39 (Martin, iPhone): `[tts-weiter] nr=2
         * stand=0.11s` und im selben Takt `[tts-neu] grund=mitte … el=spielt` — der frische Spieler hielt das eben
         * fortgesetzte Element wieder an und begann das Stück (unter `GEHOERT_AB_S`) bei seinem Anfang. Höchstens
         * `WEITER_MAX` Takte Aufschub je Lauf.
         */
        // 17.10.2026: ebenso nach einem sofortigen `play()` (auf das `pause`-Ereignis) in den letzten 0,5 s.
        let weiterInDiesemTakt = jetzt - letzterSofort < 500;
        // 15.10.2026: vom System angehalten (niemand hat pausiert, kein Abbruch, nicht am Ende) → gleich an der Stelle weiter.
        if (
          dreiSekundenRegel &&
          exitVia === "?" &&
          !tippt &&
          !routePruefungLaeuft &&
          audio.paused &&
          !audio.ended &&
          (audio.readyState >= 3 || systemAbgebrochen) &&
          document.visibilityState === "visible" &&
          weiterVersuche < WEITER_MAX &&
          !(Number.isFinite(audio.duration) && audio.duration > 0 && zeit >= audio.duration - ENDE_NAH_S)
        ) {
          weiterAnDerStelle(systemAbgebrochen ? "abgebrochen" : "pausiert");
          systemAbgebrochen = false;
          weiterInDiesemTakt = true;
        }
        if (audio.readyState < 3 || document.visibilityState !== "visible") stehtSeit = null;
        else if (stehtSeit === null || Math.abs(zeit - standZeit) > 0.005) stehtSeit = jetzt;
        standZeit = zeit;
        if (dreiSekundenRegel && !weiterInDiesemTakt && !tippt && stehtSeit !== null && jetzt - stehtSeit >= START_PRUEF_MS && !audio.seeking && !audio.ended) {
          const d = audio.duration;
          if (Number.isFinite(d) && d > 0 && zeit >= d - ENDE_NAH_S) {
            // Kurz vor seinem Ende stehen geblieben: nur das `ended` ging verloren (iOS, Sonde vom 19.07.) — es ist fertig.
            if (exitVia === "?") exitVia = "poll";
            onEnd();
            return;
          }
          wiederanlauf(stehtAmAnfang() ? "start" : "mitte");
          if (exitVia !== "?") return;
        }
        if (document.visibilityState !== "hidden" || audio.currentTime > hintenZeit + 0.05) {
          hintenZeit = audio.currentTime;
          hintenSeit = jetzt;
        } else if (
          exitVia === "?" &&
          jetzt - hintenSeit >= STILLSTAND_PRUEF_MS &&
          nativerVorleserDa() &&
          !audio.ended
        ) {
          exitVia = "hintergrund";
          try {
            audio.pause();
          } catch {
            /* egal */
          }
          onErr();
          return;
        }
        if (wachhundAktivMs >= capMs) {
          exitVia = "WACHHUND";
          // Element ausdruecklich stilllegen — haengt das ended-Event,
          // bliebe es sonst fuer immer im playback-hifi-Set.
          try {
            audio.pause();
          } catch {
            /* egal */
          }
          onErr();
          return;
        }
        try {
          const d = audio.duration;
          const amEnde =
            audio.ended ||
            (Number.isFinite(d) &&
              d > 0 &&
              audio.currentTime >= d - 0.05 &&
              audio.paused);
          if (amEnde) {
            if (exitVia === "?") exitVia = "poll";
            onEnd();
          }
        } catch {
          /* egal */
        }
      }, 500);
      audio.addEventListener("ended", onEnd);
      audio.addEventListener("error", () => onErr());
      this.abort.signal.addEventListener("abort", onAbort);
      // 2026-07-08 (Martin, "Ton mal leise"): Wiedergabe-Start signalisieren,
      // damit der PTT-Layer den warmen Mikro-Stream freigibt (iOS: laute
      // Wiedergabe ueber den Lautsprecher statt leise ueber den Ohrhoerer).
      const ersterTon = !this.playedThisTurn;
      this.playedThisTurn = true;
      try {
        // 02.10.2026: die Dauer des Stücks (falls schon bekannt) für die Miete
        // des Seitentons (seiten-ton.ts); alle anderen Hörer ignorieren `detail`.
        window.dispatchEvent(
          new CustomEvent("voice-v8:tts-audio-start", {
            detail: { dauerS: audio.duration, queueId: this.seitenTonId },
          }),
        );
      } catch {
        /* ignore */
      }
      // Ticket 2421 (23.09.2026): den Weg beim ersten Ton des Vorgangs
      // festhalten. Angestoßen nach dem Ereignis und direkt vor play(); wie
      // spät die Brücke antwortet, steht als nachMs in der Zeile. Wartet
      // NICHT — der Ton startet sofort.
      if (ersterTon) meldeErstenTon();
      // Ticket 2382: ein Fehlstart meldet sich (meldeStart, siehe oben).
      // Ticket 2448: beiTon misst den ersten Ton des Stroms.
      void audio.play().then(
        () => {
          try {
            beiTon?.();
          } catch {
            /* eine Sonde darf die Wiedergabe nie stören */
          }
        },
        (e: unknown) => {
          // 05.10.2026: Wartet das Stück schon auf eine Berührung, beendet ein abgewiesenes ÄLTERES play() nichts —
          // die Berührung hält das Element erst an (datei-spieler.ts), und das weist ein noch offenes play() ab.
          if (tippt) return;
          // Ein Abbruch, eine Nutzer-Pause oder das eigene pause() beenden ein
          // offenes play() ebenfalls — das ist kein Fehlstart.
          if (exitVia === "?" && !this.paused && !this.abort.signal.aborted) {
            meldeStart("scheitert", e);
          }
          // Ticket 2919: Ein abgelehnter Start nach einer Unterbrechung ist KEIN verlorenes Stück: Hinweis + Berührung.
          // 05.10.2026: ebenso ein abgelehnter WIEDERANLAUF (`anlauf > 0`) — das Stück lief schon einmal an.
          if (
            (startetHinten || (neu && anlauf > 0)) &&
            (e as { name?: string } | null)?.name === "NotAllowedError" &&
            exitVia === "?"
          ) {
            zeigeTipp("abgelehnt");
            return;
          }
          // 15.10.2026: In der App weist das SYSTEM den Start ab (AbortError, niemand hat pausiert — die Tonsitzung wurde
          // unterbrochen): kein verlorenes Stück, der Takt spielt es an der Stelle weiter (vorlese-wiederanlauf.ts).
          if (
            dreiSekundenRegel &&
            (e as { name?: string } | null)?.name === "AbortError" &&
            exitVia === "?" &&
            !this.paused &&
            !this.abort.signal.aborted &&
            weiterVersuche < WEITER_MAX
          ) {
            systemAbgebrochen = true;
            return;
          }
          onErr(e);
        },
      );
    });
    // Sonde auswerten: dauer = Laenge der Audiodatei, echt = tatsaechlich
    // verstrichene Zeit. Klaffen die auseinander, sitzt die Pause hier.
    standMelden();
    {
      const echt = Date.now() - playStart;
      // 05.10.2026: `ct=` — wo das Element stand. `ct` weit unter `audio` bei großem `echt` heißt: es stand.
      const line =
        `[tts-gap] via=${exitVia} audio=${endStand.dauer.toFixed(2)}s echt=${(echt / 1000).toFixed(2)}s ` +
        `rs=${endStand.rs} ct=${endStand.ct.toFixed(2)}s`;
      this.zugMessung.lauf(exitVia, echt, endStand.ct - basis);
      console.log(line);
      void fetch('/api/debug/ptt-timing', {
        method: 'POST',
        body: JSON.stringify({ line }),
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true,
        credentials: 'include',
      }).catch(() => undefined);
    }
    // Ticket 2547 (23.09.2026): Das stillstehende Element ist verbraucht —
    // dasselbe Stück frisch, statt es zu verschlucken. Ein neues Element
    // beginnt im Vordergrund.
    if (exitVia === "stillstand" && !this.abort.signal.aborted) {
      this.playing = null;
      const quelle = this.nachladenMoeglich(url) ? pufferUrl(url) : url;
      if (!neu) {
        // Zurückgenommen (Schalter aus), Stand pre.3636: von vorn, einmal; steht auch das, greift sein Wachhund.
        await this.playOne(quelle);
        return;
      }
      /*
       * 05.10.2026 (vorlese-wiederanlauf.ts): ab der Stelle, an der es stand, wenn schon etwas zu hören war — sonst
       * von seinem Anfang (eine `blob:`-Adresse immer von ihrem Anfang, `wiederanlaufAdresse`). Die Sonde des ersten
       * Tons und die Art des Stücks reisen mit. Steht auch der frische Lauf, prüft ihn seine eigene Startmessung
       * (3 s, App) oder die Rückkehr — höchstens `ANLAUF_MAX`-mal ohne Fortgang, dann hält das Stück an und fragt.
       */
      const adresse = wiederanlaufAdresse(quelle, endStand.ct, basis);
      await this.playOne(adresse, this.elementFuerAnlauf(audio, adresse), beiTon, undefined, false, hinten, naechsterAnlauf);
      return;
    }
    // Ticket 2547: im Hintergrund stehen geblieben — der Rest nativ, ab der
    // Stelle, an der das Element stand.
    if (exitVia === "hintergrund" && !this.abort.signal.aborted) {
      this.playing = null;
      await this.spieleStueckNativ(url, endStand.ct);
      return;
    }
    // Ticket 2448 (21.09.2026): Die zwei Juli-Schwaechen des Durchreich-Wegs
    // faengt die Warteschlange selbst — ein Fehler laeuft EINMAL gepuffert
    // nach, ein zu fruehes Ende spielt den Rest aus dem inzwischen fertigen
    // Zwischenspeicher. `puffer=1` in der Adresse heisst: schon nachgeladen.
    if (this.nachladenMoeglich(url) && !this.abort.signal.aborted) {
      if (exitVia.startsWith("error:")) {
        this.meldeZeile("[tts-gap] direkt scheiterte, gepuffert nachladen");
        this.playing = null;
        await this.playOne(pufferUrl(url), undefined, beiTon);
        return;
      }
      if (exitVia === "ended" || exitVia === "poll" || exitVia === "visible") {
        const rest = await pruefeNachspiel(url, audio.currentTime);
        if (rest && !this.abort.signal.aborted) {
          this.meldeZeile(`[tts-gap] direkt endete ${rest.fehlt.toFixed(1)}s zu frueh, Rest ab ${rest.ab.toFixed(1)}s`);
          this.playing = null;
          await this.playOne(`${pufferUrl(url)}#t=${rest.ab.toFixed(2)}`);
          return;
        }
      }
    }
    this.playing = null;
    // Bilanz: nur ein Stück, das bis zu seinem Ende lief, zählt ganz (Wachhund, Fehler und Abbruch: der gehörte Anteil).
    if (exitVia === "ended" || exitVia === "poll" || exitVia === "visible") this.stueckStand.ganz = true;
    this.onAudioEnd?.();
  }

  /**
   * Ein Stück als PCM über Web Audio. (02.10.2026, Ticket 2919, Stufe 1)
   *
   * Dieselben Signale wie der Web-Weg (`onAudioStart`, `tts-audio-start`,
   * erster Ton, `onAudioEnd`, `tts-audio-end` am Ende des Zuges), damit
   * Sprechtaste, Hi-Fi, Seiten-Ton-Miete (seiten-ton.ts) und Bilanz nichts
   * davon merken, welcher Spieler klang.
   *
   * ⚠️ Hintergrund (Seite im Hintergrund, nativer Spieler da): PCM wird nicht
   *    benutzt — der native Spieler spielt die Datei. Geht die Seite MITTEN im
   *    Stück in den Hintergrund, spielt er den Rest aus dem Zwischenspeicher
   *    ab der Stelle, an der das PCM stand.
   * ⚠️ Jeder Fehler vor dem ersten Paket und jede Absage des Servers schickt
   *    dieses Stück auf den Datei-Weg (`/api/tts/stream?puffer=1`); wie oft
   *    das passiert, zählt `pcmRueckfall` (tts-pcm-rueckfall.ts).
   */
  private async spielePcm(
    url: string,
    vorgeladen: PcmStueck | undefined,
    beiTon: ((m?: PcmMessung) => void) | undefined,
    erstesStueck: boolean,
  ): Promise<void> {
    const datei = pcmDateiUrl(url);
    // Mitten im Stück wechselt nur, wer einen nativen Spieler hat; ohne ihn bleibt das PCM, wo es ist.
    const verborgen = (): boolean => document.visibilityState === "hidden";
    const imHintergrund = (): boolean => verborgen() && nativerVorleserDa();
    // Beginnt das Stück bei verstecktem Bildschirm, spielt die Datei (nativ, sonst wie vor Ticket 2919):
    // ein AudioContext, der im Hintergrund erst anläuft, bliebe gesperrt.
    if (verborgen()) {
      vorgeladen?.verwerfen();
      await this.spieleDateiHinten(datei, 0, beiTon, vorgeladen?.dateiAudio);
      return;
    }
    // 11.10.2026: Nach einem Stromfehler (Netzabriss) spielt dieses Stück zehn Minuten lang als DATEI — sie wird vor dem
    // Abspielen ganz geladen, ein Abriss kann dort keine Lücke in den Ton reißen (tts-pcm-gedaechtnis.ts). Das gilt auch für
    // Stücke, die als PCM schon eingereiht und vorgeladen sind.
    // 16.10.2026 (Ganz-Modus): außer das Stück ist schon GANZ geladen — dann kann kein Abriss mehr eine Lücke reißen, und der
    // Web-Audio-Weg bleibt (auf dem Datei-Weg hält iOS das `<audio>` bei jeder Unterbrechung der Tonsitzung an).
    if (pcmGedaechtnis.dateiWeg() && !(this.ganzVorabMs !== null && vorgeladen?.fertigGeladen() === true)) {
      this.meldeZeile(pcmGedaechtnis.zeile("datei"));
      vorgeladen?.verwerfen();
      await this.playOne(datei, vorgeladen?.dateiAudio ?? undefined, beiTon ? () => beiTon() : undefined);
      return;
    }
    const pcm = vorgeladen ?? new PcmStueck(url, this.abort.signal);
    // Nutzer-Pause vor dem Start: warten wie der Web-Weg.
    while (this.paused && !this.abort.signal.aborted) {
      await new Promise((r) => setTimeout(r, 200));
    }
    if (this.abort.signal.aborted) {
      pcm.verwerfen();
      return;
    }
    vorleseHalt(true, this, this.haltProtokoll);
    this.pcmSpielt = pcm;
    meldePcmStimme(true);
    const start = Date.now();
    const onVisible = (): void => {
      // Ticket 2919 (a): Sobald die Seite versteckt wird, die Dateien der wartenden Stücke vorladen — nicht erst nach 15 s.
      if (verborgen()) this.hintergrundVorbereiten();
      if (imHintergrund()) pcm.unterbrechen("hintergrund");
    };
    // Läuft der Kontext nicht mehr (iOS: `interrupted`), ebenso — und das Stück geht sofort auf die Datei.
    pcm.beiKontextWeg = () => this.hintergrundVorbereiten();
    document.addEventListener("visibilitychange", onVisible);
    this.onAudioStart?.();
    let ende: Awaited<ReturnType<PcmStueck["spiele"]>>;
    let gemietet = false;
    let getoent = false;
    // 11.10.2026: nach einem Aussetzer in dieser Antwort wartet das Stück auf mehr Vorrat (das erste Stück nie).
    // 11.10.2026: dazu das Gedächtnis über die Antwort hinaus (Störung in den letzten zehn Minuten) — gilt auch für das erste Stück.
    const vorratAntwort = this.pcmVorrat.fuer(erstesStueck);
    const vorratSoll = Math.max(vorratAntwort, pcmGedaechtnis.vorratS());
    if (vorratSoll > vorratAntwort) this.meldeZeile(pcmGedaechtnis.zeile("vorrat"));
    try {
      ende = await pcm.spiele({
        startVorratS: vorratSoll,
        // Punkt 5 (Nachprüfung 02.10.): die Miete VOR dem Wecken des Kontexts — er nimmt die Tonsitzung, bevor ein Ton kommt.
        vorWecken: () => {
          gemietet = true;
          seitenTonMelder.ttsStart(undefined, this.seitenTonId);
        },
        vorErstemTon: () => {
          getoent = true;
          const ersterTon = !this.playedThisTurn;
          this.playedThisTurn = true;
          try {
            // Die Miete des Seitentons (seiten-ton.ts): Kennung dieser Schlange; die Dauer ist beim PCM noch
            // nicht bekannt (20 s Frist, der Takt erneuert alle 5 s) — sie wächst mit dem Strom.
            window.dispatchEvent(
              new CustomEvent("voice-v8:tts-audio-start", { detail: { queueId: this.seitenTonId } }),
            );
          } catch {
            /* ignore */
          }
          if (ersterTon) meldeErstenTon();
        },
        beiErstemTon: (m) => {
          // Nur wenn der Vorrat angehoben war: Journal (Zahlen), damit messbar ist, was er gekostet und gebracht hat.
          if (vorratSoll > START_VORRAT_S) {
            this.meldeZeile(
              `[tts-pcm] vorrat soll=${Math.round(vorratSoll * 1000)}ms start=${m.vorlaufMs}ms paket=${m.paketMs}ms ton=${m.tonMs}ms ` +
                `bisher=${this.pcmVorrat.stand().aussetzer}x/${Math.round(this.pcmVorrat.stand().lueckeMs)}ms`,
            );
          }
          beiTon?.(m);
        },
      });
    } finally {
      document.removeEventListener("visibilitychange", onVisible);
      this.pcmSpielt = null;
      meldePcmStimme(false);
      // Gemietet, aber nie getönt (Weck-Frist, Abbruch im Wecken): die Miete zurück — sonst hielte der Takt sie bis zu
      // 5 Minuten, falls kein Ende der Schlange mehr kommt. Spielt gleich die Datei, meldet sie sich neu an.
      if (gemietet && !getoent) seitenTonMelder.ttsEnde(this.seitenTonId);
    }
    const echt = (Date.now() - start) / 1000;
    // Was diesem Stück am Vorrat zustieß (Aussetzer, Lücke), gilt für die folgenden Stücke dieser Antwort.
    this.pcmVorrat.melde(ende.aussetzer, ende.lueckeMs);
    this.zugMessung.luecke(ende.lueckeMs);
    this.merkeStoerung(ende);
    // Ticket 2919: Pegel hinter der Klangkette, Zustand des Kontexts, Tonsitzung — je Strom eine Zeile, nur Zahlen.
    const pegel = pcm.pegelZeile();
    if (pegel) this.meldeZeile(pegel);
    // 11.10.2026: ein Stück mit Aussetzer sagt dazu, ob der Strom spät kam oder die Seite blockiert war (nur Zahlen).
    if (ende.aussetzer > 0) this.meldeZeile(pcm.stoerungsZeile());
    if (ende.art === "abbruch") {
      // Bilanz: `cancel` hat den gehörten Teil schon festgehalten und gezählt, bevor es die Quellen stoppte.
      this.meldeZeile(
        `[tts-gap] via=abbruch:${this.abbruchGrund ?? "unbekannt"} weg=pcm audio=${ende.dauerS.toFixed(2)}s echt=${echt.toFixed(2)}s aussetzer=${ende.aussetzer} luecke=${Math.round(ende.lueckeMs)}ms`,
      );
      // Wie auf dem Web-Weg: auch ein Abbruch endet mit onAudioEnd (die Sprech-Pille und der Knopf lösen sich daran).
      this.onAudioEnd?.();
      return;
    }
    // Ein Stück, das gespielt wurde, zählt für den Rückfall; ein Fehler vor dem ersten Paket auch.
    if (ende.art === "ende") {
      this.stueckStand.ganz = true;
      this.meldeZeile(
        `[tts-gap] via=pcm audio=${ende.dauerS.toFixed(2)}s echt=${echt.toFixed(2)}s aussetzer=${ende.aussetzer} luecke=${Math.round(ende.lueckeMs)}ms`,
      );
      this.pruefeRueckfall(pcmRueckfall.stueck(erstesStueck, ende.aussetzer > 0));
      vorleseHalt(true, this, this.haltProtokoll);
      this.onAudioEnd?.();
      return;
    }
    // art === "datei": der Datei-Weg übernimmt — ab der Stelle, an der das PCM stand.
    this.merkeStand(ende.abS, NaN);
    // Eigene Zeilenart: das Stück schreibt gleich seine EINE `[tts-gap]`-Zeile (der Datei-Weg), die Lücken-Zählung bleibt eindeutig.
    this.meldeZeile(
      `[tts-pcm] datei grund=${ende.grund} ab=${ende.abS.toFixed(2)}s aussetzer=${ende.aussetzer} luecke=${Math.round(ende.lueckeMs)}ms` +
        (ende.weck ? ` weckMs=${ende.weck.ms}` : ""),
    );
    // K1: die Weck-Frist in der App — gemessen, nicht gezählt; erst die dritte in Folge zählt EINMAL als Fehler.
    if (ende.weck) {
      this.meldeZeile(`[tts-pcm] weck-frist state=${ende.weck.state} ms=${ende.weck.ms} folge=${ende.weck.folge}`);
      if (ende.weck.zaehlt) this.pruefeRueckfall(pcmRueckfall.fehlerVorStart("weckfrist"));
    }
    // Ticket 2919 (d): Sperren und Unterbrechen sind Zustände des Telefons, keine Fehler des Weges — sie zählen
    // weder als Fehler noch als gestörtes Stück (zwei Sperr-Unterbrechungen dürfen den Schalter nicht abschalten).
    const unterbrechung = !ende.fehler && (ende.grund === "hintergrund" || ende.grund.startsWith("kontext-"));
    if (unterbrechung) {
      this.hintergrundVorbereiten();
    } else if (ende.vorErstemPaket) {
      if (ende.fehler) this.pruefeRueckfall(pcmRueckfall.fehlerVorStart(ende.grund));
    } else {
      this.pruefeRueckfall(pcmRueckfall.stueck(erstesStueck, ende.aussetzer > 0 || ende.fehler));
    }
    if (this.abort.signal.aborted) {
      this.onAudioEnd?.();
      return;
    }
    if (imHintergrund() && ende.abS > 0) {
      await this.spieleStueckNativ(datei, ende.abS, beiTon ? () => beiTon() : undefined);
      return;
    }
    // Nach einer Unterbrechung (oder bei verstecktem Bildschirm): das entsperrte Element, sofort, mit Hinweis bei Stillstand.
    if (unterbrechung || verborgen()) {
      await this.spieleDateiHinten(datei, ende.abS, beiTon, pcm.dateiAudio);
      return;
    }
    // Von vorn: die vorgeladene Datei nehmen, falls es sie gibt; ab einer Stelle ein frisches Element (`#t=`).
    await this.playOne(
      ende.abS > 0 ? `${datei}#t=${ende.abS.toFixed(2)}` : datei,
      ende.abS > 0 ? undefined : (pcm.dateiAudio ?? undefined),
      beiTon ? () => beiTon() : undefined,
    );
  }

  /**
   * Die Datei eines PCM-Stücks über das EINE entsperrte Element (datei-spieler.ts), sofort, ab `abS` Sekunden.
   * Gibt es das Element nicht (nie in einer Geste gespielt), bleibt es beim frischen — die Hinweis-und-Berührung-Kette
   * in `playOne` gilt trotzdem (`hinten`).
   */
  private async spieleDateiHinten(
    datei: string,
    abS: number,
    beiTon?: (m?: PcmMessung) => void,
    /** Die schon vorgeladene Datei desselben Satzes — nur ab Anfang und nur, wenn das entsperrte Element fehlt. */
    vorgeladen?: HTMLAudioElement | null,
  ): Promise<void> {
    const url = abS > 0 ? `${datei}#t=${abS.toFixed(2)}` : datei;
    const element = nimmDateiSpieler(url) ?? (abS > 0 ? null : (vorgeladen ?? null));
    await this.playOne(url, element ?? undefined, beiTon ? () => beiTon() : undefined, undefined, false, true);
  }

  /**
   * Ticket 2919 (a): Die Seite wurde versteckt oder der Kontext läuft nicht mehr — für ALLE wartenden PCM-Stücke
   * sofort die Datei vorladen. Das PCM-Herunterladen bleibt (der Server hält beides in einer Vertonung), kommt die
   * Seite zurück, bevor das Stück dran ist, spielt es weiter über PCM.
   */
  private hintergrundVorbereiten(nurWeb = false): void {
    for (const it of this.playQueue) {
      if (it.pcm && it.pcmUrl) this.ladeDateiVor(it, it.pcmUrl, nurWeb);
    }
  }

  /**
   * So viele WARTENDE PCM-Stücke laden gleichzeitig vor: eins. Dazu läuft das spielende — höchstens zwei Ströme zugleich
   * (2 × 384 kbit/s). Das nächste Stück hat 0,5 s Vorrat nach rund 340 ms und das spielende dauert meist Sekunden;
   * mehr Vorlauf kostet im Mobilnetz mehr, als er bringt.
   */
  private static readonly PCM_VORLADEN = 1;

  /** Die ersten wartenden PCM-Stücke starten ihren Abruf (idempotent). */
  private starteVorladen(): void {
    if (this.ganzVorabMs !== null) {
      this.starteVorladenGanz();
      return;
    }
    if (this.pcmZurueck()) return; // nicht vorn: erst nach dem Park-Ergebnis „vorn“ (stimmeBegonnen)
    if (pcmGedaechtnis.dateiWeg()) return; // Stromfehler im Gedächtnis: kein Strom, die Stücke spielen als Datei
    for (let i = 0; i < Math.min(TtsQueue.PCM_VORLADEN, this.playQueue.length); i++) {
      this.playQueue[i]?.pcm?.start();
    }
  }

  /**
   * Ganz-Modus (16.10.2026): ALLE wartenden PCM-Stücke laden, der Reihe nach, höchstens `GANZ_GLEICHZEITIG` zugleich —
   * mehr Plätze hat Soniox nicht, und Vesperas eigene Stücke stehen in derselben Reihe am Server. Das Ende eines Abrufs
   * (`beiLadeEnde`) startet den nächsten. Nach einem Abbruch nichts mehr.
   */
  private starteVorladenGanz(): void {
    if (this.abort.signal.aborted || this.pcmZurueck()) return;
    let laufend = 0;
    for (const it of this.playQueue) if (it.pcm?.laedt() && !it.pcm.ladenVorbei()) laufend++;
    for (const it of this.playQueue) {
      if (laufend >= TtsQueue.GANZ_GLEICHZEITIG) return;
      if (it.pcm && !it.pcm.laedt()) {
        it.pcm.start();
        laufend++;
      }
    }
  }

  /** Ist dieses Stück fertig vertont (oder endgültig gescheitert — dann wartet darauf nichts mehr)? */
  private stueckBereit(it: { readyAt?: number; pcm?: PcmStueck }): boolean {
    if (it.readyAt === undefined) return false; // Adresse/Datei noch unterwegs
    return it.pcm ? it.pcm.ladenVorbei() : true; // Datei-Weg: mit der Adresse ist die ganze Datei da
  }

  /**
   * Das Tor des Ganz-Modus: wartet, bis die Schlange geschlossen ist (`flush`) und jedes Stück bereit, höchstens bis
   * `ganzVorabMs` nach dem ersten Stück. Eine Journalzeile, nur Zahlen. Ein Abbruch beendet das Warten sofort.
   */
  private async warteAufGanz(): Promise<void> {
    const seit = this.ganzSeit ?? Date.now();
    const frist = this.ganzVorabMs ?? 0;
    const begonnen = Date.now();
    let grund: "alle" | "obergrenze" = "alle";
    while (!this.abort.signal.aborted) {
      if (this.finished && this.playQueue.every((it) => this.stueckBereit(it))) break;
      if (Date.now() - seit >= frist) {
        grund = "obergrenze";
        break;
      }
      await new Promise((r) => setTimeout(r, TtsQueue.GANZ_TAKT_MS));
    }
    this.ganzErledigt = true;
    if (this.abort.signal.aborted) return;
    const bereit = this.playQueue.filter((it) => this.stueckBereit(it)).length;
    this.meldeZeile(
      `[tts-ganz] sprecher=${this.sprecher ?? "vespera"} grund=${grund} stuecke=${this.playQueue.length} bereit=${bereit} ` +
        `vertont=${Date.now() - seit}ms tor=${Date.now() - begonnen}ms`,
    );
  }

  /**
   * Ein PCM-Stück scheiterte vor seinem ersten Paket: seine Datei als `<audio>` vorladen, wie auf dem Datei-Weg,
   * damit der Übergang zwischen den Sätzen nicht auf ein ganzes Soniox-Stück + ffmpeg wartet.
   */
  private ladeDateiVor(
    item: { audio?: HTMLAudioElement; pcm?: PcmStueck; dateiVorab?: DateiVorab },
    pcmUrl: string,
    nurWeb = false,
  ): void {
    if (this.abort.signal.aborted || item.audio || !item.pcm) return;
    // 17.10.2026: nicht vorn — kein Abruf, der die Render-Sperre des Servers hielte (`pcmZurueck`).
    if (this.pcmZurueck()) return;
    // 17.10.2026: spielt die App die Stimme, legt die Seite kein `<audio>` an (stimme-nativ.ts). Scheitert der Strom aber
    // (oder gilt das Stromfehler-Gedächtnis), holt die Seite die DATEI jetzt für die App — nicht erst, wenn das Stück dran
    // ist (Journal: 5–10 s Pause = ganzer Soniox-Lauf + ffmpeg). Gründe, die nur den Web-Ton betreffen (Hintergrund,
    // Kontext weg), holen für die App nichts.
    if (this.nativKandidat()) {
      if (nurWeb || item.dateiVorab) return;
      const v = this.dateiVorladen(pcmDateiUrl(pcmUrl));
      item.dateiVorab = v;
      // Ist das Stück schon dran (aus der Schlange genommen), liest `spieleUeberApp` die Datei aus `this.dateiVorab`.
      if (item === this.aktuellesStueck) {
        this.dateiVorab?.abbrechen();
        this.dateiVorab = v;
      }
      return;
    }
    try {
      const a = new Audio();
      a.preload = "auto";
      a.src = pcmDateiUrl(pcmUrl);
      a.load();
      item.audio = a;
      item.pcm.dateiAudio = a;
    } catch {
      /* Vorladen best-effort */
    }
  }

  /**
   * 11.10.2026: Ein Stück mit Lücke ≥ 1 s oder ein Stromfehler ist eine Störung — das Gedächtnis (zehn Minuten, tts-pcm-gedaechtnis.ts)
   * hebt den Vorrat an und schickt nach einem Stromfehler die Wiedergabe über die Datei. Ein Abbruch (Nutzer) zählt nicht.
   */
  private merkeStoerung(ende: Awaited<ReturnType<PcmStueck["spiele"]>>): void {
    if (ende.art === "abbruch") return;
    const strom = ende.art === "datei" && ende.fehler && istStromFehler(ende.grund);
    if (!strom && ende.lueckeMs < STOERUNG_LUECKE_MS) return;
    pcmGedaechtnis.stoerung(strom ? "strom" : "aussetzer");
    this.meldeZeile(pcmGedaechtnis.zeile("neu"));
    // Die wartenden Stücke dieser Antwort bekommen ihre Datei jetzt schon — sie spielen über die Datei (siehe `spielePcm`).
    if (strom) this.hintergrundVorbereiten();
  }

  /** Löst der Rückfall aus, steht das im Journal — mit dem Grund. */
  private pruefeRueckfall(grund: string | null): void {
    if (grund) this.meldeZeile(`[tts-pcm] rueckfall auf den Datei-Weg: ${grund}`);
  }

  /**
   * Ein Stück (oder sein Rest ab `ab` Sekunden) über den nativen Spieler.
   * (24.09.2026, Ticket 2547 — siehe vorlesen-nativ.ts)
   *
   * Dieselben Signale wie der Web-Weg (`onAudioStart`, `tts-audio-start`,
   * erster Ton, `onAudioEnd`), damit Sprechtaste, Hi-Fi und Bilanz nichts
   * davon merken, welcher Spieler klang. Ein Scheitern ist ein gemeldeter
   * Verlust, nie ein stilles Weiter.
   */
  private async spieleStueckNativ(url: string, ab: number, beiTon?: () => void): Promise<void> {
    this.nativSpielt = true;
    const start = Date.now();
    let grund: string;
    try {
      // Nutzer-Pause vor dem Start: warten wie der Web-Weg.
      while (this.paused && !this.abort.signal.aborted) {
        await new Promise((r) => setTimeout(r, 200));
      }
      if (this.abort.signal.aborted) return;
      vorleseHalt(true, this, this.haltProtokoll);
      this.onAudioStart?.();
      const ersterTon = !this.playedThisTurn;
      this.playedThisTurn = true;
      try {
        window.dispatchEvent(
          new CustomEvent("voice-v8:tts-audio-start", { detail: { queueId: this.seitenTonId } }),
        );
      } catch {
        /* ignore */
      }
      if (ersterTon) meldeErstenTon();
      try {
        beiTon?.();
      } catch {
        /* eine Sonde darf die Wiedergabe nie stören */
      }
      // Der Strom-Weg liefert die Bytes erst gepuffert vollständig.
      const quelle = istStromAdresse(url) ? pufferUrl(url) : url;
      grund = await spieleNativ(quelle, { ab, signal: this.abort.signal });
      // Bilanz: der native Spieler nennt keine Stelle — zu Ende gespielt zählt ganz, alles andere nicht.
      if (grund === "ende") this.stueckStand.ganz = true;
    } finally {
      this.nativSpielt = false;
    }
    this.meldeZeile(
      `[tts-gap] via=nativ:${grund} ab=${ab.toFixed(2)}s echt=${((Date.now() - start) / 1000).toFixed(2)}s`,
    );
    if (grund.startsWith("fehler") && !this.abort.signal.aborted) {
      this.meldeVerlust("", `nativ:${grund}`);
    }
    // Frist verlängern — das nächste Stück wird womöglich noch vertont.
    if (!this.abort.signal.aborted) vorleseHalt(true, this, this.haltProtokoll);
    this.onAudioEnd?.();
  }

  /**
   * Ein Stück über die App spielen (17.10.2026, stimme-nativ.ts, PawbotStimme). Gibt `null` zurück, wenn das Stück
   * erledigt ist (gespielt, abgebrochen, übersprungen), sonst die Adresse, die der Web-Weg jetzt spielt — dasselbe Stück,
   * ggf. ab der Stelle, bis zu der die App kam. Nichts doppelt, nichts verloren.
   *
   * Nur wenn: Kippe AN, die App kennt `stimmeStueck` (PawbotVoice) und `stimmeBereit` sagte für DIESE Antwort ja.
   * Dann fasst die Seite keinen Ton an: kein `<audio>`, kein AudioContext, keine Tonsitzung, kein Hi-Fi, keine Miete des
   * Seitentons (`tts-audio-start` trägt `nativ:true`, seiten-ton.ts mietet dann nicht), kein lautloser Puls.
   *
   * PCM: erst, wenn das erste Paket da ist, `stimmeStueck` mit Rate und Verstärkung = Kopfzeile − `PCM_ABZUG_DB`
   * (Startwert, am Gerät gegen den Datei-Weg abhören), dann die rohen s16le-Brocken. Scheitert der Strom vor dem ersten
   * Paket (oder gilt das Stromfehler-Gedächtnis), geht die DATEI an die App. Datei: ganz laden, dann an die App.
   */
  private async spieleUeberApp(
    url: string,
    pcm: PcmStueck | undefined,
    beiTon: ((m?: PcmMessung) => void) | undefined,
  ): Promise<string | null> {
    /** Hat die App das Stück angenommen? Dann spielte sie es — ein Wurf danach darf es nicht noch einmal im Web spielen. */
    let angenommen = false;
    try {
      if (!this.nativKandidat() && !(await this.nativZurueck(url, pcm))) {
        return this.abort.signal.aborted ? null : this.webWeg(url);
      }
      if (!(await this.nativEntscheiden())) return this.webWeg(url);
      // 27.10.2026: nach dem Zurück-Wechsel bleibt ein in der Web-Phase eingereihtes PCM-Stück im Web (`pcmOhneRoh`).
      if (this.pcmOhneRoh(url, pcm)) return this.webWeg(url);
      if (this.abort.signal.aborted) return null;
      while (this.paused && !this.abort.signal.aborted) await new Promise((r) => setTimeout(r, 200));
      if (this.abort.signal.aborted) return null;
      const t0 = Date.now();
      // Ab hier „spielt“ die Schlange (idle wartet, Pause geht an die App) — auch während das erste Paket kommt.
      this.stimmeNativ = { gespieltMs: 0, dauerS: NaN };
      let quelle: NativQuelle;
      let kopf: Parameters<typeof stimmeNativSpielen>[0]["kopf"];
      let art: "pcm" | "datei" = "pcm";
      const dateiUrl = istPcmAdresse(url) ? pcmDateiUrl(url) : url;
      let messung: (() => PcmMessung) | null = null;
      let pcmBytes = 0;
      const lage = istPcmAdresse(url) && pcm?.rohMerken && !pcmGedaechtnis.dateiWeg() ? await erstesPaket(pcm, this.abort.signal, ERSTES_PAKET_FRIST_MS) : null;
      if (this.abort.signal.aborted) return null;
      if (pcm && lage && lage.brocken > 0 && lage.gainDb !== null) {
        quelle = pcmQuelle(pcm);
        pcmBytes = lage.bytes; // der Vorrat, mit dem die App beginnt (`vorlauf` der Zeile `[tts-t]`)
        kopf = { rate: lage.rate, verstaerkungDb: lage.gainDb - PCM_ABZUG_DB, hoehenDb: lage.hoehenDb, hoehenHz: lage.hoehenHz };
        messung = () => {
          const l = pcm.rohLage();
          return { paketMs: l.paketMs ?? 0, tonMs: l.alterMs, vorlaufMs: Math.round((pcmBytes / 2 / l.rate) * 1000) };
        };
      } else {
        // Datei: aus dem Strom gescheitert (vor dem ersten Paket), Stromfehler-Gedächtnis, oder ohnehin eine Datei.
        if (lage) this.meldeZeile(`[stimme-nativ] pcm-vor-paket grund=${lage.fehler ?? "frist"}`);
        if (istPcmAdresse(url)) {
          pcm?.verwerfen();
          url = dateiUrl;
        }
        art = "datei";
        const passt = this.dateiVorab?.url === dateiUrl;
        // Eine Vorladung unter anderer Adresse braucht niemand mehr — abbrechen, nicht liegen lassen.
        if (!passt) this.dateiVorab?.abbrechen();
        const vorab = passt && this.dateiVorab ? this.dateiVorab : this.dateiVorladen(dateiUrl);
        this.dateiVorab = null;
        const datei = await this.mitFrist(vorab, wachhundDeckelMs(NaN, this.stueckTextLaenge));
        if (this.abort.signal.aborted) return null;
        if (typeof datei === "string") {
          this.meldeZeile(`[stimme-nativ] datei-fehlt grund=${datei}`);
          return this.webWeg(url);
        }
        quelle = dateiQuelle(datei.bytes);
        kopf = { typ: datei.typ, ab: startAusAdresse(dateiUrl) };
      }
      const textLaenge = this.stueckTextLaenge;
      const r: NativErgebnis = await stimmeNativSpielen({
        art,
        kopf,
        quelle,
        signal: this.abort.signal,
        pausiert: () => this.paused,
        deckelMs: (bytes) =>
          art === "pcm"
            ? (bytes / 2 / (kopf.rate ?? 24_000)) * 1000 + WACHHUND_ZUSCHLAG_MS
            : wachhundDeckelMs(NaN, textLaenge),
        // Angenommen: wie der Web-Weg vor dem Abspielen (Sprech-Pille, Knopf); jedes Stück endet unten mit onAudioEnd.
        beiAnnahme: () => {
          angenommen = true;
          this.onAudioStart?.();
        },
        beiEreignis: (e) => {
          if (!this.stimmeNativ) return;
          if (e.gespieltMs > this.stimmeNativ.gespieltMs) this.stimmeNativ.gespieltMs = e.gespieltMs;
          const l = art === "pcm" ? pcm?.rohLage() : undefined;
          if (l?.fertig) this.stimmeNativ.dauerS = l.bytes / 2 / l.rate;
        },
        beiStart: () => {
          // Dieselben Signale wie der Web-Weg beim ersten Ton — mit `nativ:true`: keine Miete des Seitentons.
          const ersterTon = !this.playedThisTurn;
          this.playedThisTurn = true;
          try {
            window.dispatchEvent(
              new CustomEvent("voice-v8:tts-audio-start", { detail: { queueId: this.seitenTonId, nativ: true } }),
            );
          } catch {
            /* ohne window (Tests) */
          }
          if (ersterTon) meldeErstenTon(); // liest nur den Zustand der Tonsitzung (Diagnose)
          try {
            beiTon?.(messung ? messung() : undefined);
          } catch {
            /* eine Sonde darf die Stimme nie stören */
          }
        },
      });
      if (r.art === "abgelehnt") {
        this.meldeZeile(`[stimme-nativ] abgelehnt grund=${r.grund} art=${art}`);
        if (this.abort.signal.aborted) return null;
        // 27.10.2026: steht der Motor (wieder), stellt die ganze Antwort sauber auf Web um — die wartenden Stücke laden als
        // `<audio>` vor, und jedes weitere Stück fragt nur kurz, ob er zurück ist (`nativZurueck`), statt einzeln abzuprallen.
        if (r.grund.startsWith("motor-steht") && !this.webAntwort) {
          this.webWegenMotor = true;
          this.nativEntscheid = Promise.resolve(false);
          this.meldeZeile(`[stimme-nativ] web grund=${r.grund} beim=stueck sicht=${sichtbarkeit()}`);
          this.aufWebUmstellen();
        }
        return this.webWeg(url);
      }
      const weiter = this.nachApp(r, art, url, pcm, startAusAdresse(dateiUrl), Date.now() - t0);
      if (weiter === null || typeof weiter === "string") return weiter;
      // Die App gab das Stück in einer langen Pause auf (`pause-frist`): wie auf dem Web-Weg darf eine Pause beliebig lang
      // sein — nach dem Fortsetzen dasselbe Stück neu ansetzen, ab der Stelle, bis zu der es kam (als Datei).
      while (this.paused && !this.abort.signal.aborted) await new Promise((w) => setTimeout(w, 200));
      if (this.abort.signal.aborted) return null;
      this.meldeZeile(`[stimme-nativ] neu-ansetzen ab=${startAusAdresse(weiter.neu).toFixed(2)}s`);
      return await this.spieleUeberApp(weiter.neu, undefined, beiTon);
    } catch (e) {
      /*
       * 17.10.2026 (Martins Stimme stumm): `hoererBereit` warf („.then is not a function“) — der Wurf verschwand hier
       * (try/finally) und in `void runPlayLoop()`, weder App noch WebKit spielten. Nie wieder still und nie stumm: ins
       * Journal, dann dasselbe Stück im Web — außer die App hatte es schon angenommen (nichts doppelt).
       */
      this.meldeWurf("stimme-nativ", "wurf", e);
      if (this.abort.signal.aborted) return null;
      if (angenommen) {
        this.onAudioEnd?.();
        return null;
      }
      return this.webWeg(url);
    } finally {
      this.stimmeNativ = null;
    }
  }

  /**
   * Was nach dem endgültigen Ereignis der App geschieht. `null`: erledigt; eine Adresse: der Web-Weg übernimmt;
   * `{neu}`: nach der Pause dasselbe Stück wieder über die App, ab dieser Adresse (`#t=`).
   */
  private nachApp(
    r: Exclude<NativErgebnis, { art: "abgelehnt" }>,
    art: "pcm" | "datei",
    url: string,
    pcm: PcmStueck | undefined,
    abS: number,
    echtMs: number,
  ): string | null | { neu: string } {
    const gespieltS = r.gespieltMs / 1000;
    this.meldeZeile(
      `[stimme-nativ] id=${r.id} art=${r.art} ms=${r.gespieltMs} grund=${r.grund || "-"} weg=${art} echt=${echtMs}ms` +
        (r.quelleFehler ? ` strom=${r.quelleFehler}` : ""),
    );
    this.zugMessung.lauf(r.art === "fehler" ? `error:nativ-${r.grund}` : `nativ-${r.art}`, echtMs, gespieltS);
    const dauerS = this.stimmeNativ?.dauerS ?? NaN;
    if (r.art === "ende" && !r.quelleFehler) this.stueckStand.ganz = true;
    else this.merkeStand(gespieltS, dauerS);
    // Riss der Strom (die App spielte, was kam): wie auf dem Web-Weg ins Gedächtnis — die nächsten Stücke gehen als Datei.
    if (r.quelleFehler && istStromFehler(r.quelleFehler)) {
      pcmGedaechtnis.stoerung("strom");
      this.meldeZeile(pcmGedaechtnis.zeile("neu"));
    }
    // Siri/Anruf hat die eigene Sitzung genommen und kam nicht zurück: kein Ton über WebKit (Martin hat gewählt) — nur Journal.
    const fremd = r.art === "fehler" && r.fremdUnterbrochen;
    // `sitzung:-1` mitten im Stück = Anruf/Telefon: wie `telefon` — die Antwort endet, kein Weiterspielen über WebKit.
    const anruf = r.grund === "telefon" || r.grund === "sitzung:-1";
    if (r.art === "fehler" && !anruf && !fremd && !this.abort.signal.aborted) {
      // Noch kein Ton (kein `start`): dasselbe Stück auf dem Web-Weg (die Bytes liegen noch da). Eine Datei auch ab der Stelle.
      if (r.gespieltMs === 0 && !r.gestartet) {
        this.onAudioEnd?.();
        return this.webWeg(url);
      }
      if (art === "datei") {
        this.onAudioEnd?.();
        return this.webWeg(adresseAb(url, abS + gespieltS));
      }
      // PCM mitten im Stück: abbrechen (auch den Abruf), weiter mit dem nächsten (Testprotokoll Regel 7) — der Rest zählt
      // als verloren.
      pcm?.verwerfen();
    }
    this.onAudioEnd?.();
    if (this.abort.signal.aborted) return null;
    // Das Telefon übernimmt: die Antwort hört hier auf — benannt, kein „Ende“ (kein Mikro danach).
    if (r.art === "fehler" && (anruf || fremd)) {
      pcm?.verwerfen();
      this.cancel(fremd ? "stimme-nativ:unterbrochen" : anruf && r.grund !== "telefon" ? "stimme-nativ:sitzung" : "stimme-nativ:telefon");
      return null;
    }
    // Die App beendete das Stück selbst, ohne dass die Seite abbrach — das ist nur die Pausenfrist der App (Sperrbildschirm,
    // Kopfhörer weg, Pause länger als 60 s): als Pause behandeln und das Stück danach neu ansetzen.
    if (r.art === "abbruch") {
      pcm?.verwerfen();
      if (!this.paused) {
        stimmeNativPauseMelden(); // die Anzeige (app.ts) hält alle Schlangen an
        if (!this.paused) this.pause();
      }
      const datei = istPcmAdresse(url) ? pcmDateiUrl(url) : url;
      return { neu: adresseAb(datei, abS + gespieltS) };
    }
    return null;
  }

  /** Kommt für diese Antwort die App in Frage? (Kippe, Methode — und sie hat für diese Antwort nicht schon nein gesagt.) */
  private nativKandidat(): boolean {
    return !this.webAntwort && stimmeNativMoeglich();
  }

  /**
   * Die App je Antwort fragen (`stimmeBereit`); sagt sie nein, läuft die ganze Antwort wie heute im Web — und bleibt dort
   * (kein Hin und Her mitten in der Antwort).
   *
   * 17.10.2026 (Journal 11:17–11:23, Build 113): bei JEDER Antwort `bereit=0 grund=motor-steht` — der Mikrofon-Motor stand
   * gerade unterbrochen und kam kurz danach wieder. Darum bei `motor-steht…` noch einmal fragen: alle
   * `NATIV_NEU_FRAGEN_MS`, höchstens `NATIV_FRAGEN_MAX_MS` ab der ersten Frage (sie beginnt schon beim Einreihen, die
   * Vertonung läuft parallel). Der erste Satz wartet dadurch höchstens so lange; ein Abbruch beendet das Warten sofort.
   */
  private nativEntscheiden(): Promise<boolean> {
    this.nativEntscheid ??= (async () => {
      let zuletzt = { versuch: 0, nach: 0 };
      const r = await stimmeNativBereitWarten(this.abort.signal, NATIV_FRAGEN_MAX_MS, (a, versuch, nach) => {
        zuletzt = { versuch, nach };
        this.meldeZeile(
          a.bereit
            ? `[stimme-nativ] bereit=1 modus=${a.modus} version=${a.version} versuch=${versuch} nach=${nach}ms`
            : `[stimme-nativ] bereit=0 grund=${a.grund} modus=${a.modus} version=${a.version} versuch=${versuch}`,
        );
      });
      if (r.bereit) return true;
      if (this.abort.signal.aborted) return false;
      // 27.10.2026 (Diagnose, ohne Inhalt): warum Web, nach wie vielen Fragen, und ob die Seite gerade verdeckt war —
      // im Hintergrund kann die App ihren Motor nicht anwerfen (Journal 13:01:52 `startet nicht 2003329396`, `hidden`).
      this.webWegenMotor = r.grund.startsWith("motor-steht");
      this.meldeZeile(`[stimme-nativ] web grund=${r.grund} versuche=${zuletzt.versuch} nach=${zuletzt.nach}ms sicht=${sichtbarkeit()}`);
      this.aufWebUmstellen();
      return false;
    })();
    return this.nativEntscheid;
  }

  /**
   * 27.10.2026 (Martin 13:03 „schon wieder“, Journal 13:01:42–13:03:22): die Antwort fiel auf Web, weil der Motor stand
   * (`motor-steht:unterbrochen` — die Seite war verdeckt, die App konnte ihn nicht anwerfen). 35 s später kam er wieder
   * (`wieder-da nach=35s`) — und ab da kämpften `<audio>` und Motor um die Tonsitzung: bei jedem Satz `unterbrochen`,
   * `tts-weiter grund=pausiert pause=fremd`, ~3 s Lücke. Darum fragt bei `motor-steht` jedes weitere Stück die App noch
   * EINMAL (ohne Warten); sagt sie ja, spielt ab diesem Stück wieder die App, für den Rest der Antwort.
   *
   * Die Seite wirft dabei NICHTS an (kein Motor, kein Mikro) — sie fragt nur. Ein PCM-Stück, das ohne rohe Bytes lädt
   * (eingereiht nach dem Wechsel auf Web), bleibt im Web: seine Datei käme erst nach einer ganzen Vertonung.
   */
  private async nativZurueck(url: string, pcm: PcmStueck | undefined): Promise<boolean> {
    if (!this.webAntwort || !this.webWegenMotor || this.abort.signal.aborted || !stimmeNativMoeglich()) return false;
    if (this.zurueckZahl >= NATIV_ZURUECK_MAX || this.pcmOhneRoh(url, pcm)) return false;
    // Höchstens `NATIV_ZURUECK_FRIST_MS` (die Brücke kann hängen); ein Abbruch beendet das Warten sofort.
    const r = await stimmeNativBereitKurz(this.abort.signal);
    if (!r?.bereit || this.abort.signal.aborted) return false;
    this.zurueckZahl++;
    this.meldeZeile(`[stimme-nativ] zurück bereit=1 modus=${r.modus} version=${r.version} sicht=${sichtbarkeit()}`);
    this.webAntwort = false;
    this.webWegenMotor = false;
    this.nativEntscheid = Promise.resolve(true);
    // Die wartenden Datei-Stücke holen ihre Datei wieder für die App — unter der Adresse, die das Stück trägt (nicht
    // `audio.src`: der Browser macht daraus die volle Adresse, `spieleUeberApp` fände die Vorladung nicht und holte
    // doppelt). Das vorgeladene `<audio>` bleibt als Rückfall liegen.
    if (!this.pcmZurueck()) {
      for (const it of this.playQueue) {
        if (it.dateiVorab || it.pcm) continue;
        void it.urlPromise.then((u) => {
          if (!u || it.dateiVorab || it.pcm || istPcmAdresse(u) || !this.playQueue.includes(it)) return;
          if (this.webAntwort || this.abort.signal.aborted) return;
          it.dateiVorab = this.dateiVorladen(u);
        });
      }
    }
    return true;
  }

  /**
   * Ein PCM-Stück, das ohne rohe Bytes lädt (eingereiht, während die Antwort im Web lief), spielt im Web: über die App
   * müsste es seinen Strom verwerfen und auf die ganze Datei warten (Lücke, zweite Vertonung). Nicht gemeint: das
   * Stromfehler-Gedächtnis — dort geht die Datei ohnehin an die App.
   */
  private pcmOhneRoh(url: string, pcm: PcmStueck | undefined): boolean {
    return istPcmAdresse(url) && !!pcm && !pcm.rohMerken && !pcmGedaechtnis.dateiWeg();
  }

  /**
   * Die ganze Antwort läuft im Web (die App sagte nein): die wartenden Stücke laden wie bisher als `<audio>` vor, statt
   * ihre Datei für die App zu holen — sonst wartete jeder Satz im Web auf sein Laden (Lücke, doppelter Abruf).
   */
  private aufWebUmstellen(): void {
    this.webAntwort = true;
    for (const it of this.playQueue) {
      const v = it.dateiVorab;
      if (!v || it.audio || this.abort.signal.aborted) continue;
      v.abbrechen();
      it.dateiVorab = undefined;
      if (it.pcm && it.pcmUrl) {
        // Strom-Stück: im Web spielt der Strom. Nur ein schon gescheitertes braucht seine Datei (als `<audio>`, nicht doppelt).
        if (it.pcm.rohLage().fehler !== null) this.ladeDateiVor(it, it.pcmUrl);
        continue;
      }
      try {
        const a = new Audio();
        a.preload = "auto";
        a.src = v.url;
        a.load();
        it.audio = a;
      } catch {
        /* Vorladen best-effort */
      }
    }
  }

  /** Der Web-Weg übernimmt dieses Stück: das ausgelassene Hi-Fi-Vorwärmen kommt nach (einmal je Antwort). */
  private webWeg(url: string): string {
    // Die für die App geholte Datei dieses Stücks braucht niemand mehr.
    this.dateiVorab?.abbrechen();
    this.dateiVorab = null;
    if (this.hifiAufgeschoben) {
      this.hifiAufgeschoben = false;
      prewarmHiFi();
    }
    return url;
  }

  /**
   * Die Datei eines Stücks ganz holen (der Strom-Weg gepuffert) — beim Einreihen begonnen. Ergebnis: die Bytes oder ein
   * Grund. Ein Abbruch der Schlange beendet den Abruf (Stopp, Barge-in, „Fertig“ verwerfen alles Vorgeladene).
   */
  private dateiVorladen(url: string): DateiVorab {
    const adresse = (istStromAdresse(url) ? pufferUrl(url) : url).split("#", 1)[0] ?? url;
    const ac = new AbortController();
    const ab = (): void => ac.abort();
    this.abort.signal.addEventListener("abort", ab, { once: true });
    const fertig = (async (): Promise<{ bytes: Uint8Array; typ: string } | string> => {
      try {
        const a = await fetch(adresse, { credentials: "same-origin", signal: ac.signal });
        if (!a.ok) return `http${a.status}`;
        const bytes = new Uint8Array(await a.arrayBuffer());
        if (bytes.length === 0) return "leer";
        if (bytes.length > MAX_DATEI_BYTES) return "zu-gross";
        return { bytes, typ: a.headers.get("content-type") ?? "" };
      } catch (e) {
        return `laden:${e instanceof Error ? e.name || "fehler" : "fehler"}`;
      } finally {
        this.abort.signal.removeEventListener("abort", ab);
      }
    })();
    return { url, fertig, abbrechen: ab };
  }

  /**
   * Auf die Datei warten — höchstens `fristMs`, gezählt ab dem Augenblick, in dem das Stück dran ist (wie der Wachhund
   * des Web-Wegs ohne bekannte Dauer). Hängt der Server, endet das Laden mit „frist“, und der Web-Weg übernimmt.
   */
  private async mitFrist(v: DateiVorab, fristMs: number): Promise<{ bytes: Uint8Array; typ: string } | string> {
    let uhr: ReturnType<typeof setTimeout> | undefined;
    const frist = new Promise<string>((r) => (uhr = setTimeout(() => r("frist"), fristMs)));
    try {
      const r = await Promise.race([v.fertig, frist]);
      if (r === "frist") v.abbrechen();
      return r;
    } finally {
      clearTimeout(uhr);
    }
  }

  /**
   * Beta an, ein Strom-Stück, und noch nicht ueber den gepufferten Weg gelaufen?
   *
   * ⚠️ 23.09.2026, Ticket 2448: `istStromAdresse` fehlte. Die Antworten kamen
   *    bis heute als `blob:`-Adressen — nach JEDEM Stück fragte das Netz
   *    „blob:…?puffer=1" ab, und ein Wiedergabefehler lief erst noch einmal
   *    an dieser Adresse ins Leere, bevor er als Verlust galt.
   */
  private nachladenMoeglich(url: string): boolean {
    return (
      this.direktBeta?.() === true &&
      istStromAdresse(url) &&
      !/[?&]puffer=1(?:&|#|$)/.test(url)
    );
  }

  /**
   * Eine Zeile in die Konsole UND ins Journal (/api/debug/ptt-timing, dort
   * mit uid). Ticket 2448: Die zwei Nachspiel-Zeilen standen nur in der
   * Konsole — auf einem Telefon, das niemand aufmacht. Seit 21.09. kam im
   * Journal keine einzige an; ob das Netz je griff, war nicht zu sehen.
   */
  /**
   * Ein Wurf als Journalzeile `[<quelle>] <was> grund=<Name:Text>` — gleichlautende höchstens alle `WURF_ABSTAND_MS`
   * (ein Fehler je Stück soll das Journal nicht fluten). Wirft nie.
   */
  private meldeWurf(quelle: string, was: string, e: unknown): void {
    try {
      const grund = (e instanceof Error ? `${e.name}:${e.message}` : `Wurf:${String(e)}`).replace(/\s+/g, "-").slice(0, 120);
      const zeile = `[${quelle}] ${was} grund=${grund}`;
      const jetzt = Date.now();
      const vorher = wurfZuletzt.get(zeile);
      if (vorher !== undefined && jetzt - vorher < WURF_ABSTAND_MS) return;
      if (wurfZuletzt.size > 50) wurfZuletzt.clear();
      wurfZuletzt.set(zeile, jetzt);
      this.meldeZeile(zeile);
    } catch {
      /* die Meldung darf nie selbst werfen */
    }
  }

  private meldeZeile(line: string): void {
    console.log(line);
    void fetch("/api/debug/ptt-timing", {
      method: "POST",
      body: JSON.stringify({ line }),
      headers: { "Content-Type": "text/plain" },
      keepalive: true,
      credentials: "include",
    }).catch(() => undefined);
  }

  /**
   * Ein Stueck konnte nicht vertont werden — das darf nie lautlos passieren.
   *
   * ⚠️ Genau daran ist Martins Spiegel-Absatz verschwunden. Eine
   *    Konsolenzeile ist keine Meldung: sie steht auf einem Telefon, das
   *    niemand aufmacht. Der Server erfaehrt es (dort ist es auffindbar),
   *    und auf dem Schirm steht, WAS fehlt — sonst sucht man den Fehler im
   *    Text statt in der Vertonung.
   */
  private meldeVerlust(text: string, grund: string): void {
    // 19.09.2026: Der Text stand hier und in narration-engine.ts WORTGLEICH,
    // und beide meldeten JE ABSATZ. Siehe vorlese-verlust.ts.
    meldeVorleseVerlust(text, grund, "tts-queue");
  }

  private resolveIdle(): void {
    // 2026-07-09 (Martin, PTT-Verzoegerung erneut): TTS ist jetzt GANZ fertig ->
    // Signal, damit der PTT-Layer das Mikro proaktiv WIEDER vorwaermt (pre.1512
    // gab es beim TTS-Start frei -> naechstes PTT war sonst kalt = 3s).
    // 05.09.2026: Ein Abbruch sendet bewusst kein „Ende" (ptt.ts wärmt
    // danach das Mikro vor — nach einem Abbruch wäre das falsch). Die
    // Rekorder-Schonung braucht aber ein Zeichen, sonst bliebe die
    // Tagesaufnahme pausiert, bis die Notbremse greift.
    // Ticket 2547: Die Vorlesung ist aus (fertig oder abgebrochen) — den
    // Halt der Tonsitzung sofort freigeben, nicht erst nach seiner Frist.
    // Nachprüfung Build 77: nur DIESE Warteschlange; hält noch eine andere,
    // bleibt der Halt. Ein zweiter Aufruf (cancel + finally) ist wirkungslos.
    vorleseHalt(false, this);
    meldeWiedergabeEnde(); // 20.10.2026: Antwort zu Ende — die App setzt den Namens-Beleg zurück
    beendeBeobachtung(this); // 19.10.2026: die Vorstart-Uhr steht (das Mikro selbst bleibt bis zur Öffnung oder zum Verfall)
    // Ticket 2882: fertig oder abgebrochen — diese Schlange spricht nicht mehr.
    stimmeEndet(this);
    const gesprochen = this.gesprochenDieserZug;
    if (this.playedThisTurn) this.gesprochenDieserZug = "";
    if (this.playedThisTurn && this.wasCanceled) {
      this.playedThisTurn = false;
      try {
        window.dispatchEvent(
          new CustomEvent("voice-v8:tts-audio-abbruch", { detail: { queueId: this.seitenTonId } }),
        );
      } catch {
        /* kein window (Tests) */
      }
    }
    // 19.10.2026: bis zum Ende gehört (kein Abbruch) — nächstes Mal von vorn. Ein Abbruch behält die Stelle.
    if (this.playedThisTurn && !this.wasCanceled) this.fortsetzSpur?.ende();
    if (this.playedThisTurn && !this.wasCanceled) {
      this.playedThisTurn = false;
      try {
        /*
         * Ticket 2403: Ob die vorgelesene Antwort mit einer Frage schliesst,
         * entscheidet sich HIER — der Hoerer in app.ts oeffnet danach das
         * Mikro, wenn Martin das eingeschaltet hat.
         */
        window.dispatchEvent(
          new CustomEvent("voice-v8:tts-audio-end", {
            // Welle A (lib/zug-ende.ts): ob der Zug schon zu Ende ist und was sein Zugvertrag meldete — das Mikro wartet auf done.
            detail: {
              endetMitFrage: endetMitFrage(gesprochen), queueId: this.seitenTonId,
              ...zugEndeStand(),
              // 15.10.2026 (Schmied): spricht Vespera ihn am Ende an, wartet das Mikro auf ihn (shared/schmied.ts).
              schmiedAngesprochen: this.sprecher ? false : sprichtSchmiedAn(gesprochen),
              // 19.10.2026: spricht sie am Ende Zimmermann oder Hüter an (Übergabe), wartet das Mikro auf IHN (app.ts `wartetAufEntwickler`).
              ...(this.sprecher ? {} : (() => { const z = zusatzAmEnde(gesprochen); return z ? { zusatzAngesprochen: z } : {}; })()),
              ...(this.sprecher ? { sprecher: this.sprecher } : {}),
            },
          }),
        );
      } catch {
        /* ignore */
      }
    }
    const resolvers = this.idleResolvers;
    this.idleResolvers = [];
    for (const r of resolvers) r();
  }
}
